import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { ParticipantsEditor } from '../components/admin/ParticipantsEditor';
import { Calendar as DatePickerCalendar } from '../components/ui/calendar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, 
  Mail, 
  Search, 
  Eye, 
  Edit,
  Trash2,
  Clock,
  RefreshCw,
  Lock,
  Calendar,
  Settings,
  BookOpen,
  Plus,
  Save,
  X,
  UserPlus,
  LogIn,
  Users,
  User,
  Phone,
  Building,
  IdCard,
  Award,
  Upload,
  History,
  ChevronDown,
  ChevronUp,
  Camera,
  Video,
  PlayCircle,
  GripVertical,
  Loader2,
  Languages,
  ClipboardCheck,
  HelpCircle,
  CheckCircle2,
  CircleX,
  Check,
  FileCheck,
  Receipt,
  Banknote,
  Briefcase,
  UserCheck,
  BarChart3,
  Link2,
  Newspaper,
  FileSignature
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import MonitoringTab from '../components/admin/MonitoringTab';
import SettingsTab from '../components/admin/SettingsTab';
import UsersTab from '../components/admin/UsersTab';
import CoursesTab from '../components/admin/CoursesTab';
import InvoicesTab from '../components/admin/InvoicesTab';
import GuidesTab, { GuidePatternsTab, LANG_LABELS_JA, LANG_FILTER_OPTIONS, LangCounts } from '../components/admin/GuidesTab';
import NationalitySelect from '../components/NationalitySelect';
import RichTextEditor from '../components/RichTextEditor';
import SchedulesTab from '../components/admin/SchedulesTab';
import { ListPager, usePagedList } from '../components/admin/ListPager';
import { ListToolbar, useFilteredList } from '../components/admin/ListToolbar';
import UnlinkedCertsDialog from '../components/admin/UnlinkedCertsDialog';
import DuplicateUsersDialog from '../components/admin/DuplicateUsersDialog';
import NewsTab from '../components/admin/NewsTab';
import { PaymentConfirmFields, PAYMENT_METHOD_LABELS, jstDate } from '../components/admin/PaymentConfirmFields';
import { QuoteDialog } from '../components/admin/QuoteDialog';

import { COURSE_COLOR_OPTIONS, getCourseColorOption } from '../lib/courseColors';
import { categories, types } from '../hooks/useCourses';
import { useRef } from 'react';
import WebMail from './WebMail';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


// Supported course/material languages
const COURSE_LANGS = [
  { code: 'ja', label: '日本語', cc: 'jp' },
  { code: 'en', label: 'English', cc: 'us' },
  { code: 'pt', label: 'Português', cc: 'br' },
  { code: 'es', label: 'Español', cc: 'es' },
  { code: 'vi', label: 'Tiếng Việt', cc: 'vn' },
  { code: 'id', label: 'Indonesia', cc: 'id' },
  { code: 'tl', label: 'Tagalog', cc: 'ph' },
  { code: 'zh', label: '中文', cc: 'cn' },
];

const LangFlag = ({ cc, className = '' }) => (
  <img
    src={`https://flagcdn.com/w40/${cc}.png`}
    alt={cc}
    className={`inline-block w-6 h-4 rounded-[2px] object-cover border border-gray-300 shadow-sm ${className}`}
    loading="lazy"
  />
);

// Get/set a localized value that may be a plain string (legacy) or a {lang: text} dict.
const locVal = (val, lang) => {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  return val[lang] ?? val.ja ?? '';
};
const setLocVal = (val, lang, text) => {
  const base = (val && typeof val === 'object') ? { ...val } : (typeof val === 'string' && val ? { ja: val } : {});
  base[lang] = text;
  return base;
};

// Helper function to normalize image paths
// Converts /api/uploads/xxx to /uploads/xxx for correct Nginx serving
const getImageUrl = (path) => {
  if (!path) return null;
  const baseUrl = BACKEND_URL.replace(/\/$/, '');
  // On production, /uploads/ is served directly by Nginx
  // On preview, it goes through /api/ prefix
  if (path.startsWith('/uploads/') || path.startsWith('/seals/')) {
    return `${baseUrl}${path}`;
  }
  return `${baseUrl}${path}`;
};

export default function Admin() {
  const { language, t } = useLanguage();

  // Helper: get localized course name from courses DB
  const getLocalCourseName = (item) => {
    if (!item) return '';
    const course = courses.find(c => c.id === item.courseId) || 
                   courses.find(c => c.name?.ja === item.courseName);
    if (course?.name && typeof course.name === 'object') {
      return course.name[language] || course.name.ja || item.courseName || '';
    }
    return item.courseName || item.courseId || '';
  };

  // 証明書リクエストの講習に紐づくスケジュールの期間ラベルを返す（講習名＋期間の見出し用）
  const getCertScheduleLabel = (req) => {
    if (!req) return '';
    const app = applications.find(a => a.courseId === req.courseId && (a.userId === req.userId || a.email === req.userEmail));
    const match = schedules.find(s => {
      if (s.courseId !== req.courseId) return false;
      if (app?.preferredDate) {
        const pref = new Date(app.preferredDate);
        return pref >= new Date(s.startDate) && pref <= new Date(s.endDate);
      }
      return false;
    }) || [...schedules].filter(s => s.courseId === req.courseId).sort((a, b) => String(b.startDate || '').localeCompare(String(a.startDate || '')))[0];
    if (!match || !match.startDate) return '';
    return `${match.startDate}${match.endDate ? ` 〜 ${match.endDate}` : ''}`;
  };

  // 申込に紐づくスケジュールの期間ラベル（申込一覧の講習名見出し用）
  // 申込のコース(A/B/C)と、講習名＋コースに合う講習案内パターン
  const getAppCourseOption = (applicationId) => applications.find(a => a.id === applicationId)?.courseOption || '';
  const findGuideTemplate = (title, code) => (code && guideTemplates.find(tp => tp.title === title && tp.courseOption === code))
    || guideTemplates.find(tp => tp.title === title && !tp.courseOption) || guideTemplates.find(tp => tp.title === title) || guideTemplates[0];
  // 申込の受講者ごとの言語（受講者未登録なら申込の言語）
  const appLangCodes = (a) => {
    const named = (a.participants || []).filter(p => p?.name);
    return named.length ? named.map(p => p.language || a.language || '') : [a.language || ''];
  };
  // B/Cコースはコース開始日が必須
  const guideStartDateMissing = (f) => {
    if (['B', 'C'].includes(f?.courseOption) && !f?.courseStartDate) {
      toast.error(`${f.courseOption}コースは「コース開始日」を入力してください`);
      return true;
    }
    return false;
  };
  // 講習案内の受講者名: 受講者が1名ならその氏名を優先（申込者名ではなく）
  const getGuideUserName = (applicationId, fallback) => {
    const names = ((applications.find(a => a.id === applicationId)?.participants) || []).map(p => (p?.name || '').trim()).filter(Boolean);
    return names.length === 1 ? names[0] : (fallback || '');
  };
  const getAppSchedule = (app) => {
    if (!app) return null;
    return schedules.find(s => {
      if (s.courseId !== app.courseId) return false;
      if (app.preferredDate) {
        const pref = new Date(app.preferredDate);
        return pref >= new Date(s.startDate) && pref <= new Date(s.endDate);
      }
      return false;
    }) || [...schedules].filter(s => s.courseId === app.courseId).sort((a, b) => String(b.startDate || '').localeCompare(String(a.startDate || '')))[0] || null;
  };
  const getAppScheduleLabel = (app) => {
    const match = getAppSchedule(app);
    if (!match || !match.startDate) return '';
    return `${match.startDate}${match.endDate ? ` 〜 ${match.endDate}` : ''}`;
  };

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [adminInfo, setAdminInfo] = useState(null);
  const [applications, setApplications] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);

  const [certificateRequests, setCertificateRequests] = useState([]);
  const [version, setVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [activeTab, setActiveTab] = useState('applications');
  const [editUserData, setEditUserData] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [showHistory, setShowHistory] = useState({});
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  
  // Certificate issue dialog
  const [certIssueDialogOpen, setCertIssueDialogOpen] = useState(false);
  const [certIssueRequest, setCertIssueRequest] = useState(null);
  const [certUserSearchQuery, setCertUserSearchQuery] = useState('');
  const [certUserSearchResults, setCertUserSearchResults] = useState(null);
  const [certLinkedGuide, setCertLinkedGuide] = useState(null);
  const [certNumber, setCertNumber] = useState('');
  const [certNumberSuffix, setCertNumberSuffix] = useState(''); // Suffix part for manual input
  const [certCompletionDate, setCertCompletionDate] = useState('');
  const [certSubTab, setCertSubTab] = useState('pending'); // 'pending' or 'completed'
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [adminAppDialogOpen, setAdminAppDialogOpen] = useState(false);
  const [adminAppForm, setAdminAppForm] = useState({ name: '', email: '', phone: '', company: '', courseId: '', scheduleName: '', preferredDate: '', message: '', userId: '' });
  const [mailUnreadCount, setMailUnreadCount] = useState(0); // Webmail unread count
  const [showNewCertDialog, setShowNewCertDialog] = useState(false); // New certificate dialog
  const [unlinkedCertsOpen, setUnlinkedCertsOpen] = useState(false);
  const [duplicateUsersOpen, setDuplicateUsersOpen] = useState(false);
  const [newCertData, setNewCertData] = useState({
    userId: '',
    courseId: '',
    type: 'digital',
    certificateNumber: '',
    completionDate: '',
    manualEntry: false,
    manualName: '',
    manualEmail: '',
    certificateNumberSuffix: ''
  });
  
  // Sort state for applications
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  
  // Sort state for contacts
  const [contactSortConfig, setContactSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  
  // Sort state for courses
  const [courseSortConfig, setCourseSortConfig] = useState({ key: 'name', direction: 'asc' });
  
  // Sort state for schedules
  const [scheduleSortConfig, setScheduleSortConfig] = useState({ key: 'startDate', direction: 'desc' });
  
  const [newUserData, setNewUserData] = useState({
    name: '',
    nameKana: '',
    email: '',
    password: '',
    phone: '',
    company: ''
  });
  const [addingUser, setAddingUser] = useState(false);
  
  // Invoice states
  const [invoices, setInvoices] = useState([]);
  const [guideTemplates, setGuideTemplates] = useState([]);
  const [practicalReservations, setPracticalReservations] = useState([]);
  const [monitoringSummary, setMonitoringSummary] = useState([]);
  const [monitoringLogs, setMonitoringLogs] = useState([]);
  const [monitoringFilter, setMonitoringFilter] = useState({ userId: '', courseId: '' });
  const [selectedPractical, setSelectedPractical] = useState(null);
  const [trainingGuides, setTrainingGuides] = useState([]);
  const [guideTemplateDialogOpen, setGuideTemplateDialogOpen] = useState(false);
  const [guideTemplateForm, setGuideTemplateForm] = useState({ title: '', venue: '', venueAddress: '', meetingTime: '', itemsToBring: '', notes: '', mapUrl: '', additionalInfo: '', timetable: [] });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [guideCreateDialogOpen, setGuideCreateDialogOpen] = useState(false);
  const [guideCreateForm, setGuideCreateForm] = useState({});
  // 集合時間 = 時間割の初日1限の開始時刻の10分前。日付が無ければ時刻のみ返す
  const computeGuideMeetingTime = (timetable, startDate, fallbackTime) => {
    // fallbackは時刻部分のみ採用（"YYYY-MM-DD HH:MM" や "09:00 08:20" 等の末尾を使う）
    const fbParts = (fallbackTime || '').trim().split(/\s+/);
    let time = (fbParts[fbParts.length - 1] || '08:20').trim();
    // 時間割の開始時刻は slots[].time（例:"8:30～8:40"）に入る。periodは時限ラベル
    const firstSlot = timetable?.[0]?.slots?.[0] || {};
    const src = firstSlot.time || firstSlot.period || '';
    const m = src.match(/(\d{1,2})[:：](\d{2})/);
    if (m) {
      let total = parseInt(m[1], 10) * 60 + parseInt(m[2], 10) - 10;
      if (total < 0) total = 0;
      time = `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
    }
    return startDate ? `${startDate} ${time}` : time;
  };

  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceFormData, setInvoiceFormData] = useState({
    applicationId: '', userId: '', userName: '', userEmail: '',
    companyName: '', companyAddress: '', companyPhone: '',
    courseName: '', items: [{ name: '', amount: 0 }],
    totalAmount: 0, note: '', sendEmail: true, sendWhatsApp: true, dueDate: '', hideParticipants: false, recipientDisplay: 'both'
  });
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceDetailOpen, setInvoiceDetailOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentConfirmOpen, setPaymentConfirmOpen] = useState(false);
  const [quoteApp, setQuoteApp] = useState(null);
  const [paymentConfirmData, setPaymentConfirmData] = useState({
    paidAt: '', paymentNote: '', confirmedBy: '', paymentMethod: ''
  });
  
  // File input refs for document uploads
  const idFrontRef = useRef(null);
  const idBackRef = useRef(null);
  const certFrontRef = useRef(null);
  const certBackRef = useRef(null);
  const adminTabsRef = useRef(null);
  const idFrontCameraRef = useRef(null);
  const idBackCameraRef = useRef(null);
  const certFrontCameraRef = useRef(null);
  const certBackCameraRef = useRef(null);
  const [newSchedule, setNewSchedule] = useState({
    courseId: '',
    courseName: '',
    startDate: '',
    endDate: '',
    scheduleType: 'consecutive',
    selectedDates: [],
    capacity: 15,
    enrolled: 0,
    location: '',
    notes: '',
    status: 'open'
  });
  const [newCourse, setNewCourse] = useState({
    id: '',
    category: 'forklift',
    type: 'skill',
    name: { ja: '', en: '', pt: '', id: '', vi: '', es: '', tl: '' },
    description: { ja: '', en: '', pt: '', id: '', vi: '', es: '', tl: '' },
    price: 0,
    days: 1,
    theoryHours: 0,
    practicalHours: 0,
    totalHours: 0,
    image: '',
    featured: false,
    color: '',
    registrationNumber: '',
    courseType: 'in-person',
    onDemandSettings: {
      viewingPeriodDays: 30,
      passingScore: 80,
      allowRetake: true,
      maxRetakeCount: 3,
      certificateAutoIssue: true
    }
  });

  // Video management states
  const [courseVideos, setCourseVideos] = useState([]);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [newVideo, setNewVideo] = useState({
    title: '',
    description: '',
    requiredWatchPercent: 90
  });
  const videoFileRef = useRef(null);
  const [newVideoLang, setNewVideoLang] = useState('ja');
  const [translatingVideos, setTranslatingVideos] = useState(false);
  const [quizLangTab, setQuizLangTab] = useState('ja');
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [videoEdit, setVideoEdit] = useState({ title: '', description: '', requiredWatchPercent: 90 });
  const [courseLangTab, setCourseLangTab] = useState('ja');
  const [translatingCourse, setTranslatingCourse] = useState(false);
  const [uploadingVideoLang, setUploadingVideoLang] = useState(null); // `${videoId}:${lang}`
  const videoLangFileRef = useRef(null);
  const videoLangTargetRef = useRef(null); // { videoId, lang }

  // Seal image upload states
  const [uploadingSeal, setUploadingSeal] = useState(false);
  const sealFileRef = useRef(null);

  // Quiz/Test management states
  const [quizDialogOpen, setQuizDialogOpen] = useState(false);
  const [selectedVideoForQuiz, setSelectedVideoForQuiz] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [savingQuiz, setSavingQuiz] = useState(false);
  const [translatingQuiz, setTranslatingQuiz] = useState(false);
  const [quizData, setQuizData] = useState({
    title: t('admin.quiz.defaultTitle'),
    passingScore: 80,
    timeLimit: null,
    questions: []
  });

  // New management states
  const [recruitments, setRecruitments] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [businessReports, setBusinessReports] = useState([]);
  const [recruitmentFormOpen, setRecruitmentFormOpen] = useState(false);
  const [editingRecruitment, setEditingRecruitment] = useState(null);
  const [editingMembership, setEditingMembership] = useState(null);
  const [recruitmentForm, setRecruitmentForm] = useState({ title: '', location: '', salary: '', workHours: '', requirements: '', description: '', isPublished: true });
  const [membershipForm, setMembershipForm] = useState({ name: '', conditions: '', fee: '', benefits: '', description: '', isPublished: true });
  const [reportForm, setReportForm] = useState({ fiscalYear: '', title: '', reportType: 'business', isPublished: true });
  const [editingReportId, setEditingReportId] = useState(null);

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      verifyToken(token);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.email) {
        setAdminInfo(response.data);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('adminToken');
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('adminToken');
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        setAdminInfo({ email: response.data.email, name: response.data.name });
        setIsAuthenticated(true);
        toast.success(t('admin.loginSuccess'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.loginError'));
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t('admin.nameRequired'));
      return;
    }
    if (password.length < 6) {
      toast.error(t('admin.passwordMinLength'));
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/register`, { email, password, name });
      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        setAdminInfo({ email: response.data.email, name: response.data.name });
        setIsAuthenticated(true);
        toast.success(t('admin.registerSuccess'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.registerFailed'));
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminInfo(null);
    localStorage.removeItem('adminToken');
    toast.success(t('admin.logoutSuccess'));
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Admin upload document for user
  const handleAdminUploadDocument = async (userId, file, docType) => {
    if (!file) return;
    
    setUploadingDoc(docType);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('docType', docType);
    
    try {
      const response = await axios.post(
        `${API}/admin/users/${userId}/documents`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        toast.success(t('admin.certificates.uploadSuccess'));
        // Map docType to upload date field
        const dateFieldMap = {
          idDocumentFront: 'idDocumentFrontUploadedAt',
          idDocumentBack: 'idDocumentBackUploadedAt',
          certDocumentFront: 'certDocumentFrontUploadedAt',
          certDocumentBack: 'certDocumentBackUploadedAt'
        };
        const dateField = dateFieldMap[docType];
        // Update local state with path and upload date
        setSelectedItem(prev => ({
          ...prev, 
          [docType]: response.data.path,
          [dateField]: response.data.uploadedAt
        }));
        setEditUserData(prev => ({
          ...prev, 
          [docType]: response.data.path,
          [dateField]: response.data.uploadedAt
        }));
        // Refresh user list
        fetchData();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || t('admin.common.uploadFailed'));
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleAdminDeleteDocument = async (userId, docType) => {
    if (!window.confirm(t('admin.certificates.deleteCurrentConfirm'))) return;
    try {
      const response = await axios.delete(
        `${API}/admin/users/${userId}/documents/${docType}`,
        { headers: getAuthHeaders() }
      );
      if (response.data.success) {
        toast.success(t('admin.certificates.deleteSuccess'));
        const dateFieldMap = {
          idDocumentFront: 'idDocumentFrontUploadedAt',
          idDocumentBack: 'idDocumentBackUploadedAt',
          certDocumentFront: 'certDocumentFrontUploadedAt',
          certDocumentBack: 'certDocumentBackUploadedAt'
        };
        const dateField = dateFieldMap[docType];
        setSelectedItem(prev => ({ ...prev, [docType]: null, [dateField]: null }));
        setEditUserData(prev => ({ ...prev, [docType]: null, [dateField]: null }));
        fetchData();
      }
    } catch (error) {
      console.error('Delete document error:', error);
      toast.error(error.response?.data?.detail || t('admin.common.deleteFailed'));
    }
  };

  const handleAdminDeleteDocumentHistory = async (userId, docType, historyIndex) => {
    if (!window.confirm(t('admin.certificates.deleteHistoryConfirm'))) return;
    try {
      const response = await axios.delete(
        `${API}/admin/users/${userId}/documents/${docType}/history/${historyIndex}`,
        { headers: getAuthHeaders() }
      );
      if (response.data.success) {
        toast.success(t('admin.certificates.deleteHistorySuccess'));
        const historyFieldMap = {
          idDocumentFront: 'idDocumentFrontHistory',
          idDocumentBack: 'idDocumentBackHistory',
          certDocumentFront: 'certDocumentFrontHistory',
          certDocumentBack: 'certDocumentBackHistory'
        };
        const historyField = historyFieldMap[docType];
        setSelectedItem(prev => {
          const newHistory = [...(prev[historyField] || [])];
          newHistory.splice(historyIndex, 1);
          return { ...prev, [historyField]: newHistory };
        });
        fetchData();
      }
    } catch (error) {
      console.error('Delete history error:', error);
      toast.error(error.response?.data?.detail || t('admin.common.deleteFailed'));
    }
  };

  // Add new user (admin)
  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserData.name || !newUserData.email || !newUserData.password) {
      toast.error(t('admin.users.requiredFields'));
      return;
    }
    
    setAddingUser(true);
    try {
      const response = await axios.post(`${API}/admin/users`, newUserData, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        toast.success(t('admin.users.registerSuccess'));
        setShowAddUserModal(false);
        setNewUserData({ name: '', nameKana: '', email: '', password: '', phone: '', company: '' });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.users.registerError'));
    } finally {
      setAddingUser(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [appsRes, contactsRes, schedulesRes, coursesRes, settingsRes, versionRes, usersRes, certReqRes, invoicesRes, guideTemplatesRes, trainingGuidesRes, practicalRes, monitorRes, recruitRes, memberRes, reportRes] = await Promise.all([
        axios.get(`${API}/applications`, { headers }).catch(e => ({ data: [] })),
        axios.get(`${API}/contacts`, { headers }).catch(e => ({ data: [] })),
        axios.get(`${API}/schedules`).catch(e => ({ data: [] })),
        axios.get(`${API}/courses`).catch(e => ({ data: [] })),
        axios.get(`${API}/settings`).catch(e => ({ data: {} })),
        axios.get(`${API}/version`).catch(e => ({ data: { version: '' } })),
        axios.get(`${API}/admin/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/certificate-requests`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/invoices`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/guide-templates`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/training-guides`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/practical-reservations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/monitoring/summary`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/recruitments`).catch(() => ({ data: [] })),
        axios.get(`${API}/membership-types`).catch(() => ({ data: [] })),
        axios.get(`${API}/business-reports`).catch(() => ({ data: [] })),
      ]);
      setApplications(appsRes.data || []);
      setContacts(contactsRes.data || []);
      setSchedules(schedulesRes.data || []);
      setCourses(coursesRes.data || []);
      setSettings(settingsRes.data || {});
      setVersion(versionRes.data?.version || '');
      setUsers(usersRes.data || []);
      setCertificateRequests(certReqRes.data || []);
      setInvoices(invoicesRes.data || []);
      setGuideTemplates(guideTemplatesRes.data || []);
      setTrainingGuides(trainingGuidesRes.data || []);
      setPracticalReservations(practicalRes.data || []);
      setMonitoringSummary(monitorRes.data || []);
      setRecruitments(recruitRes.data || []);
      setMembershipTypes(memberRes.data || []);
      setBusinessReports(reportRes.data || []);

      // Fetch webmail unread count
      try {
        const unreadRes = await axios.get(`${API}/admin/mail/unread-count`, { headers });
        setMailUnreadCount(unreadRes.data?.unread || 0);
      } catch {}
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('admin.common.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // Application handlers
  const updateApplication = async (id, data) => {
    try {
      await axios.put(`${API}/applications/${id}`, data, { headers: getAuthHeaders() });
      
      // ステータスが「確定」に変更された場合、請求書作成ポップアップを表示
      if (data.status === 'confirmed') {
        const app = { ...(applications.find(a => a.id === id) || selectedItem), ...data };
        
        // 既存の請求書チェック（二重発行防止）
        const existingInvoice = invoices.find(inv => inv.applicationId === id);
        if (existingInvoice) {
          const proceed = window.confirm(
            `この申込には既に請求書（${existingInvoice.invoiceNumber}）が存在します。\n新しく請求書を作成しますか？`
          );
          if (!proceed) {
            toast.success(t('admin.applications.updateSuccess'));
            fetchData();
            setDialogOpen(false);
            return;
          }
        }

        const user = users.find(u => u.id === app?.userId || u.email === app?.email);
        const course = courses.find(c => c.id === app?.courseId);
        
        // 申込の希望日・コースIDからスケジュールを特定
        const matchSchedule = schedules.find(s => {
          if (s.courseId !== app?.courseId) return false;
          if (app?.preferredDate) {
            const pref = new Date(app.preferredDate);
            const start = new Date(s.startDate);
            const end = new Date(s.endDate);
            return pref >= start && pref <= end;
          }
          return true;
        }) || schedules.find(s => s.courseId === app?.courseId);
        
        // ドロップダウンと同じ形式のラベルを生成（請求書作成フォームの講習名を自動選択させる）
        let courseName = '';
        if (matchSchedule) {
          const statusLabel = matchSchedule.status === 'full' ? `【${t('admin.schedules.full')}】` : matchSchedule.status === 'closed' ? `【${t('admin.schedules.closed')}】` : matchSchedule.status === 'in_progress' ? `【${t('admin.schedules.inProgress')}】` : '';
          const sc = courses.find(c => c.id === matchSchedule.courseId) || courses.find(c => c.name?.ja === matchSchedule.courseName);
          const localName = sc?.name?.[language] || sc?.name?.ja || matchSchedule.courseName;
          courseName = `${statusLabel}${localName} (${matchSchedule.startDate}${matchSchedule.endDate ? ` 〜 ${matchSchedule.endDate}` : ''})`;
        } else {
          courseName = course?.name?.[language] || course?.name?.ja || app?.courseName || '';
        }
        
        setEditingInvoice(null);
        const _courseLabel = course?.name?.[language] || course?.name?.ja || app?.courseName || t('admin.invoices.courseFee');
        const _unitPrice = course?.price || 0;
        const _bulkNames = (app?.participants || []).map(p => (p?.name || '').trim()).filter(Boolean);
        let _items, _total;
        if (_bulkNames.length > 1) {
          _items = _bulkNames.map(n => ({ name: `${_courseLabel}（${n}様）`, amount: _unitPrice }));
          _total = _unitPrice * _bulkNames.length;
        } else {
          const _singleName = _bulkNames[0] || (app?.name || user?.name || '').trim();
          _items = [{ name: _singleName ? `${_courseLabel}（${_singleName}様）` : _courseLabel, amount: _unitPrice }];
          _total = _unitPrice;
        }
        setInvoiceFormData({
          applicationId: id,
          userId: app?.userId || user?.id || '',
          userName: app?.name || user?.name || '',
          userEmail: app?.email || user?.email || '',
          userPhone: app?.phone || user?.phone || '',
          userAddress: app?.address || user?.address || '',
          companyName: app?.company || user?.company || '',
          companyAddress: '',
          companyPhone: '',
          courseName: courseName,
          items: _items,
          totalAmount: _total,
          note: '',
          sendEmail: true,
          sendWhatsApp: true,
          dueDate: '',
          hideParticipants: false,
          recipientDisplay: 'both'
        });
        
        toast.success(t('admin.applications.updateSuccess'));
        fetchData();
        setDialogOpen(false);
        // 少し遅延して請求書ダイアログを開く
        setTimeout(() => setInvoiceDialogOpen(true), 300);
        return;
      }

      // 受講済み合格の場合、証明書発行済みに自動登録
      if (data.status === 'completed_passed' && data.certificateNumber) {
        const app = { ...(applications.find(a => a.id === id) || selectedItem), ...data };
        const user = users.find(u => u.id === app?.userId || u.email === app?.email);
        const course = courses.find(c => c.id === app?.courseId);
        
        // 既に同じ講習の発行済み証明書がないか確認
        const existingCert = certificateRequests.find(
          r => r.courseId === app?.courseId && r.userId === (app?.userId || user?.id) && r.status === 'completed'
        );
        
        if (!existingCert) {
          try {
            await axios.post(`${API}/admin/certificate-requests/create`, {
              userId: app?.userId || user?.id || '',
              userName: app?.name || user?.name || '',
              userEmail: app?.email || user?.email || '',
              courseId: app?.courseId || '',
              courseName: course?.name?.ja || app?.courseName || '',
              type: 'digital',
              status: 'completed',
              certificateNumber: data.certificateNumber,
              completionDate: data.completedAt || new Date().toISOString().split('T')[0]
            }, { headers: getAuthHeaders() });
          } catch (certError) {
            console.error('証明書自動登録エラー:', certError);
          }
        }
      }
      
      toast.success(t('admin.applications.updateSuccess'));
      fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error(t('admin.common.updateFailed'));
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm(t('admin.applications.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/applications/${id}`, { headers: getAuthHeaders() });
      toast.success(t('admin.applications.deleteSuccess'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.common.deleteFailed'));
    }
  };

  // Contact handlers
  const updateContact = async (id, data) => {
    try {
      await axios.put(`${API}/contacts/${id}`, data, { headers: getAuthHeaders() });
      toast.success(t('admin.contacts.updateSuccess'));
      fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error(t('admin.common.updateFailed'));
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm(t('admin.contacts.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/contacts/${id}`, { headers: getAuthHeaders() });
      toast.success(t('admin.contacts.deleteSuccess'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.common.deleteFailed'));
    }
  };

  // Schedule handlers
  const createSchedule = async () => {
    try {
      await axios.post(`${API}/schedules`, newSchedule, { headers: getAuthHeaders() });
      toast.success(t('admin.schedules.addSuccess'));
      fetchData();
      setDialogOpen(false);
      setNewSchedule({
        courseId: '',
        courseName: '',
        startDate: '',
        endDate: '',
        capacity: 15,
        enrolled: 0,
        location: '',
        notes: '',
        status: 'open'
      });
    } catch (error) {
      toast.error(t('admin.common.addFailed'));
    }
  };

  const updateSchedule = async (id, data) => {
    try {
      await axios.put(`${API}/schedules/${id}`, data, { headers: getAuthHeaders() });
      toast.success(t('admin.schedules.updateSuccess'));
      fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error(t('admin.common.updateFailed'));
    }
  };

  // 連日/とび日 共通の日付列挙・変換ヘルパー
  const enumerateDates = (start, end) => {
    if (!start || !end || start > end) return start ? [start] : [];
    const out = [];
    const [ys, ms, ds] = start.split('-').map(Number);
    const [ye, me, de] = end.split('-').map(Number);
    let cur = new Date(ys, ms - 1, ds);
    const last = new Date(ye, me - 1, de);
    while (cur <= last) {
      out.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`);
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  };
  const toISODateStr = (d) => {
    const x = new Date(d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  };
  const parseISODateObj = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };

  // スケジュール登録/編集の送信（連日・とび日、バリデーション込み）
  const submitScheduleForm = async () => {
    const isEdit = dialogType === 'editSchedule';
    const sForm = isEdit ? (selectedItem || {}) : newSchedule;
    const type = sForm.scheduleType || 'consecutive';
    if (!sForm.courseId) { toast.error(t('admin.form.selectCourse')); return; }
    let dates = [];
    if (type === 'selected_dates') {
      dates = Array.from(new Set(sForm.selectedDates || [])).sort();
      if (dates.length === 0) { toast.error('とび日では最低1日以上選択してください'); return; }
    } else {
      if (!sForm.startDate || !sForm.endDate) { toast.error('開始日と終了日を入力してください'); return; }
      if (sForm.startDate > sForm.endDate) { toast.error('開始日は終了日より後の日付を設定できません'); return; }
      dates = enumerateDates(sForm.startDate, sForm.endDate);
    }
    if (!(settings && settings.allowPastScheduleDates)) {
      const today = toISODateStr(new Date());
      if (dates.some(d => d < today)) { toast.error('過去日は登録できません（設定で許可に変更できます）'); return; }
    }
    const start = dates[0];
    const end = dates[dates.length - 1];
    const payload = {
      ...sForm,
      scheduleType: type,
      selectedDates: type === 'selected_dates' ? dates : [],
      startDate: start,
      endDate: end,
    };
    try {
      if (isEdit) {
        await axios.put(`${API}/schedules/${selectedItem.id}`, payload, { headers: getAuthHeaders() });
        toast.success(t('admin.schedules.updateSuccess'));
      } else {
        await axios.post(`${API}/schedules`, payload, { headers: getAuthHeaders() });
        toast.success(t('admin.schedules.addSuccess'));
        setNewSchedule({ courseId: '', courseName: '', startDate: '', endDate: '', scheduleType: 'consecutive', selectedDates: [], capacity: 15, enrolled: 0, location: '', notes: '', status: 'open' });
      }
      fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error(isEdit ? t('admin.common.updateFailed') : t('admin.common.addFailed'));
    }
  };


  const deleteSchedule = async (id) => {
    if (!window.confirm(t('admin.schedules.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/schedules/${id}`, { headers: getAuthHeaders() });
      toast.success(t('admin.schedules.deleteSuccess'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.common.deleteFailed'));
    }
  };

  // Course handlers
  const createCourse = async () => {
    if (!newCourse.id || !newCourse.name.ja) {
      toast.error(t('admin.courses.requiredFields'));
      return;
    }
    try {
      const courseData = {
        ...newCourse,
        totalHours: newCourse.theoryHours + newCourse.practicalHours
      };
      await axios.post(`${API}/courses`, courseData, { headers: getAuthHeaders() });
      toast.success(t('admin.courses.addSuccess'));
      fetchData();
      setDialogOpen(false);
      setNewCourse({
        id: '',
        category: 'forklift',
        type: 'skill',
        name: { ja: '', en: '', pt: '', id: '', vi: '', es: '', tl: '' },
        description: { ja: '', en: '', pt: '', id: '', vi: '', es: '', tl: '' },
        price: 0,
        days: 1,
        theoryHours: 0,
        practicalHours: 0,
        totalHours: 0,
        image: '',
        featured: false,
        color: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.common.addFailed'));
    }
  };

  const updateCourse = async (id, data) => {
    try {
      await axios.put(`${API}/courses/${id}`, data, { headers: getAuthHeaders() });
      toast.success(t('admin.courses.updateSuccess'));
      fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error(t('admin.common.updateFailed'));
    }
  };

  const deleteCourse = async (id) => {
    if (!window.confirm(t('admin.courses.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/courses/${id}`, { headers: getAuthHeaders() });
      toast.success(t('admin.courses.deleteSuccess'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.common.deleteFailed'));
    }
  };

  const handleTranslateCourse = async () => {
    const isEdit = dialogType === 'editCourse';
    const src = isEdit ? selectedItem : newCourse;
    const texts = {
      name: src?.name?.ja || '',
      description: src?.description?.ja || '',
      detailedContent: src?.detailedContent?.ja || '',
    };
    if (!texts.name && !texts.description && !texts.detailedContent) {
      toast.error(t('admin.courses.translateNoJa'));
      return;
    }
    if (!window.confirm(t('admin.courses.translateConfirm'))) return;
    const targetLangs = COURSE_LANGS.map(l => l.code).filter(c => c !== 'ja');
    setTranslatingCourse(true);
    try {
      const res = await axios.post(`${API}/admin/translate`,
        { texts, targetLangs, sourceLang: 'ja' },
        { headers: getAuthHeaders() });
      const tr = res.data.translations || {};
      const applyField = (field, existing) => {
        const out = { ...(existing || {}) };
        Object.entries(tr[field] || {}).forEach(([lng, val]) => {
          if (val) out[lng] = val;
        });
        return out;
      };
      const updated = {
        ...src,
        name: applyField('name', src?.name),
        description: applyField('description', src?.description),
        detailedContent: applyField('detailedContent', src?.detailedContent),
      };
      if (isEdit) {
        setSelectedItem(updated);
      } else {
        setNewCourse(updated);
      }
      toast.success(t('admin.courses.translateSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.courses.translateFailed'));
    } finally {
      setTranslatingCourse(false);
    }
  };

  // Video management functions
  const fetchCourseVideos = async (courseId) => {
    try {
      const response = await axios.get(`${API}/admin/courses/${courseId}/videos`, {
        headers: getAuthHeaders()
      });
      setCourseVideos(response.data.videos || []);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
      setCourseVideos([]);
    }
  };

  const uploadVideo = async () => {
    if (!videoFileRef.current?.files[0] || !newVideo.title || !selectedItem?.id) {
      toast.error(t('admin.videos.requiredFields'));
      return;
    }

    const file = videoFileRef.current.files[0];
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', newVideo.title);
    formData.append('description', newVideo.description || '');
    formData.append('order', courseVideos.length);
    formData.append('requiredWatchPercent', newVideo.requiredWatchPercent);
    formData.append('lang', newVideoLang);

    setUploadingVideo(true);
    setVideoUploadProgress(0);

    try {
      await axios.post(
        `${API}/admin/courses/${selectedItem.id}/videos`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setVideoUploadProgress(percent);
          }
        }
      );
      toast.success(t('admin.videos.uploadSuccess'));
      fetchCourseVideos(selectedItem.id);
      setNewVideo({ title: '', description: '', requiredWatchPercent: 90 });
      if (videoFileRef.current) videoFileRef.current.value = '';
    } catch (error) {
      console.error('Video upload error:', error);
      toast.error(t('admin.common.uploadFailed'));
    } finally {
      setUploadingVideo(false);
      setVideoUploadProgress(0);
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm(t('admin.videos.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/admin/courses/${selectedItem.id}/videos/${videoId}`, {
        headers: getAuthHeaders()
      });
      toast.success(t('admin.videos.deleteSuccess'));
      fetchCourseVideos(selectedItem.id);
    } catch (error) {
      toast.error(t('admin.common.deleteFailed'));
    }
  };

  const startEditVideo = (video) => {
    setEditingVideoId(video.id);
    setVideoEdit({
      title: locVal(video.title, newVideoLang),
      description: locVal(video.description, newVideoLang),
      requiredWatchPercent: video.requiredWatchPercent ?? 90,
      _titleObj: video.title,
      _descObj: video.description,
    });
  };

  const updateVideoMeta = async (videoId) => {
    if (!videoEdit.title?.trim() || !selectedItem?.id) {
      toast.error(t('admin.videos.requiredFields'));
      return;
    }
    try {
      await axios.put(
        `${API}/admin/courses/${selectedItem.id}/videos/${videoId}`,
        {
          title: setLocVal(videoEdit._titleObj, newVideoLang, videoEdit.title),
          description: setLocVal(videoEdit._descObj, newVideoLang, videoEdit.description || ''),
          requiredWatchPercent: parseInt(videoEdit.requiredWatchPercent) || 90,
        },
        { headers: getAuthHeaders() }
      );
      toast.success(t('admin.videos.updateSuccess'));
      setEditingVideoId(null);
      fetchCourseVideos(selectedItem.id);
    } catch (error) {
      console.error('Video update error:', error);
      toast.error(t('admin.common.updateFailed'));
    }
  };


  const handleTranslateVideos = async () => {
    const targetLangs = COURSE_LANGS.map(l => l.code).filter(c => c !== 'ja');
    const asDict = (v) => (typeof v === 'string' ? { ja: v } : (v || {}));
    const vids = courseVideos || [];
    const toDo = vids.filter(v => {
      const tt = asDict(v.title), dd = asDict(v.description);
      const tNeed = tt.ja && targetLangs.some(l => !tt[l]);
      const dNeed = dd.ja && targetLangs.some(l => !dd[l]);
      return tNeed || dNeed;
    });
    if (toDo.length === 0) {
      toast.info(t('admin.videos.translateNothing'));
      return;
    }
    if (!window.confirm(t('admin.videos.translateConfirm').replace('{n}', toDo.length))) return;
    setTranslatingVideos(true);
    let done = 0, failed = 0;
    for (const v of toDo) {
      try {
        const titleObj = asDict(v.title), descObj = asDict(v.description);
        const texts = {};
        if (titleObj.ja) texts.title = titleObj.ja;
        if (descObj.ja) texts.description = descObj.ja;
        const res = await axios.post(`${API}/admin/translate`,
          { texts, targetLangs, sourceLang: 'ja' },
          { headers: getAuthHeaders() });
        const tr = res.data.translations || {};
        const newTitle = { ...titleObj };
        Object.entries(tr.title || {}).forEach(([l, val]) => { if (val && !newTitle[l]) newTitle[l] = val; });
        const newDesc = { ...descObj };
        Object.entries(tr.description || {}).forEach(([l, val]) => { if (val && !newDesc[l]) newDesc[l] = val; });
        await axios.put(`${API}/admin/courses/${selectedItem.id}/videos/${v.id}`,
          { title: newTitle, description: newDesc, requiredWatchPercent: v.requiredWatchPercent ?? 90 },
          { headers: getAuthHeaders() });
        done++;
      } catch (e) {
        failed++;
      }
    }
    setTranslatingVideos(false);
    if (selectedItem?.id) fetchCourseVideos(selectedItem.id);
    toast.success(t('admin.videos.translateDone').replace('{done}', done).replace('{failed}', failed));
  };


  const triggerVideoLangUpload = (videoId, lang) => {
    videoLangTargetRef.current = { videoId, lang };
    if (videoLangFileRef.current) {
      videoLangFileRef.current.value = '';
      videoLangFileRef.current.click();
    }
  };

  const handleVideoLangFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const target = videoLangTargetRef.current;
    if (!file || !target || !selectedItem?.id) return;
    const { videoId, lang } = target;
    const key = `${videoId}:${lang}`;
    setUploadingVideoLang(key);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('lang', lang);
      await axios.post(
        `${API}/admin/courses/${selectedItem.id}/videos/${videoId}/language`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(t('admin.videos.langUploadSuccess'));
      fetchCourseVideos(selectedItem.id);
    } catch (error) {
      console.error('Language video upload error:', error);
      toast.error(t('admin.common.uploadFailed'));
    } finally {
      setUploadingVideoLang(null);
      videoLangTargetRef.current = null;
      if (videoLangFileRef.current) videoLangFileRef.current.value = '';
    }
  };

  const deleteVideoLang = async (videoId, lang) => {
    if (!window.confirm(t('admin.videos.langDeleteConfirm'))) return;
    try {
      await axios.delete(
        `${API}/admin/courses/${selectedItem.id}/videos/${videoId}/language/${lang}`,
        { headers: getAuthHeaders() }
      );
      toast.success(t('admin.videos.deleteSuccess'));
      fetchCourseVideos(selectedItem.id);
    } catch (error) {
      toast.error(t('admin.common.deleteFailed'));
    }
  };

  // Quiz/Test management functions
  const handleTranslateQuiz = async () => {
    const targetLangs = COURSE_LANGS.map(l => l.code).filter(c => c !== 'ja');
    const asDict = (v) => (typeof v === 'string' ? { ja: v } : (v || {}));
    const texts = {};
    const titleObj = asDict(quizData.title);
    if (titleObj.ja) texts['title'] = titleObj.ja;
    (quizData.questions || []).forEach((q, qi) => {
      const qt = asDict(q.questionText);
      if (qt.ja) texts[`q_${qi}`] = qt.ja;
      (q.options || []).forEach((o, oi) => {
        const ot = asDict(o.text);
        if (ot.ja) texts[`q_${qi}_o_${oi}`] = ot.ja;
      });
    });
    if (Object.keys(texts).length === 0) {
      toast.info(t('admin.quiz.translateNothing'));
      return;
    }
    if (!window.confirm(t('admin.quiz.translateConfirm'))) return;
    setTranslatingQuiz(true);
    try {
      const res = await axios.post(`${API}/admin/translate`,
        { texts, targetLangs, sourceLang: 'ja' },
        { headers: getAuthHeaders() });
      const tr = res.data.translations || {};
      const newTitle = asDict(quizData.title);
      Object.entries(tr['title'] || {}).forEach(([l, val]) => { if (val && !newTitle[l]) newTitle[l] = val; });
      const newQuestions = (quizData.questions || []).map((q, qi) => {
        const qt = asDict(q.questionText);
        Object.entries(tr[`q_${qi}`] || {}).forEach(([l, val]) => { if (val && !qt[l]) qt[l] = val; });
        const newOptions = (q.options || []).map((o, oi) => {
          const ot = asDict(o.text);
          Object.entries(tr[`q_${qi}_o_${oi}`] || {}).forEach(([l, val]) => { if (val && !ot[l]) ot[l] = val; });
          return { ...o, text: ot };
        });
        return { ...q, questionText: qt, options: newOptions };
      });
      setQuizData(prev => ({ ...prev, title: newTitle, questions: newQuestions }));
      toast.success(t('admin.quiz.translateDone'));
    } catch (e) {
      toast.error(t('admin.quiz.translateFailed'));
    } finally {
      setTranslatingQuiz(false);
    }
  };

  const openQuizEditor = async (video) => {
    setSelectedVideoForQuiz(video);
    setQuizLoading(true);
    setQuizDialogOpen(true);
    
    try {
      // Check if quiz already exists for this video
      if (video.quiz) {
        setQuizData({
          title: video.quiz.title || t('admin.quiz.defaultTitle'),
          passingScore: video.quiz.passingScore || 80,
          timeLimit: video.quiz.timeLimit || null,
          questions: video.quiz.questions || []
        });
      } else {
        // Initialize with empty quiz
        setQuizData({
          title: t('admin.quiz.defaultTitle'),
          passingScore: 80,
          timeLimit: null,
          questions: []
        });
      }
    } catch (error) {
      console.error('Error loading quiz:', error);
      toast.error(t('admin.quiz.loadFailed'));
    } finally {
      setQuizLoading(false);
    }
  };

  const addQuestion = () => {
    setQuizData(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          id: `new_${Date.now()}`,
          questionText: {},
          questionType: 'single',
          options: [
            { text: {}, isCorrect: false },
            { text: {}, isCorrect: false },
            { text: {}, isCorrect: false },
            { text: {}, isCorrect: false }
          ],
          points: 1
        }
      ]
    }));
  };

  const updateQuestion = (index, field, value) => {
    setQuizData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const updateQuestionOption = (qIndex, oIndex, field, value) => {
    setQuizData(prev => ({
      ...prev,
      questions: prev.questions.map((q, qi) => 
        qi === qIndex 
          ? {
              ...q,
              options: q.options.map((opt, oi) => 
                oi === oIndex ? { ...opt, [field]: value } : opt
              )
            }
          : q
      )
    }));
  };

  const toggleCorrectOption = (qIndex, oIndex) => {
    setQuizData(prev => ({
      ...prev,
      questions: prev.questions.map((q, qi) => {
        if (qi !== qIndex) return q;
        
        if (q.questionType === 'single') {
          // Single choice: only one can be correct
          return {
            ...q,
            options: q.options.map((opt, oi) => ({
              ...opt,
              isCorrect: oi === oIndex
            }))
          };
        } else {
          // Multiple choice: toggle the option
          return {
            ...q,
            options: q.options.map((opt, oi) => 
              oi === oIndex ? { ...opt, isCorrect: !opt.isCorrect } : opt
            )
          };
        }
      })
    }));
  };

  const removeQuestion = (index) => {
    if (!window.confirm(t('admin.quiz.deleteQuestionConfirm'))) return;
    setQuizData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const addOption = (qIndex) => {
    setQuizData(prev => ({
      ...prev,
      questions: prev.questions.map((q, qi) => 
        qi === qIndex 
          ? { ...q, options: [...q.options, { text: {}, isCorrect: false }] }
          : q
      )
    }));
  };

  const removeOption = (qIndex, oIndex) => {
    setQuizData(prev => ({
      ...prev,
      questions: prev.questions.map((q, qi) => 
        qi === qIndex 
          ? { ...q, options: q.options.filter((_, oi) => oi !== oIndex) }
          : q
      )
    }));
  };

  const saveQuiz = async () => {
    if (!selectedVideoForQuiz || !selectedItem) return;
    
    // Validate quiz
    if (quizData.questions.length === 0) {
      toast.error(t('admin.quiz.minOneQuestion'));
      return;
    }
    
    for (let i = 0; i < quizData.questions.length; i++) {
      const q = quizData.questions[i];
      if (!locVal(q.questionText, 'ja').trim()) {
        toast.error(`${t('admin.quiz.questionNum')}${i + 1}${t('admin.quiz.enterText')}`);
        return;
      }
      const hasValidOptions = q.options.filter(o => locVal(o.text, 'ja').trim()).length >= 2;
      if (!hasValidOptions) {
        toast.error(`${t('admin.quiz.questionNum')}${i + 1}${t('admin.quiz.minTwoOptions')}`);
        return;
      }
      const hasCorrect = q.options.some(o => o.isCorrect);
      if (!hasCorrect) {
        toast.error(`${t('admin.quiz.questionNum')}${i + 1}${t('admin.quiz.selectCorrect')}`);
        return;
      }
    }
    
    setSavingQuiz(true);
    try {
      const method = selectedVideoForQuiz.quiz ? 'put' : 'post';
      await axios[method](
        `${API}/admin/courses/${selectedItem.id}/videos/${selectedVideoForQuiz.id}/quiz`,
        quizData,
        { headers: getAuthHeaders() }
      );
      
      toast.success(t('admin.quiz.saveSuccess'));
      setQuizDialogOpen(false);
      fetchCourseVideos(selectedItem.id);
    } catch (error) {
      console.error('Error saving quiz:', error);
      toast.error(t('admin.quiz.saveFailed'));
    } finally {
      setSavingQuiz(false);
    }
  };

  const deleteQuiz = async () => {
    if (!selectedVideoForQuiz || !selectedItem) return;
    if (!window.confirm(t('admin.quiz.deleteConfirm'))) return;
    
    try {
      await axios.delete(
        `${API}/admin/courses/${selectedItem.id}/videos/${selectedVideoForQuiz.id}/quiz`,
        { headers: getAuthHeaders() }
      );
      toast.success(t('admin.quiz.deleteSuccess'));
      setQuizDialogOpen(false);
      fetchCourseVideos(selectedItem.id);
    } catch (error) {
      toast.error(t('admin.quiz.deleteFailed'));
    }
  };

  const updateOnDemandSettings = async (courseId, settings) => {
    try {
      await axios.put(
        `${API}/admin/courses/${courseId}/ondemand-settings`,
        settings,
        { headers: getAuthHeaders() }
      );
      toast.success(t('admin.courses.saveSuccess'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.settings.saveError'));
    }
  };

  // Settings handlers
  const updateSettings = async () => {
    try {
      await axios.put(`${API}/settings`, settings, { headers: getAuthHeaders() });
      toast.success(t('admin.settings.saveSuccess'));
      setEditMode(false);
    } catch (error) {
      toast.error(t('admin.common.saveFailed'));
    }
  };

  // Upload seal image for certificates
  const uploadSealImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('admin.settings.selectImageFile'));
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('admin.settings.fileSizeLimit'));
      return;
    }

    setUploadingSeal(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/settings/seal`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update settings with new seal image path
      setSettings(prev => ({
        ...prev,
        sealImage: response.data.sealImage
      }));
      
      toast.success(t('admin.settings.sealUploadSuccess'));
    } catch (error) {
      console.error('Seal upload error:', error);
      toast.error(t('admin.common.uploadFailed'));
    } finally {
      setUploadingSeal(false);
      if (sealFileRef.current) {
        sealFileRef.current.value = '';
      }
    }
  };

  // Delete seal image
  const deleteSealImage = async () => {
    if (!window.confirm(t('admin.settings.sealDeleteConfirm'))) return;

    try {
      await axios.delete(`${API}/admin/settings/seal`, {
        headers: getAuthHeaders()
      });
      
      setSettings(prev => ({
        ...prev,
        sealImage: null
      }));
      
      toast.success(t('admin.settings.sealDeleteSuccess'));
    } catch (error) {
      toast.error(t('admin.common.deleteFailed'));
    }
  };

  const openDialog = (type, item = null) => {
    setDialogType(type);
    
    // For editApplication, extract certificateNumberSuffix from existing certificateNumber
    if (type === 'editApplication' && item) {
      const course = courses.find(c => c.id === item.courseId);
      const prefix = course?.registrationNumber || '';
      let suffix = '';
      if (item.certificateNumber && prefix && item.certificateNumber.startsWith(prefix + '-')) {
        suffix = item.certificateNumber.substring(prefix.length + 1);
      } else if (item.certificateNumber && !prefix) {
        suffix = item.certificateNumber;
      }
      // Auto-fill nationality / material language from the applicant's user profile when missing
      const user = users.find(u => u.id === item.userId || u.email === item.email);
      const langMap = {
        'Japan': 'ja', '日本': 'ja',
        'Brazil': 'pt', 'ブラジル': 'pt', 'Portugal': 'pt',
        'United States': 'en', 'アメリカ': 'en', 'United Kingdom': 'en', 'Australia': 'en', 'Canada': 'en', 'Philippines': 'tl', 'フィリピン': 'tl',
        'Vietnam': 'vi', 'ベトナム': 'vi',
        'Indonesia': 'id', 'インドネシア': 'id',
        'Spain': 'es', 'スペイン': 'es', 'Mexico': 'es', 'メキシコ': 'es', 'Peru': 'es', 'ペルー': 'es', 'Bolivia': 'es', 'ボリビア': 'es', 'Colombia': 'es', 'Argentina': 'es',
        'China': 'zh', '中国': 'zh', 'Korea': 'ja', '韓国': 'ja',
      };
      const nationality = item.nationality || user?.nationality || '';
      const language = item.language || user?.language || langMap[nationality] || '';
      setSelectedItem({...item, nationality, language, certificateNumberSuffix: suffix});
    } else {
      setSelectedItem(item);
    }
    
    setDialogOpen(true);
    
    // The course list excludes detailedContent for performance; fetch the full
    // course so the rich-text editor is populated (and not wiped on save).
    if (type === 'editCourse' && item?.id) {
      axios.get(`${API}/courses/${item.id}`, { headers: getAuthHeaders() })
        .then((res) => {
          if (res.data) {
            setSelectedItem((prev) => (prev && prev.id === item.id ? { ...prev, ...res.data } : prev));
          }
        })
        .catch(() => {});
    }
    
    // Reset password change mode when opening dialogs
    setShowPasswordChange(false);
    
    // Fetch videos when opening video management dialog
    if (type === 'manageVideos' && item?.id) {
      fetchCourseVideos(item.id);
    }
  };

  const filteredApplications = applications.filter(app =>
    app.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.courseName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredContacts = contacts.filter(contact =>
    contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCourses = courses.filter(course =>
    course.name?.ja?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort applications
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedApplications = [...filteredApplications].sort((a, b) => {
    const { key, direction } = sortConfig;
    let aValue = a[key];
    let bValue = b[key];
    
    // Handle date fields
    if (key === 'createdAt' || key === 'updatedAt') {
      aValue = new Date(aValue || 0).getTime();
      bValue = new Date(bValue || 0).getTime();
    }
    
    // Handle string fields
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = String(bValue || '').toLowerCase();
    }
    
    // Handle null/undefined
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';
    
    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronDown className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-[#00A651]" />
      : <ChevronDown className="h-3 w-3 text-[#00A651]" />;
  };

  // Generic sort indicator for other tabs
  const SortIndicatorFor = ({ config, columnKey }) => {
    if (config.key !== columnKey) {
      return <ChevronDown className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100" />;
    }
    return config.direction === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-[#00A651]" />
      : <ChevronDown className="h-3 w-3 text-[#00A651]" />;
  };

  // Generic sort handler
  const createSortHandler = (setConfig) => (key) => {
    setConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleContactSort = createSortHandler(setContactSortConfig);
  const handleCourseSort = createSortHandler(setCourseSortConfig);
  const handleScheduleSort = createSortHandler(setScheduleSortConfig);

  // Generic sort function
  const sortData = (data, config) => {
    return [...data].sort((a, b) => {
      const { key, direction } = config;
      let aValue = key.includes('.') ? key.split('.').reduce((o, k) => o?.[k], a) : a[key];
      let bValue = key.includes('.') ? key.split('.').reduce((o, k) => o?.[k], b) : b[key];
      
      // Handle date fields
      if (key.includes('Date') || key === 'createdAt' || key === 'updatedAt') {
        aValue = new Date(aValue || 0).getTime();
        bValue = new Date(bValue || 0).getTime();
      }
      
      // Handle string fields
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }
      
      // Handle null/undefined
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Sorted data for each tab
  const sortedContacts = sortData(filteredContacts, contactSortConfig);
  const sortedCourses = sortData(filteredCourses, courseSortConfig);
  const sortedSchedules = sortData(schedules, scheduleSortConfig);

  // 一覧の表示件数（10/20/50/100件、既定20件）
  const certPendingList = certificateRequests.filter(r => r.status !== 'completed');
  const certCompletedList = certificateRequests.filter(r => r.status === 'completed');

  // 一覧ごとのキーワード検索＋絞り込み
  const applicationFilter = useFilteredList(
    sortedApplications,
    ['name', 'nameKana', 'email', 'phone', 'company', 'courseName',
      a => (a.participants || []).map(p => p?.name).join(' '),
      a => getLocalCourseName(a)],
    [{ key: 'status', label: 'ステータス', options: [
      { value: 'pending', label: t('admin.applications.pending') },
      { value: 'confirmed', label: t('admin.applications.confirmed') },
      { value: 'completed_passed', label: t('admin.applications.completedPassed') },
      { value: 'completed_failed', label: t('admin.applications.completedFailed') },
      { value: 'cancelled', label: t('admin.common.cancelled') },
    ] },
    { key: 'language', label: '言語', get: a => appLangCodes(a).map(c => c || 'none'), options: LANG_FILTER_OPTIONS },
    { key: 'courseId', label: '講習', get: a => a.courseId, options: [...new Set(schedules.map(sc => sc.courseId).filter(Boolean))]
      .map(id => ({ value: id, label: courses.find(c => c.id === id)?.name?.ja || schedules.find(sc => sc.courseId === id)?.courseName || id }))
      .sort((x, y) => { const ix = courses.findIndex(c => c.id === x.value), iy = courses.findIndex(c => c.id === y.value); return (ix < 0 ? 999 : ix) - (iy < 0 ? 999 : iy); }) },
    { key: 'scheduleId', label: '日程', dependsOn: 'courseId', get: a => getAppSchedule(a)?.id, options: [...schedules]
      .filter(sc => sc.courseId && sc.startDate)
      .sort((x, y) => String(x.startDate).localeCompare(String(y.startDate)))
      .map(sc => ({ value: sc.id, parent: sc.courseId, label: `${sc.startDate}${sc.endDate ? ` 〜 ${sc.endDate}` : ''}` })) }]
  );
  const contactFilter = useFilteredList(
    sortedContacts,
    ['name', 'email', 'subject', 'message'],
    [{ key: 'status', label: t('admin.common.status'), options: [
      { value: 'unread', label: t('admin.contacts.unread') },
      { value: 'read', label: t('admin.contacts.read') },
    ] }]
  );
  const certPendingFilter = useFilteredList(
    certPendingList,
    ['userName', 'userEmail', 'courseName', 'certificateNumber', r => getLocalCourseName(r)],
    [{ key: 'status', label: t('admin.common.status'), options: [
      { value: 'pending', label: t('admin.applications.pending') },
      { value: 'processing', label: t('admin.certificates.processing') },
    ] }]
  );
  const certCompletedFilter = useFilteredList(
    certCompletedList,
    ['userName', 'userEmail', 'courseName', 'certificateNumber', 'completionDate', r => getLocalCourseName(r)]
  );
  const practicalFilter = useFilteredList(
    practicalReservations,
    ['userName', 'userEmail', 'courseName', 'venue', 'confirmedDate', r => getLocalCourseName(r)],
    [{ key: 'status', label: t('admin.common.status'), options: [
      { value: 'pending', label: t('admin.practical.pending') },
      { value: 'approved', label: t('admin.practical.approved') },
      { value: 'completed', label: t('admin.practical.pass') },
      { value: 'rejected', label: t('admin.practical.rejected') },
    ] }]
  );

  const applicationPager = usePagedList(applicationFilter.result);
  const contactPager = usePagedList(contactFilter.result);
  const certPendingPager = usePagedList(certPendingFilter.result);
  const certCompletedPager = usePagedList(certCompletedFilter.result);
  const practicalPager = usePagedList(practicalFilter.result);
  const recruitmentPager = usePagedList(recruitments);
  const membershipPager = usePagedList(membershipTypes);
  const reportPager = usePagedList(businessReports);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center" data-testid="admin-login">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-[#00A651] p-3 rounded-full">
              <Lock className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-center text-gray-900 mb-6">
            {isRegistering ? t('admin.register') : t('admin.login')}
          </h1>
          
          <form onSubmit={isRegistering ? handleRegister : handleLogin}>
            {isRegistering && (
              <div className="mb-4">
                <Label htmlFor="name">{t('admin.name')}</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder={t('admin.form.enterName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full mt-1"
                  data-testid="admin-name-input"
                />
              </div>
            )}
            <div className="mb-4">
              <Label htmlFor="email">{t('admin.form.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('admin.form.enterEmail')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1"
                data-testid="admin-email-input"
              />
            </div>
            <div className="mb-6">
              <Label htmlFor="password">{t('admin.password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t('admin.form.enterPassword')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-1"
                data-testid="admin-password-input"
              />
              {isRegistering && (
                <p className="text-xs text-gray-500 mt-1">{t('admin.passwordHint')}</p>
              )}
            </div>
            <Button 
              type="submit" 
              className="w-full bg-[#00A651] hover:bg-[#008a43] mb-4"
              data-testid="admin-submit-button"
            >
              {isRegistering ? (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  {t('admin.registerButton')}
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4 mr-2" />
                  {t('admin.loginButton')}
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-page">
      {/* User photo lightbox popup */}
      <Dialog open={!!lightboxImg} onOpenChange={(o) => { if (!o) setLightboxImg(null); }}>
        <DialogContent className="max-w-lg p-2 bg-black/95 border-none" data-testid="user-photo-lightbox">
          {lightboxImg && (
            <img src={lightboxImg} alt="" className="w-full max-h-[80vh] object-contain rounded" />
          )}
        </DialogContent>
      </Dialog>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_jwsea-edu/artifacts/885p674v_image.png" 
                alt="JWSEA Logo" 
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{t('admin.title')}</h1>
                <p className="text-xs text-gray-500">Ver. {version} | {adminInfo?.name || adminInfo?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={fetchData}
                disabled={loading}
                className="flex items-center gap-2"
                data-testid="refresh-button"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                {t('admin.common.refresh')}
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                {t('admin.logout')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
          <div 
            className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-[#00A651] cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => { setActiveTab('applications'); setTimeout(() => adminTabsRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('admin.stats.applications')}</p>
                <p className="text-2xl font-bold text-gray-900">{applications.length}</p>
                <div className="flex gap-2 mt-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">{t('admin.applications.pending')}: {applications.filter(a => a.status === 'pending').length}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">{t('admin.applications.confirmed')}: {applications.filter(a => a.status !== 'pending').length}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700">{t('admin.stats.guides')}: {trainingGuides.length}</span>
                </div>
              </div>
              <FileText className="h-8 w-8 text-[#00A651]" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => { setActiveTab('contacts'); setTimeout(() => adminTabsRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('admin.stats.contacts')}</p>
                <p className="text-2xl font-bold text-gray-900">{contacts.length}</p>
                <div className="flex gap-2 mt-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">{t('admin.contacts.unread')}: {contacts.filter(c => c.status === 'unread').length}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">{t('admin.contacts.read')}: {contacts.filter(c => c.status !== 'unread').length}</span>
                </div>
              </div>
              <Mail className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-purple-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => { setActiveTab('schedules'); setTimeout(() => adminTabsRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('admin.stats.schedules')}</p>
                <p className="text-2xl font-bold text-gray-900">{schedules.length}</p>
                <div className="flex gap-2 mt-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">{t('admin.schedules.open')}: {schedules.filter(s => s.status === 'open' || s.status === 'full' || s.status === 'in_progress').length}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">{t('admin.schedules.closed')}: {schedules.filter(s => s.status === 'closed').length}</span>
                </div>
              </div>
              <Calendar className="h-8 w-8 text-purple-500" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-orange-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => { setActiveTab('courses'); setTimeout(() => adminTabsRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('admin.stats.courses')}</p>
                <p className="text-2xl font-bold text-gray-900">{courses.length}</p>
              </div>
              <BookOpen className="h-8 w-8 text-orange-500" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => { setActiveTab('certificates'); setTimeout(() => adminTabsRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('admin.stats.pendingCertificates')}</p>
                <p className="text-2xl font-bold text-gray-900">{certificateRequests.length}</p>
                <div className="flex gap-2 mt-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">{t('admin.certificates.pending')}: {certificateRequests.filter(r => r.status !== 'completed').length}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">{t('admin.certificates.issued')}: {certificateRequests.filter(r => r.status === 'completed').length}</span>
                </div>
              </div>
              <Award className="h-8 w-8 text-green-500" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-amber-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => { setActiveTab('invoices'); setTimeout(() => adminTabsRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('admin.stats.invoices')}</p>
                <p className="text-2xl font-bold text-gray-900">{invoices.length}</p>
                <div className="flex gap-2 mt-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${invoices.filter(i => i.status === 'unpaid').length > 0 ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-orange-100 text-orange-700'}`}>{t('admin.invoices.unpaid')}: {invoices.filter(i => i.status === 'unpaid').length}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">{t('admin.invoices.paid')}: {invoices.filter(i => i.status === 'paid').length}</span>
                </div>
              </div>
              <Receipt className="h-8 w-8 text-amber-500" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-teal-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => { setActiveTab('users'); setTimeout(() => adminTabsRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('admin.stats.members')}</p>
                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
              </div>
              <Users className="h-8 w-8 text-teal-500" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-gray-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => { setActiveTab('settings'); setTimeout(() => adminTabsRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('admin.stats.settings')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  <Settings className="h-6 w-6 text-gray-500" />
                </p>
              </div>
              <Settings className="h-8 w-8 text-gray-500" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-indigo-500 cursor-pointer hover:shadow-md transition-shadow"
            data-testid="dashboard-webmail-card"
            onClick={() => { setActiveTab('webmail'); setTimeout(() => adminTabsRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('admin.stats.webmail')}</p>
                <div className="flex gap-2 mt-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">{t('admin.contacts.unread')}: {mailUnreadCount}</span>
                </div>
              </div>
              <Mail className="h-8 w-8 text-indigo-500" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-500 cursor-pointer hover:shadow-md transition-shadow"
            data-testid="dashboard-monitoring-card"
            onClick={() => { setActiveTab('monitoring'); setTimeout(() => adminTabsRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('admin.stats.monitoring')}</p>
                <p className="text-2xl font-bold text-gray-900">{monitoringSummary.length}</p>
              </div>
              <Eye className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-rose-500 cursor-pointer hover:shadow-md transition-shadow"
            data-testid="dashboard-recruitment-card"
            onClick={() => { setActiveTab('recruitment'); setTimeout(() => adminTabsRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('admin.stats.recruitments')}</p>
                <p className="text-2xl font-bold text-gray-900">{recruitments.length}</p>
              </div>
              <Briefcase className="h-8 w-8 text-rose-500" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-violet-500 cursor-pointer hover:shadow-md transition-shadow"
            data-testid="dashboard-membership-card"
            onClick={() => { setActiveTab('membership'); setTimeout(() => adminTabsRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('admin.stats.memberships')}</p>
                <p className="text-2xl font-bold text-gray-900">{membershipTypes.length}</p>
              </div>
              <UserCheck className="h-8 w-8 text-violet-500" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-emerald-500 cursor-pointer hover:shadow-md transition-shadow"
            data-testid="dashboard-reports-card"
            onClick={() => { setActiveTab('reports'); setTimeout(() => adminTabsRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('admin.stats.reports')}</p>
                <p className="text-2xl font-bold text-gray-900">{businessReports.length}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-emerald-500" />
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder={t('admin.common.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full"
              data-testid="admin-search"
            />
          </div>
        </div>

        {/* Tabs */}
        <div ref={adminTabsRef}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="bg-white rounded-lg shadow-sm">
          <TabsList className="w-full border-b border-gray-200 rounded-t-lg p-0 h-auto grid grid-cols-8">
            <TabsTrigger 
              value="applications" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
            >
              <FileText className="h-4 w-4 mr-1" />{t('admin.tabs.applications')}
              {applications.filter(a => a.status === 'pending').length > 0 && (
                <Badge className="ml-1 bg-yellow-500 text-white text-[10px] px-1.5 py-0" data-testid="applications-tab-pending-badge">{applications.filter(a => a.status === 'pending').length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="certificates" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
            >
              <Award className="h-4 w-4 mr-1" />
              {t('admin.tabs.certificateRequests')}
              {certificateRequests.filter(r => r.status === 'pending').length > 0 && (
                <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0">
                  {certificateRequests.filter(r => r.status === 'pending').length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="contacts" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
            >
              <Mail className="h-4 w-4 mr-1" />{t('admin.tabs.contacts')}</TabsTrigger>
            <TabsTrigger 
              value="courses" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
            >
              <BookOpen className="h-4 w-4 mr-1" />{t('admin.tabs.courses')}</TabsTrigger>
            <TabsTrigger 
              value="schedules" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
            >
              <Calendar className="h-4 w-4 mr-1" />
              {t('admin.tabs.schedules')}
            </TabsTrigger>
            <TabsTrigger 
              value="settings" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
            >
              <Settings className="h-4 w-4 mr-1" />
              {t('admin.settings.title')}
            </TabsTrigger>
            <TabsTrigger 
              value="users" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
            >
              <Users className="h-4 w-4 mr-1" />{t('admin.tabs.users')}</TabsTrigger>
            <TabsTrigger 
              value="invoices" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
            >
              <Receipt className="h-4 w-4 mr-1" />{t('admin.tabs.invoices')}</TabsTrigger>
            <TabsTrigger 
              value="guides" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
            >
              <FileCheck className="h-4 w-4 mr-1" />
              {t('admin.certificates.trainingGuide')}
            </TabsTrigger>
            <TabsTrigger 
              value="guidePatterns" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
            >
              <BookOpen className="h-4 w-4 mr-1" />
              {t('admin.guides.editPattern')}
            </TabsTrigger>
            <TabsTrigger 
              value="webmail" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
              data-testid="webmail-tab"
            >
              <Mail className="h-4 w-4 mr-1" />{t('admin.tabs.webmail')}</TabsTrigger>
            <TabsTrigger 
              value="practical" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
              data-testid="practical-tab"
            >
              <ClipboardCheck className="h-4 w-4 mr-1" />
              {t('admin.tabs.practical')}
            </TabsTrigger>
            <TabsTrigger 
              value="monitoring" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
              data-testid="monitoring-tab"
            >
              <Eye className="h-4 w-4 mr-1" />{t('admin.tabs.monitoring')}</TabsTrigger>
            <TabsTrigger 
              value="recruitment" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
              data-testid="recruitment-tab"
            >
              <Briefcase className="h-4 w-4 mr-1" />{t('admin.tabs.recruitment')}</TabsTrigger>
            <TabsTrigger 
              value="membership" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
              data-testid="membership-tab"
            >
              <UserCheck className="h-4 w-4 mr-1" />{t('admin.tabs.membership')}</TabsTrigger>
            <TabsTrigger 
              value="reports" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
              data-testid="reports-tab"
            >
              <BarChart3 className="h-4 w-4 mr-1" />{t('admin.tabs.reports')}</TabsTrigger>
            <TabsTrigger 
              value="news" 
              className="py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs"
              data-testid="news-tab"
            >
              <Newspaper className="h-4 w-4 mr-1" />お知らせ</TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="applications" className="p-0">
            <div className="p-4 border-b bg-gray-50 flex flex-wrap items-center justify-between gap-3" data-testid="applications-count-bar">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-700">申込件数</span>
                <span className="inline-flex items-center gap-1 text-sm px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-800 font-semibold" data-testid="applications-pending-count">
                  未対応 {applications.filter(a => a.status === 'pending').length}件
                </span>
                <span className="inline-flex items-center gap-1 text-sm px-2.5 py-1 rounded-full bg-green-100 text-green-800 font-semibold" data-testid="applications-handled-count">
                  対応済 {applications.filter(a => a.status !== 'pending').length}件
                </span>
                <span className="inline-flex items-center gap-1 text-sm px-2.5 py-1 rounded-full bg-gray-200 text-gray-700" data-testid="applications-total-count">
                  合計 {applications.length}件
                </span>
              </div>
              <Button size="sm" className="bg-[#00A651] hover:bg-[#008a43]" onClick={() => {
                setAdminAppForm({ name: '', email: '', phone: '', company: '', courseId: '', scheduleName: '', preferredDate: '', message: '', userId: '' });
                setAdminAppDialogOpen(true);
              }}>
                <Plus className="h-4 w-4 mr-1" />
                新規申込追加
              </Button>
            </div>
            <div className="overflow-x-auto">
              <div className="px-4 pt-3">
                <ListToolbar filter={applicationFilter} testId="applications" placeholder="氏名・メール・受講者・講習で検索" />
                <LangCounts codes={applicationFilter.result.flatMap(appLangCodes)} label="受講者の言語別人数" testId="applications" />
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="w-[100px] cursor-pointer hover:bg-gray-100 group select-none"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center gap-1">
                        {t('admin.common.dateTime')}
                        <SortIndicator columnKey="createdAt" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100 group select-none"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-1">
                        {t('admin.applications.applicant')}
                        <SortIndicator columnKey="name" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100 group select-none"
                      onClick={() => handleSort('email')}
                    >
                      <div className="flex items-center gap-1">{t('admin.tabs.webmail')}<SortIndicator columnKey="email" />
                      </div>
                    </TableHead>
                    <TableHead>{t('admin.applications.participantInfo')}</TableHead>
                    <TableHead>言語</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100 group select-none"
                      onClick={() => handleSort('courseName')}
                    >
                      <div className="flex items-center gap-1">
                        {t('admin.applications.course')}
                        <SortIndicator columnKey="courseName" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="w-[80px] cursor-pointer hover:bg-gray-100 group select-none"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center gap-1">
                        {t('admin.common.status')}
                        <SortIndicator columnKey="status" />
                      </div>
                    </TableHead>
                    <TableHead className="w-[120px]">{t('admin.common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applicationPager.total === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        {t('admin.applications.noApplications')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    applicationPager.paged.map((app) => (
                      <TableRow key={app.id}>
                        <TableCell className="text-xs text-gray-500">
                          {formatDateShort(app.createdAt)}
                        </TableCell>
                        <TableCell className="font-medium" data-testid={`app-applicant-name-${app.id}`}>
                          {((app.name || '').match(/[\s\S]{1,20}/gu) || []).map((line, i) => <div key={i} className="leading-tight whitespace-nowrap">{line}</div>)}
                        </TableCell>
                        <TableCell className="text-sm">{app.email}</TableCell>
                        <TableCell className="text-sm max-w-[180px]" data-testid={`app-participants-${app.id}`}>
                          {(app.participants && app.participants.filter(p => p?.name).length > 0)
                            ? app.participants.filter(p => p?.name).map((p, i) => <div key={i} className="leading-tight">{p.name}</div>)
                            : '-'}
                        </TableCell>
                        <TableCell data-testid={`app-language-${app.id}`}>
                          {(app.participants && app.participants.filter(p => p?.name).length > 0)
                            ? app.participants.filter(p => p?.name).map((p, i) => {
                                const code = p.language || app.language;
                                return (
                                  <div key={i} className="leading-tight">
                                    {code ? <span className="text-[10px] px-1 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 whitespace-nowrap" data-testid={`app-participant-lang-${app.id}-${i}`}>{LANG_LABELS_JA[code] || code}</span> : '-'}
                                  </div>
                                );
                              })
                            : (app.language ? <span className="text-[10px] px-1 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 whitespace-nowrap">{LANG_LABELS_JA[app.language] || app.language}</span> : '-')}
                        </TableCell>
                        <TableCell className="text-sm max-w-[150px]">
                          <div className="flex items-center gap-1 min-w-0">
                            {app.courseOption && (
                              <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#e6f6ec] text-[#00A651] border border-[#00A651]/30" data-testid={`app-course-option-${app.id}`}>{app.courseOption}コース</span>
                            )}
                            <span className="truncate">{getLocalCourseName(app)}</span>
                          </div>
                          {getAppScheduleLabel(app) && (
                            <div className="text-xs text-gray-500 mt-0.5" data-testid={`app-schedule-${app.id}`}>{getAppScheduleLabel(app)}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            className={
                              app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              app.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                              app.status === 'completed_passed' ? 'bg-green-100 text-green-800' :
                              app.status === 'completed_failed' ? 'bg-orange-100 text-orange-800' :
                              app.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }
                          >
                            {app.status === 'pending' ? t('admin.applications.pending') :
                             app.status === 'confirmed' ? t('admin.applications.confirmed') :
                             app.status === 'completed_passed' ? t('admin.applications.completedPassed') :
                             app.status === 'completed_failed' ? t('admin.applications.completedFailed') :
                             app.status === 'cancelled' ? t('admin.common.cancelled') : app.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openDialog('viewApplication', app)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openDialog('editApplication', app)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" title="見積書" className="text-[#00A651]" onClick={() => setQuoteApp(app)} data-testid={`app-quote-btn-${app.id}`}>
                              <FileSignature className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteApplication(app.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="px-4">
              <ListPager pager={applicationPager} testId="applications" />
            </div>
          </TabsContent>

          {/* Certificate Requests Tab - Unified */}
          <TabsContent value="certificates" className="p-0">
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
              <div className="flex gap-2">
                <Button size="sm" variant={certSubTab === 'pending' ? 'default' : 'outline'}
                  className={certSubTab === 'pending' ? 'bg-[#00A651] hover:bg-[#008a43]' : ''}
                  onClick={() => setCertSubTab('pending')}>
                  {t('admin.certificates.pending')}
                  <Badge className="ml-1 bg-yellow-500 text-white text-xs px-1.5 py-0">{certificateRequests.filter(r => r.status !== 'completed').length}</Badge>
                </Button>
                <Button size="sm" variant={certSubTab === 'completed' ? 'default' : 'outline'}
                  className={certSubTab === 'completed' ? 'bg-[#00A651] hover:bg-[#008a43]' : ''}
                  onClick={() => setCertSubTab('completed')}>
                  {t('admin.certificates.issued')}
                  <Badge className="ml-1 bg-green-500 text-white text-xs px-1.5 py-0">{certificateRequests.filter(r => r.status === 'completed').length}</Badge>
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setUnlinkedCertsOpen(true)}
                  variant="outline"
                  size="sm"
                  data-testid="open-unlinked-certs-btn"
                >
                  <Link2 className="h-4 w-4 mr-1" />
                  未紐付けを一括リンク
                </Button>
                {certSubTab === 'completed' && (
                  <Button
                    onClick={() => {
                      setNewCertData({ userId: '', courseId: '', type: 'digital', certificateNumber: '', completionDate: '', manualEntry: false, manualName: '', manualEmail: '', certificateNumberSuffix: '' });
                      setShowNewCertDialog(true);
                    }}
                    className="bg-[#00A651] hover:bg-[#008a43]"
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('admin.common.addNew')}
                  </Button>
                )}
              </div>
            </div>
            {certSubTab === 'pending' && (
            <div className="overflow-x-auto">
              <div className="px-4 pt-3">
                <ListToolbar filter={certPendingFilter} testId="cert-pending" placeholder="氏名・メール・講習・証明書番号で検索" />
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">{t('admin.certificates.requestDate')}</TableHead>
                    <TableHead>{t('admin.certificates.userName')}</TableHead>
                    <TableHead>{t('admin.tabs.webmail')}</TableHead>
                    <TableHead>{t('admin.certificates.courseName')}</TableHead>
                    <TableHead className="w-[140px]">{t('admin.certificates.types')}</TableHead>
                    <TableHead className="w-[120px]">{t('admin.certificates.existingNumber')}</TableHead>
                    <TableHead className="w-[100px]">{t('admin.common.status')}</TableHead>
                    <TableHead className="w-[120px]">{t('admin.common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {certPendingPager.total === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        {t('admin.certificates.noCertificates')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    certPendingPager.paged.map((req) => {
                      // Get types array (support both old 'type' and new 'types' format)
                      const certTypes = req.types || (req.type ? [req.type] : []);
                      return (
                      <TableRow key={req.id}>
                        <TableCell className="text-sm">{formatDate(req.createdAt)}</TableCell>
                        <TableCell className="font-medium">{req.userName}</TableCell>
                        <TableCell className="text-sm">{req.userEmail}</TableCell>
                        <TableCell className="text-sm">
                          <div className="font-medium text-gray-900">{getLocalCourseName(req)}</div>
                          {getCertScheduleLabel(req) && (
                            <div className="text-xs text-gray-500 mt-0.5" data-testid={`cert-schedule-${req.id}`}>{getCertScheduleLabel(req)}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {certTypes.map((ct, idx) => (
                              <Badge key={idx} className={ct === 'card' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                                {ct === 'card' ? t('admin.certificates.card') : t('admin.certificates.digital')}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {req.certificateNumber ? (
                            <span className="text-[#00A651] font-medium">{req.certificateNumber}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            req.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                            req.status === 'passed' || req.status === 'completed' ? 'bg-green-100 text-green-700' :
                            req.status === 'failed' ? 'bg-red-100 text-red-700' :
                            req.status === 'cancelled' ? 'bg-gray-100 text-gray-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {req.status === 'pending' ? t('admin.certificates.pending') :
                             req.status === 'processing' ? t('admin.certificates.processing') :
                             req.status === 'passed' || req.status === 'completed' ? t('admin.certificates.passed') :
                             req.status === 'failed' ? t('admin.certificates.failed') :
                             req.status === 'cancelled' ? t('admin.common.cancelled') : req.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {req.status === 'pending' && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/admin/certificate-requests/${req.id}`, 
                                      { status: 'processing' },
                                      { headers: getAuthHeaders() }
                                    );
                                    toast.success(t('admin.certificates.processingChanged'));
                                    fetchData();
                                  } catch (error) {
                                    toast.error(t('admin.common.updateFailed'));
                                  }
                                }}
                              >
                                {t('admin.certificates.startProcessing')}
                              </Button>
                            )}
                            {req.status !== 'pending' && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-green-600"
                                onClick={() => {
                                  // Auto-fill courseId from courseName if missing
                                  let fixedReq = { ...req };
                                  if (!fixedReq.courseId && fixedReq.courseName) {
                                    const matchCourse = courses.find(c => c.name?.ja === fixedReq.courseName);
                                    if (matchCourse) fixedReq.courseId = matchCourse.id;
                                  }
                                  // Map 'completed' back to 'passed' for the dropdown
                                  if (fixedReq.status === 'completed') fixedReq.status = 'passed';
                                  setCertIssueRequest(fixedReq);
                                  // If existing certificate number exists, pre-fill it
                                  if (fixedReq.certificateNumber) {
                                    const course = courses.find(c => c.id === fixedReq.courseId) || courses.find(c => c.name?.ja === fixedReq.courseName);
                                    const regNum = course?.registrationNumber || '';
                                    if (regNum && fixedReq.certificateNumber.startsWith(regNum + '-')) {
                                      setCertNumberSuffix(fixedReq.certificateNumber.substring(regNum.length + 1));
                                    } else {
                                      setCertNumberSuffix(fixedReq.certificateNumber);
                                    }
                                    setCertNumber(fixedReq.certificateNumber);
                                  } else {
                                    setCertNumber('');
                                    setCertNumberSuffix('');
                                  }
                                  setCertCompletionDate(fixedReq.completionDate || '');
                                  // Find linked training guide
                                  const linkedGuide = trainingGuides.find(g => g.applicationId === fixedReq.applicationId) || null;
                                  setCertLinkedGuide(linkedGuide);
                                  // Fill user info from users collection (always use latest)
                                  if (fixedReq.userId) {
                                    const u = users.find(usr => usr.id === fixedReq.userId);
                                    if (u) {
                                      fixedReq.userBirthDate = fixedReq.userBirthDate || u.birthDate || '';
                                      fixedReq.userAddress = fixedReq.userAddress || u.address || '';
                                      fixedReq.userPhoto = fixedReq.userPhoto || u.profileImage || u.photoFront || '';
                                    }
                                  } else if (fixedReq.userName || fixedReq.userEmail) {
                                    // Try to match by name or email even if userId is empty
                                    const u = users.find(usr => 
                                      (fixedReq.userEmail && usr.email === fixedReq.userEmail) ||
                                      (fixedReq.userName && usr.name === fixedReq.userName)
                                    );
                                    if (u) {
                                      fixedReq.userId = u.id;
                                      fixedReq.userBirthDate = fixedReq.userBirthDate || u.birthDate || '';
                                      fixedReq.userAddress = fixedReq.userAddress || u.address || '';
                                      fixedReq.userPhoto = fixedReq.userPhoto || u.profileImage || u.photoFront || '';
                                    }
                                  }
                                  setCertIssueRequest(fixedReq);
                                  setCertIssueDialogOpen(true);
                                  setCertUserSearchQuery('');
                                  setCertUserSearchResults(null);                                }}
                              >
                                編集
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={async () => {
                                if (window.confirm(t('admin.certificates.deleteConfirm'))) {
                                  try {
                                    await axios.delete(`${API}/admin/certificate-requests/${req.id}`, {
                                      headers: getAuthHeaders()
                                    });
                                    toast.success(t('admin.common.deleted'));
                                    fetchData();
                                  } catch (error) {
                                    toast.error(t('admin.common.deleteFailed'));
                                  }
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );})
                  )}
                </TableBody>
              </Table>
            </div>
            )}
            {certSubTab === 'pending' && (
              <div className="px-4">
                <ListPager pager={certPendingPager} testId="cert-pending" />
              </div>
            )}
            {certSubTab === 'completed' && (
            <div className="overflow-x-auto">
              <div className="px-4 pt-3">
                <ListToolbar filter={certCompletedFilter} testId="cert-completed" placeholder="氏名・メール・講習・証明書番号で検索" />
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">{t('admin.certificates.issueDate')}</TableHead>
                    <TableHead>{t('admin.certificates.userName')}</TableHead>
                    <TableHead>{t('admin.tabs.webmail')}</TableHead>
                    <TableHead>{t('admin.certificates.courseName')}</TableHead>
                    <TableHead className="w-[140px]">{t('admin.certificates.types')}</TableHead>
                    <TableHead className="w-[120px]">{t('admin.certificates.certNumber')}</TableHead>
                    <TableHead className="w-[100px]">{t('admin.certificates.completionDate')}</TableHead>
                    <TableHead className="w-[120px]">{t('admin.common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {certCompletedPager.total === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        {t('admin.certificates.noIssuedCertificates')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    certCompletedPager.paged.map((req) => {
                      // Get types array (support both old 'type' and new 'types' format)
                      const certTypes = req.types || (req.type ? [req.type] : []);
                      return (
                      <TableRow key={req.id}>
                        <TableCell className="text-sm">{req.updatedAt ? formatDate(req.updatedAt) : formatDate(req.createdAt)}</TableCell>
                        <TableCell className="font-medium">{req.userName}</TableCell>
                        <TableCell className="text-sm">{req.userEmail}</TableCell>
                        <TableCell className="text-sm">{getLocalCourseName(req)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {certTypes.map((ct, idx) => (
                              <Badge key={idx} className={ct === 'card' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                                {ct === 'card' ? t('admin.certificates.card') : t('admin.certificates.digital')}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm font-medium text-[#00A651]">
                          {req.certificateNumber || '-'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {req.completionDate || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-600 hover:text-green-800"
                              title="PDF表示"
                              onClick={async () => {
                                try {
                                  const res = await axios.get(`${API}/admin/certificate-requests/${req.id}/pdf`, {
                                    headers: getAuthHeaders(),
                                    responseType: 'blob'
                                  });
                                  const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                  window.open(url, '_blank');
                                } catch(e) { toast.error('PDF生成に失敗しました'); }
                              }}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-gray-500 hover:text-blue-600"
                              onClick={() => {
                                let fixedReq2 = { ...req };
                                if (!fixedReq2.courseId && fixedReq2.courseName) {
                                  const mc = courses.find(c => c.name?.ja === fixedReq2.courseName);
                                  if (mc) fixedReq2.courseId = mc.id;
                                }
                                // Map 'completed' back to 'passed' for the dropdown
                                if (fixedReq2.status === 'completed') fixedReq2.status = 'passed';
                                setCertIssueRequest(fixedReq2);
                                const course = courses.find(c => c.id === fixedReq2.courseId) || courses.find(c => c.name?.ja === fixedReq2.courseName);
                                const regNum = course?.registrationNumber || '';
                                const existingNum = fixedReq2.certificateNumber || '';
                                if (regNum && existingNum.startsWith(regNum + '-')) {
                                  setCertNumberSuffix(existingNum.substring(regNum.length + 1));
                                } else {
                                  setCertNumberSuffix(existingNum);
                                }
                                setCertNumber(existingNum);
                                setCertCompletionDate(req.completionDate || '');
                                const linkedGuide2 = trainingGuides.find(g => g.applicationId === fixedReq2.applicationId) || null;
                                setCertLinkedGuide(linkedGuide2);
                                // Fill user info from users collection (always use latest)
                                if (fixedReq2.userId) {
                                  const u2 = users.find(usr => usr.id === fixedReq2.userId);
                                  if (u2) {
                                    fixedReq2.userBirthDate = fixedReq2.userBirthDate || u2.birthDate || '';
                                    fixedReq2.userAddress = fixedReq2.userAddress || u2.address || '';
                                    fixedReq2.userPhoto = fixedReq2.userPhoto || u2.profileImage || u2.photoFront || '';
                                  }
                                } else if (fixedReq2.userName || fixedReq2.userEmail) {
                                  const u2 = users.find(usr => 
                                    (fixedReq2.userEmail && usr.email === fixedReq2.userEmail) ||
                                    (fixedReq2.userName && usr.name === fixedReq2.userName)
                                  );
                                  if (u2) {
                                    fixedReq2.userId = u2.id;
                                    fixedReq2.userBirthDate = fixedReq2.userBirthDate || u2.birthDate || '';
                                    fixedReq2.userAddress = fixedReq2.userAddress || u2.address || '';
                                    fixedReq2.userPhoto = fixedReq2.userPhoto || u2.profileImage || u2.photoFront || '';
                                  }
                                }
                                setCertIssueRequest(fixedReq2);
                              setCertIssueDialogOpen(true);
                              setCertUserSearchQuery('');
                              setCertUserSearchResults(null);
                              }}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              {t('admin.common.edit')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-gray-500 hover:text-red-600"
                              onClick={async () => {
                                if (!window.confirm(t('admin.certificates.deleteIssuedConfirm'))) return;
                                try {
                                  await axios.delete(`${API}/admin/certificate-requests/${req.id}`, {
                                    headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
                                  });
                                  toast.success(t('admin.certificates.deleteSuccess'));
                                  fetchData();
                                } catch (error) {
                                  toast.error(t('admin.common.deleteFailed'));
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              {t('admin.common.delete')}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );})
                  )}
                </TableBody>
              </Table>
            </div>
            )}
            {certSubTab === 'completed' && (
              <div className="px-4">
                <ListPager pager={certCompletedPager} testId="cert-completed" />
              </div>
            )}
          </TabsContent>


          {/* Contacts Tab */}
          <TabsContent value="contacts" className="p-0">
            <div className="overflow-x-auto">
              <div className="px-4 pt-3">
                <ListToolbar filter={contactFilter} testId="contacts" placeholder="氏名・メール・件名・本文で検索" />
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="w-[100px] cursor-pointer hover:bg-gray-100 group select-none"
                      onClick={() => handleContactSort('createdAt')}
                    >
                      <div className="flex items-center gap-1">
                        {t('admin.common.dateTime')}
                        <SortIndicatorFor config={contactSortConfig} columnKey="createdAt" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100 group select-none"
                      onClick={() => handleContactSort('name')}
                    >
                      <div className="flex items-center gap-1">
                        {t('admin.common.name')}
                        <SortIndicatorFor config={contactSortConfig} columnKey="name" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100 group select-none"
                      onClick={() => handleContactSort('email')}
                    >
                      <div className="flex items-center gap-1">{t('admin.tabs.webmail')}<SortIndicatorFor config={contactSortConfig} columnKey="email" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100 group select-none"
                      onClick={() => handleContactSort('subject')}
                    >
                      <div className="flex items-center gap-1">
                        {t('admin.contacts.subject')}
                        <SortIndicatorFor config={contactSortConfig} columnKey="subject" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="w-[80px] cursor-pointer hover:bg-gray-100 group select-none"
                      onClick={() => handleContactSort('status')}
                    >
                      <div className="flex items-center gap-1">
                        {t('admin.common.status')}
                        <SortIndicatorFor config={contactSortConfig} columnKey="status" />
                      </div>
                    </TableHead>
                    <TableHead className="w-[120px]">{t('admin.common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contactPager.total === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        {t('admin.contacts.noContacts')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    contactPager.paged.map((contact) => (
                      <TableRow key={contact.id}>
                        <TableCell className="text-xs text-gray-500">
                          {formatDateShort(contact.createdAt)}
                        </TableCell>
                        <TableCell className="font-medium">{contact.name}</TableCell>
                        <TableCell className="text-sm">{contact.email}</TableCell>
                        <TableCell className="text-sm max-w-[150px] truncate">
                          {contact.subject}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            className={contact.status === 'unread' 
                              ? 'bg-yellow-100 text-yellow-800' 
                              : 'bg-green-100 text-green-800'
                            }
                          >
                            {contact.status === 'unread' ? t('admin.contacts.unread') : t('admin.contacts.read')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openDialog('viewContact', contact)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openDialog('editContact', contact)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteContact(contact.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="px-4">
              <ListPager pager={contactPager} testId="contacts" />
            </div>
          </TabsContent>

          {/* Courses Tab */}
          <CoursesTab
            sortedCourses={sortedCourses}
            handleCourseSort={handleCourseSort}
            courseSortConfig={courseSortConfig}
            SortIndicatorFor={SortIndicatorFor}
            openDialog={openDialog}
            deleteCourse={deleteCourse}
            categories={categories}
            types={types}
            t={t}
          />

          {/* Schedules Tab */}
          <SchedulesTab
            sortedSchedules={sortedSchedules}
            handleScheduleSort={handleScheduleSort}
            scheduleSortConfig={scheduleSortConfig}
            SortIndicatorFor={SortIndicatorFor}
            openDialog={openDialog}
            deleteSchedule={deleteSchedule}
            formatDateShort={formatDateShort}
            t={t}
            courses={courses}
            language={language}
            applications={applications}
          />

          {/* Settings Tab */}
          {/* Webmail Tab */}
          <TabsContent value="webmail" className="p-4">
            <WebMail getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          {/* Practical Reservations Tab */}
          <TabsContent value="practical" className="p-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('admin.practical.title')}</h3>
            <ListToolbar filter={practicalFilter} testId="practical" placeholder="氏名・メール・講習・会場で検索" />
            {practicalPager.total === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <ClipboardCheck className="h-12 w-12 mx-auto mb-2" />
                <p>{t('admin.practical.noReservations')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {practicalPager.paged.map(res => (
                  <div key={res.id} className={`border rounded-lg p-4 ${res.status === 'pending' ? 'border-yellow-300 bg-yellow-50' : res.result === 'pass' ? 'border-green-300 bg-green-50' : res.result === 'fail' ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{res.userName}</span>
                          <span className="text-xs text-gray-500">{res.userEmail}</span>
                          <Badge className={`text-xs ${
                            res.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                            res.status === 'approved' ? 'bg-blue-200 text-blue-800' :
                            res.result === 'pass' ? 'bg-green-200 text-green-800' :
                            res.result === 'fail' ? 'bg-red-200 text-red-800' :
                            'bg-gray-200 text-gray-800'
                          }`}>
                            {res.status === 'pending' ? t('admin.practical.pending') :
                             res.status === 'approved' ? t('admin.practical.approved') :
                             res.result === 'pass' ? t('admin.practical.pass') :
                             res.result === 'fail' ? t('admin.practical.fail') :
                             res.status === 'rejected' ? t('admin.practical.rejected') : res.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-700">{getLocalCourseName(res)}</p>
                        <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                          <div>{t('admin.practical.pref1')}: {res.preferredDate1 || '-'}</div>
                          {res.preferredDate2 && <div>{t('admin.practical.pref2')}: {res.preferredDate2}</div>}
                          {res.preferredDate3 && <div>{t('admin.practical.pref3')}: {res.preferredDate3}</div>}
                          {res.message && <div className="mt-1 text-gray-600">{t('admin.practical.message')}: {res.message}</div>}
                          {res.confirmedDate && <div className="text-blue-600 font-medium">{t('admin.practical.confirmedDate')}: {res.confirmedDate}</div>}
                          {res.venue && <div>{t('admin.practical.venue')}: {res.venue}</div>}
                          {res.adminNote && <div className="text-gray-600">{t('admin.practical.adminNote')}: {res.adminNote}</div>}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 ml-3">
                        {res.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 text-xs"
                              data-testid={`approve-practical-${res.id}`}
                              onClick={async () => {
                                const confirmedDate = prompt(t('admin.practical.enterConfirmedDate'), res.preferredDate1);
                                if (!confirmedDate) return;
                                const venue = prompt(t('admin.practical.enterVenue'), '');
                                try {
                                  await axios.put(`${API}/admin/practical-reservations/${res.id}`, {
                                    status: 'approved', confirmedDate, venue: venue || ''
                                  }, { headers: getAuthHeaders() });
                                  toast.success(t('admin.practical.approveSuccess'));
                                  fetchData();
                                } catch (e) { toast.error(t('admin.common.updateFailed')); }
                              }}
                            >{t('admin.practical.approve')}</Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 text-xs"
                              onClick={async () => {
                                const note = prompt(t('admin.practical.rejectReason'), '');
                                try {
                                  await axios.put(`${API}/admin/practical-reservations/${res.id}`, {
                                    status: 'rejected', adminNote: note || ''
                                  }, { headers: getAuthHeaders() });
                                  toast.success(t('admin.practical.rejectSuccess'));
                                  fetchData();
                                } catch (e) { toast.error(t('admin.common.updateFailed')); }
                              }}
                            >{t('admin.practical.reject')}</Button>
                          </>
                        )}
                        {res.status === 'approved' && !res.result && (
                          <>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-xs"
                              data-testid={`pass-practical-${res.id}`}
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/admin/practical-reservations/${res.id}/result`, {
                                    result: 'pass'
                                  }, { headers: getAuthHeaders() });
                                  toast.success(t('admin.practical.passSuccess'));
                                  fetchData();
                                } catch (e) { toast.error(t('admin.common.updateFailed')); }
                              }}
                            >{t('admin.practical.pass')}</Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 text-xs"
                              onClick={async () => {
                                const note = prompt(t('admin.practical.failReason'), '');
                                try {
                                  await axios.put(`${API}/admin/practical-reservations/${res.id}/result`, {
                                    result: 'fail', adminNote: note || ''
                                  }, { headers: getAuthHeaders() });
                                  toast.success(t('admin.practical.failSuccess'));
                                  fetchData();
                                } catch (e) { toast.error(t('admin.common.updateFailed')); }
                              }}
                            >{t('admin.practical.fail')}</Button>
                          </>
                        )}
                        {res.status === 'completed' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 text-xs"
                            onClick={async () => {
                              if (!window.confirm(t('admin.practical.deleteConfirm'))) return;
                              try {
                                await axios.delete(`${API}/admin/practical-reservations/${res.id}`, { headers: getAuthHeaders() });
                                toast.success(t('admin.common.deleted'));
                                fetchData();
                              } catch (e) { toast.error(t('admin.common.deleteFailed')); }
                            }}
                          >{t('admin.common.delete')}</Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <ListPager pager={practicalPager} testId="practical" />
          </TabsContent>

          {/* AI Monitoring Tab */}
          <TabsContent value="monitoring" className="p-4" data-testid="monitoring-tab-content">
            <MonitoringTab 
              monitoringSummary={monitoringSummary}
              setMonitoringSummary={setMonitoringSummary}
              API={API}
              getAuthHeaders={getAuthHeaders}
              formatDate={formatDate}
            />
          </TabsContent>

          <SettingsTab
            settings={settings}
            setSettings={setSettings}
            editMode={editMode}
            setEditMode={setEditMode}
            uploadingSeal={uploadingSeal}
            sealFileRef={sealFileRef}
            updateSettings={updateSettings}
            uploadSealImage={uploadSealImage}
            deleteSealImage={deleteSealImage}
            getImageUrl={getImageUrl}
            API={API}
          />
          <UsersTab
            users={users}
            showAddUserModal={showAddUserModal}
            setShowAddUserModal={setShowAddUserModal}
            newUserData={newUserData}
            setNewUserData={setNewUserData}
            addingUser={addingUser}
            handleAddUser={handleAddUser}
            formatDateShort={formatDateShort}
            getImageUrl={getImageUrl}
            getAuthHeaders={getAuthHeaders}
            fetchData={fetchData}
            setSelectedItem={setSelectedItem}
            setDialogType={setDialogType}
            setDialogOpen={setDialogOpen}
            onOpenDuplicates={() => setDuplicateUsersOpen(true)}
          />

          {/* Invoices Tab */}
          <InvoicesTab
            invoices={invoices}
            trainingGuides={trainingGuides}
            formatDateShort={formatDateShort}
            getAuthHeaders={getAuthHeaders}
            fetchData={fetchData}
            setSelectedInvoice={setSelectedInvoice}
            setInvoiceDetailOpen={setInvoiceDetailOpen}
            setPaymentConfirmData={setPaymentConfirmData}
            setPaymentConfirmOpen={setPaymentConfirmOpen}
            setEditingInvoice={setEditingInvoice}
            setInvoiceFormData={setInvoiceFormData}
            setInvoiceDialogOpen={setInvoiceDialogOpen}
            guideTemplates={guideTemplates}
            schedules={schedules}
            setGuideCreateForm={setGuideCreateForm}
            setGuideCreateDialogOpen={setGuideCreateDialogOpen}
            courses={courses}
            language={language}
          />

          {/* Training Guide Management Tab */}
          <GuidesTab
            trainingGuides={trainingGuides}
            formatDateShort={formatDateShort}
            getAuthHeaders={getAuthHeaders}
            fetchData={fetchData}
            onEditGuide={(g) => {
              setGuideCreateForm({
                id: g.id,
                applicationId: g.applicationId || '',
                templateId: g.templateId || '',
                courseName: g.courseName || '',
                schedule: g.schedule || '',
                userName: g.userName || '',
                courseOption: g.courseOption || getAppCourseOption(g.applicationId),
                courseStartDate: g.courseStartDate || '',
                userEmail: g.userEmail || '',
                venue: g.venue || '',
                venueAddress: g.venueAddress || '',
                meetingTime: g.meetingTime || '',
                itemsToBring: g.itemsToBring || '',
                notes: g.notes || '',
                mapUrl: g.mapUrl || '',
                additionalInfo: g.additionalInfo || ''
              });
              setGuideCreateDialogOpen(true);
            }}
            t={t}
            courses={courses}
            applications={applications}
            language={language}
          />

          <GuidePatternsTab
            guideTemplates={guideTemplates}
            settings={settings}
            courses={courses}
            setEditingTemplate={setEditingTemplate}
            setGuideTemplateForm={setGuideTemplateForm}
            setGuideTemplateDialogOpen={setGuideTemplateDialogOpen}
            getAuthHeaders={getAuthHeaders}
            fetchData={fetchData}
            t={t}
            language={language}
          />

          {/* Recruitment Tab */}
          <TabsContent value="recruitment" className="p-4">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">{t('admin.recruitment.title')}</h2>
                <Button className="bg-[#00A651] hover:bg-[#008a43]" onClick={() => { setEditingRecruitment(null); setRecruitmentForm({ title: '', location: '', salary: '', workHours: '', requirements: '', description: '', isPublished: true }); setRecruitmentFormOpen(true); }} data-testid="add-recruitment-btn">
                  <Plus className="h-4 w-4 mr-2" />{t('admin.recruitment.addNew')}
                </Button>
              </div>
              {/* Form */}
              {(recruitmentFormOpen || editingRecruitment !== null) && (
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <h3 className="font-semibold mb-3">{editingRecruitment ? t('admin.settingsTab.edit') : t('admin.recruitment.addNew')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><label className="text-sm font-medium">{t('admin.recruitment.jobTitle')} *</label><input className="w-full border rounded px-3 py-2 mt-1 text-sm" value={recruitmentForm.title} onChange={e => setRecruitmentForm({...recruitmentForm, title: e.target.value})} /></div>
                    <div><label className="text-sm font-medium">{t('admin.recruitment.location')}</label><input className="w-full border rounded px-3 py-2 mt-1 text-sm" value={recruitmentForm.location} onChange={e => setRecruitmentForm({...recruitmentForm, location: e.target.value})} /></div>
                    <div><label className="text-sm font-medium">{t('admin.recruitment.salary')}</label><input className="w-full border rounded px-3 py-2 mt-1 text-sm" value={recruitmentForm.salary} onChange={e => setRecruitmentForm({...recruitmentForm, salary: e.target.value})} /></div>
                    <div><label className="text-sm font-medium">{t('admin.recruitment.workHours')}</label><input className="w-full border rounded px-3 py-2 mt-1 text-sm" value={recruitmentForm.workHours} onChange={e => setRecruitmentForm({...recruitmentForm, workHours: e.target.value})} /></div>
                    <div className="md:col-span-2"><label className="text-sm font-medium">{t('admin.recruitment.requirements')}</label><textarea className="w-full border rounded px-3 py-2 mt-1 text-sm" rows={2} value={recruitmentForm.requirements} onChange={e => setRecruitmentForm({...recruitmentForm, requirements: e.target.value})} /></div>
                    <div className="md:col-span-2"><label className="text-sm font-medium">{t('admin.recruitment.description')}</label><textarea className="w-full border rounded px-3 py-2 mt-1 text-sm" rows={3} value={recruitmentForm.description} onChange={e => setRecruitmentForm({...recruitmentForm, description: e.target.value})} /></div>
                    <div><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={recruitmentForm.isPublished} onChange={e => setRecruitmentForm({...recruitmentForm, isPublished: e.target.checked})} />{t('admin.recruitment.published')}</label></div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button className="bg-[#00A651] hover:bg-[#008a43]" onClick={async () => {
                      try {
                        const headers = getAuthHeaders();
                        if (editingRecruitment) {
                          await axios.put(`${API}/admin/recruitments/${editingRecruitment}`, recruitmentForm, { headers });
                        } else {
                          await axios.post(`${API}/admin/recruitments`, recruitmentForm, { headers });
                        }
                        setEditingRecruitment(null); setRecruitmentFormOpen(false); setRecruitmentForm({ title: '', location: '', salary: '', workHours: '', requirements: '', description: '', isPublished: true }); fetchData();
                        toast.success(t('admin.common.updated'));
                      } catch { toast.error('エラーが発生しました'); }
                    }}>{t('admin.common.save')}</Button>
                    <Button variant="outline" onClick={() => { setEditingRecruitment(null); setRecruitmentFormOpen(false); setRecruitmentForm({ title: '', location: '', salary: '', workHours: '', requirements: '', description: '', isPublished: true }); }}>{t('admin.common.cancel')}</Button>
                  </div>
                </div>
              )}
              {/* List */}
              <div className="space-y-3">
                {recruitmentPager.paged.map(r => (
                  <div key={r.id} className="bg-white border rounded-lg p-4 flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{r.title}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded ${r.isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{r.isPublished ? t('admin.recruitment.published') : t('admin.recruitment.unpublished')}</span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">{[r.location, r.salary, r.workHours].filter(Boolean).join(' / ')}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => { setEditingRecruitment(r.id); setRecruitmentFormOpen(true); setRecruitmentForm({ title: r.title, location: r.location || '', salary: r.salary || '', workHours: r.workHours || '', requirements: r.requirements || '', description: r.description || '', isPublished: r.isPublished }); }}>{t('admin.settingsTab.edit')}</Button>
                      <Button size="sm" variant="destructive" onClick={async () => { if (!window.confirm(t('admin.common.delete') + '?')) return; try { await axios.delete(`${API}/admin/recruitments/${r.id}`, { headers: getAuthHeaders() }); fetchData(); toast.success(t('admin.common.deleted')); } catch { toast.error(t('admin.common.updateFailed')); } }}>{t('admin.common.delete')}</Button>
                    </div>
                  </div>
                ))}
                {recruitments.length === 0 && <p className="text-center text-gray-400 py-8">{t('admin.recruitment.noData')}</p>}
              </div>
              <ListPager pager={recruitmentPager} testId="recruitments" />
            </div>
          </TabsContent>

          {/* Membership Tab */}
          <TabsContent value="membership" className="p-4">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">{t('admin.membership.title')}</h2>
                <Button className="bg-[#00A651] hover:bg-[#008a43]" onClick={() => { setEditingMembership(null); setMembershipForm({ name: '', conditions: '', fee: '', benefits: '', description: '', isPublished: true }); }} data-testid="add-membership-btn">
                  <Plus className="h-4 w-4 mr-2" />{t('admin.membership.addNew')}
                </Button>
              </div>
              {/* Form */}
              {(editingMembership !== null || membershipForm.name !== '') && (
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <h3 className="font-semibold mb-3">{editingMembership ? t('admin.settingsTab.edit') : t('admin.membership.addNew')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><label className="text-sm font-medium">{t('admin.membership.typeName')} *</label><input className="w-full border rounded px-3 py-2 mt-1 text-sm" value={membershipForm.name} onChange={e => setMembershipForm({...membershipForm, name: e.target.value})} /></div>
                    <div><label className="text-sm font-medium">{t('admin.membership.fee')}</label><input className="w-full border rounded px-3 py-2 mt-1 text-sm" value={membershipForm.fee} onChange={e => setMembershipForm({...membershipForm, fee: e.target.value})} placeholder="例: 年額10,000円" /></div>
                    <div className="md:col-span-2"><label className="text-sm font-medium">{t('admin.membership.conditions')}</label><textarea className="w-full border rounded px-3 py-2 mt-1 text-sm" rows={2} value={membershipForm.conditions} onChange={e => setMembershipForm({...membershipForm, conditions: e.target.value})} /></div>
                    <div className="md:col-span-2"><label className="text-sm font-medium">{t('admin.membership.benefits')}</label><textarea className="w-full border rounded px-3 py-2 mt-1 text-sm" rows={2} value={membershipForm.benefits} onChange={e => setMembershipForm({...membershipForm, benefits: e.target.value})} /></div>
                    <div className="md:col-span-2"><label className="text-sm font-medium">{t('admin.membership.description')}</label><textarea className="w-full border rounded px-3 py-2 mt-1 text-sm" rows={3} value={membershipForm.description} onChange={e => setMembershipForm({...membershipForm, description: e.target.value})} /></div>
                    <div><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={membershipForm.isPublished} onChange={e => setMembershipForm({...membershipForm, isPublished: e.target.checked})} />{t('admin.membership.published')}</label></div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button className="bg-[#00A651] hover:bg-[#008a43]" onClick={async () => {
                      try {
                        const headers = getAuthHeaders();
                        if (editingMembership) {
                          await axios.put(`${API}/admin/membership-types/${editingMembership}`, membershipForm, { headers });
                        } else {
                          await axios.post(`${API}/admin/membership-types`, membershipForm, { headers });
                        }
                        setEditingMembership(null); setMembershipForm({ name: '', conditions: '', fee: '', benefits: '', description: '', isPublished: true }); fetchData();
                        toast.success(t('admin.common.updated'));
                      } catch { toast.error('エラーが発生しました'); }
                    }}>{t('admin.common.save')}</Button>
                    <Button variant="outline" onClick={() => { setEditingMembership(null); setMembershipForm({ name: '', conditions: '', fee: '', benefits: '', description: '', isPublished: true }); }}>{t('admin.common.cancel')}</Button>
                  </div>
                </div>
              )}
              {/* List */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {membershipPager.paged.map(m => (
                  <div key={m.id} className="bg-white border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold">{m.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded ${m.isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{m.isPublished ? t('admin.membership.published') : t('admin.membership.unpublished')}</span>
                    </div>
                    {m.fee && <p className="text-lg font-bold text-[#00A651] mb-2">{m.fee}</p>}
                    {m.conditions && <p className="text-sm text-gray-600 mb-1"><strong>{t('admin.membership.conditions')}:</strong> {m.conditions}</p>}
                    {m.benefits && <p className="text-sm text-gray-600 mb-2"><strong>{t('admin.membership.benefits')}:</strong> {m.benefits}</p>}
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => { setEditingMembership(m.id); setMembershipForm({ name: m.name, conditions: m.conditions || '', fee: m.fee || '', benefits: m.benefits || '', description: m.description || '', isPublished: m.isPublished }); }}>{t('admin.settingsTab.edit')}</Button>
                      <Button size="sm" variant="destructive" onClick={async () => { if (!window.confirm(t('admin.common.delete') + '?')) return; try { await axios.delete(`${API}/admin/membership-types/${m.id}`, { headers: getAuthHeaders() }); fetchData(); toast.success(t('admin.common.deleted')); } catch { toast.error(t('admin.common.updateFailed')); } }}>{t('admin.common.delete')}</Button>
                    </div>
                  </div>
                ))}
              </div>
              {membershipTypes.length === 0 && <p className="text-center text-gray-400 py-8">{t('admin.membership.noData')}</p>}
              <ListPager pager={membershipPager} testId="memberships" />
            </div>
          </TabsContent>

          {/* Business Reports Tab */}
          <TabsContent value="reports" className="p-4">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">{t('admin.reports.title')}</h2>
              </div>
              {/* Add / Edit Form */}
              <div className="bg-gray-50 rounded-lg p-4 border">
                <h3 className="font-semibold mb-3">{editingReportId ? '編集' : t('admin.reports.addNew')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div><label className="text-sm font-medium">{t('admin.reports.fiscalYear')}</label><input className="w-full border rounded px-3 py-2 mt-1 text-sm" data-testid="report-fiscal-year" value={reportForm.fiscalYear} onChange={e => setReportForm({...reportForm, fiscalYear: e.target.value})} placeholder="例: 2025" /></div>
                  <div><label className="text-sm font-medium">{t('admin.reports.reportTitle')}</label><input className="w-full border rounded px-3 py-2 mt-1 text-sm" data-testid="report-title-input" value={reportForm.title} onChange={e => setReportForm({...reportForm, title: e.target.value})} /></div>
                  <div><label className="text-sm font-medium">{t('admin.reports.reportType')}</label><select className="w-full border rounded px-3 py-2 mt-1 text-sm" data-testid="report-type-select" value={reportForm.reportType} onChange={e => setReportForm({...reportForm, reportType: e.target.value})}><option value="business">{t('admin.reports.business')}</option><option value="financial">{t('admin.reports.financial')}</option></select></div>
                  {!editingReportId && (
                    <div><label className="text-sm font-medium">{t('admin.reports.pdfFile')}</label><input type="file" accept=".pdf" className="w-full border rounded px-3 py-2 mt-1 text-sm" data-testid="report-file-input" onChange={async (e) => {
                      const file = e.target.files[0]; if (!file) return;
                      try {
                        const formData = new FormData(); formData.append('file', file);
                        const headers = getAuthHeaders(); headers['Content-Type'] = 'multipart/form-data';
                        const res = await axios.post(`${API}/admin/business-reports`, formData, { headers });
                        if (res.data?.id) {
                          await axios.put(`${API}/admin/business-reports/${res.data.id}`, { ...reportForm, fileUrl: res.data.fileUrl }, { headers: getAuthHeaders() });
                          setReportForm({ fiscalYear: '', title: '', reportType: 'business', isPublished: true }); fetchData();
                          toast.success(t('admin.certificates.uploadSuccess'));
                        }
                      } catch { toast.error(t('admin.common.uploadFailed')); }
                    }} /></div>
                  )}
                  {editingReportId && (
                    <div className="flex items-end gap-2">
                      <Button data-testid="report-save-edit-btn" onClick={async () => {
                        try {
                          await axios.put(`${API}/admin/business-reports/${editingReportId}`, {
                            fiscalYear: reportForm.fiscalYear,
                            title: reportForm.title,
                            reportType: reportForm.reportType,
                            isPublished: reportForm.isPublished,
                          }, { headers: getAuthHeaders() });
                          setEditingReportId(null);
                          setReportForm({ fiscalYear: '', title: '', reportType: 'business', isPublished: true });
                          fetchData();
                          toast.success(t('admin.common.updated'));
                        } catch { toast.error(t('admin.common.updateFailed')); }
                      }}>{t('admin.common.save')}</Button>
                      <Button variant="outline" data-testid="report-cancel-edit-btn" onClick={() => {
                        setEditingReportId(null);
                        setReportForm({ fiscalYear: '', title: '', reportType: 'business', isPublished: true });
                      }}>{t('admin.common.cancel')}</Button>
                    </div>
                  )}
                </div>
              </div>
              {/* List */}
              <div className="space-y-3">
                {reportPager.paged.map(r => (
                  <div key={r.id} className="bg-white border rounded-lg p-4 flex justify-between items-center" data-testid={`report-item-${r.id}`}>
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-[#00A651]" />
                      <div>
                        <p className="font-medium">{r.title || r.fileUrl?.split('/').pop()}</p>
                        <div className="text-xs text-gray-500">{r.fiscalYear} / {r.reportType === 'business' ? t('admin.reports.business') : t('admin.reports.financial')} / <span className={r.isPublished ? 'text-green-600' : 'text-gray-400'}>{r.isPublished ? t('admin.reports.published') : t('admin.reports.unpublished')}</span></div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {r.fileUrl && <a href={r.fileUrl} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline">{t('admin.reports.download')}</Button></a>}
                      <Button size="sm" variant="outline" data-testid={`report-edit-btn-${r.id}`} onClick={() => {
                        setEditingReportId(r.id);
                        setReportForm({ fiscalYear: r.fiscalYear || '', title: r.title || '', reportType: r.reportType || 'business', isPublished: r.isPublished !== false });
                      }}>{t('admin.common.edit')}</Button>
                      <Button size="sm" variant="outline" onClick={async () => { try { await axios.put(`${API}/admin/business-reports/${r.id}`, { isPublished: !r.isPublished }, { headers: getAuthHeaders() }); fetchData(); } catch {} }}>{r.isPublished ? t('admin.reports.unpublished') : t('admin.reports.published')}</Button>
                      <Button size="sm" variant="destructive" onClick={async () => { if (!window.confirm(t('admin.common.delete') + '?')) return; try { await axios.delete(`${API}/admin/business-reports/${r.id}`, { headers: getAuthHeaders() }); fetchData(); toast.success(t('admin.common.deleted')); } catch { toast.error(t('admin.common.updateFailed')); } }}>{t('admin.common.delete')}</Button>
                    </div>
                  </div>
                ))}
                {businessReports.length === 0 && <p className="text-center text-gray-400 py-8">{t('admin.reports.noData')}</p>}
              </div>
              <ListPager pager={reportPager} testId="reports" />
            </div>
          </TabsContent>

          <NewsTab authHeaders={getAuthHeaders} />

        </Tabs>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {dialogType === 'viewApplication' && t('admin.dialogs.viewApplication')}
              {dialogType === 'editApplication' && t('admin.dialogs.editApplication')}
              {dialogType === 'viewContact' && t('admin.dialogs.viewContact')}
              {dialogType === 'editContact' && t('admin.dialogs.editContact')}
              {dialogType === 'newSchedule' && t('admin.dialogs.newSchedule')}
              {dialogType === 'editSchedule' && t('admin.dialogs.editSchedule')}
              {dialogType === 'newCourse' && t('admin.dialogs.newCourse')}
              {dialogType === 'editCourse' && t('admin.dialogs.editCourse')}
              {dialogType === 'viewUser' && t('admin.dialogs.viewUser')}
            </DialogTitle>
          </DialogHeader>

          {/* View/Edit Application */}
          {(dialogType === 'viewApplication' || dialogType === 'editApplication') && selectedItem && (
            <div className="space-y-1">
              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <Label>{t('admin.applications.applicant')}</Label>
                  <Input value={selectedItem.name || (dialogType === 'editApplication' ? '' : '')} disabled={!(dialogType === 'editApplication')} onChange={(e) => setSelectedItem({ ...selectedItem, name: e.target.value })} className="mt-0.5" data-testid="app-edit-name" />
                </div>
                <div>
                  <Label>{t('admin.form.nameKana')}</Label>
                  <Input value={selectedItem.nameKana || (dialogType === 'editApplication' ? '' : '')} disabled={!(dialogType === 'editApplication')} onChange={(e) => setSelectedItem({ ...selectedItem, nameKana: e.target.value })} className="mt-0.5" data-testid="app-edit-nameKana" />
                </div>
                <div>
                  <Label>{t('admin.form.email')}</Label>
                  <Input value={selectedItem.email || (dialogType === 'editApplication' ? '' : '')} disabled={!(dialogType === 'editApplication')} onChange={(e) => setSelectedItem({ ...selectedItem, email: e.target.value })} className="mt-0.5" data-testid="app-edit-email" />
                </div>
                <div>
                  <Label>{t('admin.form.phone')}</Label>
                  <Input value={selectedItem.phone || (dialogType === 'editApplication' ? '' : '')} disabled={!(dialogType === 'editApplication')} onChange={(e) => setSelectedItem({ ...selectedItem, phone: e.target.value })} className="mt-0.5" data-testid="app-edit-phone" />
                </div>
                <div className="col-span-2">
                  <Label>{t('admin.form.address')}</Label>
                  <Input value={selectedItem.address || (dialogType === 'editApplication' ? '' : '-')} disabled={!(dialogType === 'editApplication')} onChange={(e) => setSelectedItem({ ...selectedItem, address: e.target.value })} className="mt-0.5" data-testid="app-edit-address" />
                </div>
                <div>
                  <Label>{t('admin.form.company')}</Label>
                  <Input value={selectedItem.company || (dialogType === 'editApplication' ? '' : '-')} disabled={!(dialogType === 'editApplication')} onChange={(e) => setSelectedItem({ ...selectedItem, company: e.target.value })} className="mt-0.5" data-testid="app-edit-company" />
                </div>
                <div>
                  <Label>{t('mypage.profile.nationality')}</Label>
                  {dialogType === 'editApplication' ? (
                    <div className="mt-0.5">
                      <NationalitySelect
                        value={selectedItem.nationality || ''}
                        language={language}
                        dataTestId="edit-application-nationality"
                        onChange={(val) => {
                          const langMap = {
                            'Japan': 'ja', '日本': 'ja',
                            'Brazil': 'pt', 'ブラジル': 'pt', 'Portugal': 'pt',
                            'United States': 'en', 'アメリカ': 'en', 'United Kingdom': 'en', 'Australia': 'en', 'Canada': 'en', 'Philippines': 'tl', 'フィリピン': 'tl',
                            'Vietnam': 'vi', 'ベトナム': 'vi',
                            'Indonesia': 'id', 'インドネシア': 'id',
                            'Spain': 'es', 'スペイン': 'es', 'Mexico': 'es', 'メキシコ': 'es', 'Peru': 'es', 'ペルー': 'es', 'Bolivia': 'es', 'ボリビア': 'es', 'Colombia': 'es', 'Argentina': 'es',
                            'China': 'zh', '中国': 'zh', 'Korea': 'ja', '韓国': 'ja',
                          };
                          const autoLang = langMap[val] || selectedItem.language || '';
                          setSelectedItem({...selectedItem, nationality: val, language: autoLang});
                        }}
                      />
                    </div>
                  ) : (
                    <Input value={selectedItem.nationality || '-'} disabled className="mt-0.5" />
                  )}
                </div>
                <div>
                  <Label>{t('admin.form.language')}</Label>
                  {dialogType === 'editApplication' ? (
                    <select
                      value={selectedItem.language || ''}
                      onChange={(e) => setSelectedItem({...selectedItem, language: e.target.value})}
                      className="mt-0.5 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                    >
                      <option value="">-</option>
                      {Object.entries(LANG_LABELS_JA).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
                    </select>
                  ) : (
                    <Input value={LANG_LABELS_JA[selectedItem.language] || selectedItem.language || '-'} disabled className="mt-0.5" />
                  )}
                </div>
                <div>
                  <Label>{t('admin.applications.course')}</Label>
                  {dialogType === 'editApplication' ? (
                    <select
                      value={selectedItem.courseId || ''}
                      onChange={(e) => { const c = courses.find(x => x.id === e.target.value); setSelectedItem({ ...selectedItem, courseId: e.target.value, courseName: c?.name?.ja || selectedItem.courseName, preferredDate: '', courseOption: '' }); }}
                      className="mt-0.5 w-full border rounded-md px-2 py-2 text-sm bg-white"
                      data-testid="app-edit-course"
                    >
                      {!courses.some(c => c.id === selectedItem.courseId) && <option value={selectedItem.courseId || ''}>{getLocalCourseName(selectedItem)}</option>}
                      {courses.map(c => <option key={c.id} value={c.id}>{c.name?.ja || c.id}</option>)}
                    </select>
                  ) : (
                    <Input value={getLocalCourseName(selectedItem)} disabled className="mt-0.5" />
                  )}
                </div>
                <div>
                  <Label>{t('admin.form.preferredDate')}</Label>
                  {dialogType === 'editApplication' ? (
                    <div className="flex gap-1 mt-0.5">
                      <select
                        value={schedules.some(sc => sc.courseId === selectedItem.courseId && sc.startDate === selectedItem.preferredDate) ? selectedItem.preferredDate : ''}
                        onChange={(e) => e.target.value && setSelectedItem({ ...selectedItem, preferredDate: e.target.value })}
                        className="flex-1 min-w-0 border rounded-md px-2 py-2 text-sm bg-white"
                        data-testid="app-edit-schedule"
                      >
                        <option value="">日程から選択</option>
                        {schedules.filter(sc => sc.courseId === selectedItem.courseId && sc.startDate).sort((a, b) => String(a.startDate).localeCompare(String(b.startDate))).map(sc => (
                          <option key={sc.id} value={sc.startDate}>{sc.startDate}{sc.endDate ? ` 〜 ${sc.endDate}` : ''}</option>
                        ))}
                      </select>
                      <Input type="date" value={selectedItem.preferredDate || ''} onChange={(e) => setSelectedItem({ ...selectedItem, preferredDate: e.target.value })} className="w-[150px]" data-testid="app-edit-preferred-date" />
                    </div>
                  ) : (
                    <Input value={selectedItem.preferredDate ? formatDateShort(selectedItem.preferredDate) : '-'} disabled className="mt-0.5" />
                  )}
                </div>
                {dialogType === 'editApplication' && (courses.find(c => c.id === selectedItem.courseId)?.courseOptions || []).some(o => o.enabled) && (
                  <div>
                    <Label>コース</Label>
                    <select
                      value={selectedItem.courseOption || ''}
                      onChange={(e) => setSelectedItem({ ...selectedItem, courseOption: e.target.value })}
                      className="mt-0.5 w-full border rounded-md px-2 py-2 text-sm bg-white"
                      data-testid="app-edit-course-option"
                    >
                      <option value="">指定なし</option>
                      {(courses.find(c => c.id === selectedItem.courseId)?.courseOptions || []).filter(o => o.enabled).map(o => <option key={o.code} value={o.code}>{o.code}コース</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <Label>{t('admin.common.status')}</Label>
                  {dialogType === 'editApplication' ? (
                    <Select 
                      value={selectedItem.status} 
                      onValueChange={(v) => setSelectedItem({...selectedItem, status: v})}
                    >
                      <SelectTrigger className={`mt-0.5 font-semibold text-sm ${
                        selectedItem.status === 'pending' ? 'bg-yellow-100 border-yellow-400 text-yellow-800' :
                        selectedItem.status === 'contacted' ? 'bg-blue-100 border-blue-400 text-blue-800' :
                        selectedItem.status === 'confirmed' ? 'bg-green-100 border-green-400 text-green-800' :
                        selectedItem.status === 'completed_passed' ? 'bg-emerald-100 border-emerald-500 text-emerald-800' :
                        selectedItem.status === 'completed_failed' ? 'bg-red-100 border-red-400 text-red-800' :
                        selectedItem.status === 'cancelled' ? 'bg-gray-200 border-gray-400 text-gray-600' :
                        ''
                      }`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">{t('admin.applications.pending')}</SelectItem>
                        <SelectItem value="contacted">{t('admin.applications.contacted')}</SelectItem>
                        <SelectItem value="confirmed">{t('admin.applications.confirmed')}</SelectItem>
                        <SelectItem value="completed_passed">{t('admin.applications.completedPassed')}</SelectItem>
                        <SelectItem value="completed_failed">{t('admin.applications.completedFailed')}</SelectItem>
                        <SelectItem value="cancelled">{t('admin.common.cancel')}</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={
                      selectedItem.status === 'pending' ? t('admin.applications.pending') :
                      selectedItem.status === 'confirmed' ? t('admin.applications.confirmed') :
                      selectedItem.status === 'completed_passed' ? t('admin.applications.completedPassed') :
                      selectedItem.status === 'completed_failed' ? t('admin.applications.completedFailed') :
                      selectedItem.status === 'cancelled' ? t('admin.common.cancelled') : selectedItem.status
                    } disabled className="mt-0.5" />
                  )}
                </div>
              </div>
              {/* {t('admin.applications.invoiceInfo')} */}
              {(() => {
                const relatedInvoices = invoices.filter(inv => inv.applicationId === selectedItem.id);
                if (relatedInvoices.length === 0) return null;
                return (
                  <div>
                    <Label className="text-sm font-medium">{t('admin.applications.invoiceStatus')}</Label>
                    <div className="space-y-2 mt-1">
                      {relatedInvoices.map(inv => (
                        <div key={inv.id} className="flex items-center justify-between border rounded-lg p-3 text-sm bg-gray-50">
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-xs text-gray-500">{inv.invoiceNumber}</span>
                            <span className="font-medium">¥{(inv.totalAmount || 0).toLocaleString()}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {inv.status === 'paid' && inv.paidAt && (
                              <span className="text-xs text-gray-500">{formatDateShort(inv.paidAt)}</span>
                            )}
                            <Badge className={
                              inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                              inv.status === 'cancelled' ? 'bg-gray-100 text-gray-500' :
                              'bg-orange-100 text-orange-700'
                            }>
                              {inv.status === 'paid' ? t('admin.invoices.paid') : inv.status === 'cancelled' ? t('admin.common.cancelled') : t('admin.invoices.unpaid')}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {/* {t('admin.applications.participantInfo')} */}
              {dialogType === 'editApplication' && (
                <ParticipantsEditor
                  participants={selectedItem.participants || []}
                  onChange={(list) => setSelectedItem({ ...selectedItem, participants: list })}
                  defaultLanguage={selectedItem.language || 'ja'}
                />
              )}
              {dialogType !== 'editApplication' && selectedItem.participants && selectedItem.participants.length > 0 && (
                <div>
                  <Label className="text-sm font-medium">{t('admin.applications.participantInfo')}（{selectedItem.participants.length}{t('admin.common.people')})</Label>
                  <div className="space-y-3 mt-2">
                    {selectedItem.participants.map((p, idx) => (
                      <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium text-[#00A651] bg-green-100 px-2 py-0.5 rounded-full">{t('admin.applications.participant')} {idx + 1}</span>
                          <span className="text-sm font-medium">{p.name || '-'}</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-gray-600 mb-2">
                          {p.birthDate && <div><span className="text-gray-400">{t('admin.form.birthDateLabel')}</span> {p.birthDate}</div>}
                          {p.gender && <div><span className="text-gray-400">{t('admin.form.genderLabel')}</span> {p.gender === 'male' ? t('admin.form.male') : t('admin.form.female')}</div>}
                          {p.nationality && <div><span className="text-gray-400">{t('admin.form.nationalityLabel')}</span> {p.nationality}</div>}
                          {p.postalCode && <div><span className="text-gray-400">{t('admin.form.postalCodeLabel')}</span> {p.postalCode}</div>}
                          {p.address && <div className="col-span-2"><span className="text-gray-400">{t('admin.form.addressLabel')}</span> {p.address}</div>}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {p.idDocumentFront && (
                            <div>
                              <span className="text-xs text-gray-500">{t('admin.users.idDocuments')} (表)</span>
                              <a href={getImageUrl(p.idDocumentFront)} target="_blank" rel="noreferrer" className="block mt-1">
                                <img src={getImageUrl(p.idDocumentFront)} alt="ID Front" className="w-full h-16 object-cover rounded border hover:opacity-80 transition-opacity" />
                              </a>
                            </div>
                          )}
                          {p.idDocumentBack && (
                            <div>
                              <span className="text-xs text-gray-500">{t('admin.users.idDocuments')} (裏)</span>
                              <a href={getImageUrl(p.idDocumentBack)} target="_blank" rel="noreferrer" className="block mt-1">
                                <img src={getImageUrl(p.idDocumentBack)} alt="ID Back" className="w-full h-16 object-cover rounded border hover:opacity-80 transition-opacity" />
                              </a>
                            </div>
                          )}
                          {p.idDocumentPhoto && (
                            <div>
                              <span className="text-xs text-gray-500">{t('admin.users.idDocuments')}</span>
                              <a href={getImageUrl(p.idDocumentPhoto)} target="_blank" rel="noreferrer" className="block mt-1">
                                <img src={getImageUrl(p.idDocumentPhoto)} alt="ID" className="w-full h-16 object-cover rounded border hover:opacity-80 transition-opacity" />
                              </a>
                            </div>
                          )}
                          {p.qualificationFront && (
                            <div>
                              <span className="text-xs text-gray-500">{t('admin.users.qualifications')} (表)</span>
                              <a href={getImageUrl(p.qualificationFront)} target="_blank" rel="noreferrer" className="block mt-1">
                                <img src={getImageUrl(p.qualificationFront)} alt="Qual Front" className="w-full h-16 object-cover rounded border hover:opacity-80 transition-opacity" />
                              </a>
                            </div>
                          )}
                          {p.qualificationBack && (
                            <div>
                              <span className="text-xs text-gray-500">{t('admin.users.qualifications')} (裏)</span>
                              <a href={getImageUrl(p.qualificationBack)} target="_blank" rel="noreferrer" className="block mt-1">
                                <img src={getImageUrl(p.qualificationBack)} alt="Qual Back" className="w-full h-16 object-cover rounded border hover:opacity-80 transition-opacity" />
                              </a>
                            </div>
                          )}
                          {p.qualificationPhoto && (
                            <div>
                              <span className="text-xs text-gray-500">{t('admin.users.qualifications')}</span>
                              <a href={getImageUrl(p.qualificationPhoto)} target="_blank" rel="noreferrer" className="block mt-1">
                                <img src={getImageUrl(p.qualificationPhoto)} alt="Qualification" className="w-full h-16 object-cover rounded border hover:opacity-80 transition-opacity" />
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(selectedItem.message || dialogType === 'editApplication') && (
                <div>
                  <Label>{t('admin.form.notes')}</Label>
                  <Textarea value={selectedItem.message || ''} disabled={dialogType !== 'editApplication'} onChange={(e) => setSelectedItem({ ...selectedItem, message: e.target.value })} rows={3} className="mt-1" data-testid="app-edit-message" />
                </div>
              )}
              {dialogType === 'editApplication' && (
                <>
                  {/* 登録ユーザーとの紐付け */}
                  <div className="bg-gray-50 border rounded-md p-3" data-testid="edit-app-user-link">
                    <Label className="text-xs font-semibold">登録ユーザーと紐付け</Label>
                    <select
                      className="w-full mt-1 border rounded-md p-2 text-sm bg-white"
                      value={selectedItem.userId || ''}
                      data-testid="edit-app-user-select"
                      onChange={(e) => setSelectedItem({ ...selectedItem, userId: e.target.value })}
                    >
                      <option value="">紐付けなし</option>
                      {[...users].filter(u => u.role !== 'admin')
                        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ja'))
                        .map(u => (
                          <option key={u.id} value={u.id}>
                            {u.name}{u.email ? `（${u.email}）` : ''}
                          </option>
                        ))}
                    </select>
                    <p className="text-[11px] text-gray-400 mt-1">
                      {selectedItem.userId
                        ? '保存すると、この受講者のマイページに申込が表示されます'
                        : '紐付けると、その受講者のマイページに申込が表示されます（任意）'}
                    </p>
                  </div>
                  {/* {t('admin.applications.completionInfo')} */}
                  {selectedItem.status === 'completed_passed' && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-medium text-green-800 mb-3">{t('admin.applications.completionInfo')}</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>{t('admin.applications.completionDate')}</Label>
                          <Input 
                            type="date"
                            value={selectedItem.completedAt?.split('T')[0] || ''}
                            onChange={(e) => setSelectedItem({...selectedItem, completedAt: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>{t('admin.certificates.certNumber')}</Label>
                          <div className="flex gap-1 mt-1">
                            <div className="flex-shrink-0 bg-gray-100 border border-r-0 rounded-l-md px-3 py-2 text-sm text-gray-600">
                              {courses.find(c => c.id === selectedItem.courseId)?.registrationNumber || t('admin.common.notSet')}
                            </div>
                            <Input 
                              value={selectedItem.certificateNumberSuffix || ''}
                              onChange={(e) => {
                                const prefix = courses.find(c => c.id === selectedItem.courseId)?.registrationNumber || '';
                                const suffix = e.target.value;
                                setSelectedItem({
                                  ...selectedItem, 
                                  certificateNumberSuffix: suffix,
                                  certificateNumber: prefix ? `${prefix}-${suffix}` : suffix
                                });
                              }}
                              className="rounded-l-none"
                              placeholder={t('admin.certificates.enterSequence')}
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {t('admin.certificates.finalFormat')}: {selectedItem.certificateNumber || (courses.find(c => c.id === selectedItem.courseId)?.registrationNumber ? `${courses.find(c => c.id === selectedItem.courseId)?.registrationNumber}-` : '') + (selectedItem.certificateNumberSuffix || '???')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label>{t('admin.form.adminMemo')}</Label>
                    <Textarea 
                      value={selectedItem.notes || ''} 
                      onChange={(e) => setSelectedItem({...selectedItem, notes: e.target.value})}
                      rows={3} 
                      className="mt-1"
                      placeholder={t('admin.form.internalMemo')}
                    />
                  </div>
                </>
              )}
              {dialogType === 'editApplication' && (
                <DialogFooter className="flex-wrap gap-2">
                  <div className="flex-1" />
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('admin.common.cancel')}</Button>
                  <Button className="bg-[#00A651] hover:bg-[#008a43]" data-testid="app-edit-save" onClick={() => updateApplication(selectedItem.id, {
                    name: selectedItem.name || '',
                    nameKana: selectedItem.nameKana || '',
                    email: selectedItem.email || '',
                    phone: selectedItem.phone || '',
                    address: selectedItem.address || '',
                    company: selectedItem.company || '',
                    courseId: selectedItem.courseId,
                    courseName: selectedItem.courseName,
                    courseOption: selectedItem.courseOption || '',
                    preferredDate: selectedItem.preferredDate || '',
                    message: selectedItem.message || '',
                    participants: (selectedItem.participants || []).filter(p => (p?.name || '').trim()),
                    status: selectedItem.status, 
                    notes: selectedItem.notes,
                    completedAt: selectedItem.completedAt,
                    certificateNumber: selectedItem.certificateNumber,
                    nationality: selectedItem.nationality,
                    language: selectedItem.language,
                    userId: selectedItem.userId || ''
                  })}>
                    {t('admin.common.save')}
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}

          {/* View/Edit Contact */}
          {(dialogType === 'viewContact' || dialogType === 'editContact') && selectedItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('admin.common.name')}</Label>
                  <Input value={selectedItem.name || ''} disabled className="mt-1" />
                </div>
                <div>
                  <Label>{t('admin.form.email')}</Label>
                  <Input value={selectedItem.email || ''} disabled className="mt-1" />
                </div>
                <div>
                  <Label>{t('admin.form.phone')}</Label>
                  <Input value={selectedItem.phone || '-'} disabled className="mt-1" />
                </div>
                <div>
                  <Label>{t('admin.common.status')}</Label>
                  {dialogType === 'editContact' ? (
                    <Select 
                      value={selectedItem.status} 
                      onValueChange={(v) => setSelectedItem({...selectedItem, status: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unread">{t('admin.contacts.unread')}</SelectItem>
                        <SelectItem value="read">{t('admin.contacts.read')}</SelectItem>
                        <SelectItem value="replied">{t('admin.contacts.replied')}</SelectItem>
                        <SelectItem value="resolved">{t('admin.contacts.resolved')}</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={selectedItem.status === 'unread' ? t('admin.contacts.unread') : t('admin.contacts.read')} disabled className="mt-1" />
                  )}
                </div>
              </div>
              <div>
                <Label>{t('admin.contacts.subject')}</Label>
                <Input value={selectedItem.subject || ''} disabled className="mt-1" />
              </div>
              <div>
                <Label>{t('admin.contacts.message')}</Label>
                <Textarea value={selectedItem.message || ''} disabled rows={4} className="mt-1" />
              </div>
              {dialogType === 'editContact' && (
                <>
                  <div>
                    <Label>{t('admin.contacts.reply')}</Label>
                    <Textarea 
                      value={selectedItem.response || ''} 
                      onChange={(e) => setSelectedItem({...selectedItem, response: e.target.value})}
                      rows={3} 
                      className="mt-1"
                      placeholder={t('admin.contacts.replyPlaceholder')}
                      data-testid="contact-reply-textarea"
                    />
                  </div>
                  <div>
                    <Label>{t('admin.form.adminMemo')}</Label>
                    <Textarea 
                      value={selectedItem.notes || ''} 
                      onChange={(e) => setSelectedItem({...selectedItem, notes: e.target.value})}
                      rows={2} 
                      className="mt-1"
                      placeholder={t('admin.form.internalMemo')}
                    />
                  </div>
                  {selectedItem.repliedAt && (
                    <div className="text-xs text-gray-500">
                      返信済み: {new Date(selectedItem.repliedAt).toLocaleString('ja-JP')}
                    </div>
                  )}
                  <DialogFooter className="flex gap-2">
                    <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('admin.common.cancel')}</Button>
                    <Button 
                      variant="outline"
                      className="border-[#00A651] text-[#00A651] hover:bg-[#00A651] hover:text-white"
                      disabled={!selectedItem.response}
                      data-testid="contact-send-reply-btn"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/contacts/${selectedItem.id}/reply`, { response: selectedItem.response }, { headers: getAuthHeaders() });
                          toast.success(t('admin.contacts.updateSuccess'));
                          fetchData();
                          setDialogOpen(false);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || t('admin.common.saveFailed'));
                        }
                      }}
                    >
                      返信して送信
                    </Button>
                    <Button className="bg-[#00A651] hover:bg-[#008a43]" onClick={() => updateContact(selectedItem.id, { status: selectedItem.status, response: selectedItem.response, notes: selectedItem.notes })}>
                      {t('admin.common.save')}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </div>
          )}

          {/* New/Edit Schedule */}
          {(dialogType === 'newSchedule' || dialogType === 'editSchedule') && (
            <div className="space-y-4">
              <div>
                <Label>{t('admin.form.course')}</Label>
                <Select 
                  value={dialogType === 'editSchedule' ? selectedItem?.courseId : newSchedule.courseId}
                  onValueChange={(v) => {
                    const course = courses.find(c => c.id === v);
                    if (dialogType === 'editSchedule') {
                      setSelectedItem({...selectedItem, courseId: v, courseName: course?.name?.ja || ''});
                    } else {
                      setNewSchedule({...newSchedule, courseId: v, courseName: course?.name?.ja || ''});
                    }
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={t('admin.form.selectCourse')} />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map(course => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.name?.ja}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(() => {
                const isEdit = dialogType === 'editSchedule';
                const sForm = isEdit ? (selectedItem || {}) : newSchedule;
                const patch = (p) => isEdit ? setSelectedItem({ ...selectedItem, ...p }) : setNewSchedule({ ...newSchedule, ...p });
                const scheduleType = sForm.scheduleType || 'consecutive';
                const selDates = Array.isArray(sForm.selectedDates) ? sForm.selectedDates : [];
                const previewDates = scheduleType === 'selected_dates'
                  ? [...selDates].sort()
                  : enumerateDates((sForm.startDate || '').split('T')[0], (sForm.endDate || '').split('T')[0]);
                return (
                  <>
                    <div className="border rounded-lg p-3 bg-gray-50">
                      <Label className="text-sm font-semibold">日程登録方式</Label>
                      <div className="flex flex-col sm:flex-row gap-3 mt-2">
                        <label className={`flex-1 flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${scheduleType === 'consecutive' ? 'border-[#00A651] bg-[#e6f6ec]' : 'border-gray-200 bg-white'}`}>
                          <input type="radio" name="scheduleType" checked={scheduleType === 'consecutive'} onChange={() => patch({ scheduleType: 'consecutive' })} className="mt-1" data-testid="schedule-type-consecutive" />
                          <span className="text-sm"><span className="font-medium">連日</span><br /><span className="text-xs text-gray-500">開始日から終了日まで連続して登録します</span></span>
                        </label>
                        <label className={`flex-1 flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${scheduleType === 'selected_dates' ? 'border-[#00A651] bg-[#e6f6ec]' : 'border-gray-200 bg-white'}`}>
                          <input type="radio" name="scheduleType" checked={scheduleType === 'selected_dates'} onChange={() => patch({ scheduleType: 'selected_dates' })} className="mt-1" data-testid="schedule-type-selected" />
                          <span className="text-sm"><span className="font-medium">とび日</span><br /><span className="text-xs text-gray-500">実施する日をカレンダーから個別に選択します</span></span>
                        </label>
                      </div>
                    </div>

                    {scheduleType === 'consecutive' ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label>{t('admin.schedules.startDate')}</Label>
                          <Input type="date" data-testid="schedule-start-date"
                            value={(sForm.startDate || '').split('T')[0] || ''}
                            onChange={(e) => {
                              const startDate = e.target.value;
                              const course = courses.find(c => c.id === sForm.courseId);
                              const days = course?.days || 1;
                              const endDate = startDate ? (() => { const d = parseISODateObj(startDate); d.setDate(d.getDate() + days - 1); return toISODateStr(d); })() : '';
                              patch({ startDate, endDate });
                            }}
                            className="mt-1" />
                        </div>
                        <div>
                          <Label>{t('admin.schedules.endDate')}</Label>
                          <Input type="date" data-testid="schedule-end-date"
                            value={(sForm.endDate || '').split('T')[0] || ''}
                            onChange={(e) => patch({ endDate: e.target.value })}
                            className="mt-1" />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                        <div>
                          <Label>日付を選択（複数可）</Label>
                          <div className="mt-1 border rounded-lg bg-white flex justify-center overflow-x-auto">
                            <DatePickerCalendar mode="multiple"
                              selected={selDates.map(parseISODateObj)}
                              onSelect={(days) => {
                                const uniqSorted = Array.from(new Set((days || []).map(toISODateStr))).sort();
                                patch({ selectedDates: uniqSorted, startDate: uniqSorted[0] || '', endDate: uniqSorted[uniqSorted.length - 1] || '' });
                              }} />
                          </div>
                        </div>
                        <div>
                          <Label>選択した日付（{selDates.length}日）</Label>
                          <div className="mt-1 border rounded-lg p-2 bg-white max-h-56 overflow-y-auto space-y-1" data-testid="schedule-selected-list">
                            {selDates.length === 0 ? (
                              <p className="text-xs text-gray-400 text-center py-3">カレンダーから日付を選択してください</p>
                            ) : (
                              [...selDates].sort().map(d => (
                                <div key={d} className="flex items-center justify-between text-sm bg-gray-50 rounded px-2 py-1">
                                  <span>{d}</span>
                                  <button type="button" className="text-red-400 hover:text-red-600"
                                    onClick={() => {
                                      const next = selDates.filter(x => x !== d).sort();
                                      patch({ selectedDates: next, startDate: next[0] || '', endDate: next[next.length - 1] || '' });
                                    }}><X className="h-3.5 w-3.5" /></button>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="border rounded-lg p-3 bg-blue-50" data-testid="schedule-preview">
                      <p className="text-sm font-medium text-gray-700">登録日程：{previewDates.length}日間</p>
                      {previewDates.length > 0 && (
                        <ul className="mt-1 text-xs text-gray-600 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-0.5">
                          {previewDates.map(d => (<li key={d}>・{d}</li>))}
                        </ul>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>{t('admin.schedules.capacity')}</Label>
                        <Input type="number" value={sForm.capacity ?? 15} onChange={(e) => patch({ capacity: parseInt(e.target.value) || 0 })} className="mt-1" />
                      </div>
                      <div>
                        <Label>申込数</Label>
                        <Input type="number" value={sForm.enrolled ?? 0} onChange={(e) => patch({ enrolled: parseInt(e.target.value) || 0 })} className="mt-1" data-testid="schedule-enrolled-input" />
                      </div>
                    </div>
                  </>
                );
              })()}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('admin.common.status')}</Label>
                  <Select 
                    value={dialogType === 'editSchedule' ? selectedItem?.status : newSchedule.status}
                    onValueChange={(v) => {
                      if (dialogType === 'editSchedule') {
                        setSelectedItem({...selectedItem, status: v});
                      } else {
                        setNewSchedule({...newSchedule, status: v});
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">{t('admin.schedules.open')}</SelectItem>
                      <SelectItem value="in_progress">{t('admin.schedules.inProgress')}</SelectItem>
                      <SelectItem value="full">{t('admin.schedules.full')}</SelectItem>
                      <SelectItem value="closed">{t('admin.schedules.closed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>{t('admin.schedules.location')}</Label>
                <Input 
                  value={dialogType === 'editSchedule' ? selectedItem?.location || '' : newSchedule.location}
                  onChange={(e) => {
                    if (dialogType === 'editSchedule') {
                      setSelectedItem({...selectedItem, location: e.target.value});
                    } else {
                      setNewSchedule({...newSchedule, location: e.target.value});
                    }
                  }}
                  className="mt-1"
                  placeholder={t('admin.schedules.locationPlaceholder')}
                />
              </div>
              <div>
                <Label>{t('admin.form.remarks')}</Label>
                <Textarea 
                  value={dialogType === 'editSchedule' ? selectedItem?.notes || '' : newSchedule.notes}
                  onChange={(e) => {
                    if (dialogType === 'editSchedule') {
                      setSelectedItem({...selectedItem, notes: e.target.value});
                    } else {
                      setNewSchedule({...newSchedule, notes: e.target.value});
                    }
                  }}
                  rows={2}
                  className="mt-1"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('admin.common.cancel')}</Button>
                <Button 
                  className="bg-[#00A651] hover:bg-[#008a43]" 
                  data-testid="schedule-submit-btn"
                  onClick={submitScheduleForm}
                >
                  {dialogType === 'editSchedule' ? t('admin.common.save') : t('admin.common.add')}
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* New/Edit Course */}
          {(dialogType === 'newCourse' || dialogType === 'editCourse') && (
            <div className="space-y-4">
              {dialogType === 'newCourse' && (
                <div>
                  <Label>{t('admin.courses.courseId')}</Label>
                  <Input 
                    value={newCourse.id}
                    onChange={(e) => setNewCourse({...newCourse, id: e.target.value})}
                    className="mt-1"
                    placeholder="forklift-skill"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('admin.courses.category')}</Label>
                  <Select 
                    value={dialogType === 'editCourse' ? selectedItem?.category : newCourse.category}
                    onValueChange={(v) => {
                      if (dialogType === 'editCourse') {
                        setSelectedItem({...selectedItem, category: v});
                      } else {
                        setNewCourse({...newCourse, category: v});
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.filter(c => c.id !== 'all').map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name.ja}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('admin.certificates.types')}</Label>
                  <Select 
                    value={dialogType === 'editCourse' ? selectedItem?.type : newCourse.type}
                    onValueChange={(v) => {
                      if (dialogType === 'editCourse') {
                        setSelectedItem({...selectedItem, type: v});
                      } else {
                        setNewCourse({...newCourse, type: v});
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {types.filter(t => t.id !== 'all').map(type => (
                        <SelectItem key={type.id} value={type.id}>{type.name.ja}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-1 mb-2 border-b pb-2" data-testid="course-lang-tabs">
                  {COURSE_LANGS.map((lng) => (
                    <button
                      key={lng.code}
                      type="button"
                      onClick={() => setCourseLangTab(lng.code)}
                      data-testid={`course-lang-tab-${lng.code}`}
                      className={`px-3 py-1 rounded-md text-sm transition-colors flex items-center gap-1.5 ${courseLangTab === lng.code ? 'bg-[#00A651] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      <LangFlag cc={lng.cc} /> {lng.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={handleTranslateCourse}
                    disabled={translatingCourse}
                    data-testid="course-translate-all-btn"
                    title={t('admin.courses.translateAllHint')}
                    className="ml-auto px-3 py-1 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 disabled:opacity-60"
                  >
                    {translatingCourse
                      ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('admin.courses.translating')}</>
                      : <><Languages className="h-3.5 w-3.5" /> {t('admin.courses.translateAll')}</>}
                  </button>
                </div>
                <Label>{t('admin.courses.name')} ({COURSE_LANGS.find(l => l.code === courseLangTab)?.label})</Label>
                <Input
                  data-testid={`course-name-input-${courseLangTab}`}
                  value={dialogType === 'editCourse' ? (selectedItem?.name?.[courseLangTab] || '') : (newCourse.name[courseLangTab] || '')}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (dialogType === 'editCourse') {
                      setSelectedItem({...selectedItem, name: {...(selectedItem.name || {}), [courseLangTab]: val}});
                    } else {
                      setNewCourse({...newCourse, name: {...newCourse.name, [courseLangTab]: val}});
                    }
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{t('admin.courses.registrationNumber')}</Label>
                <Input 
                  value={dialogType === 'editCourse' ? selectedItem?.registrationNumber || '' : newCourse.registrationNumber || ''}
                  onChange={(e) => {
                    if (dialogType === 'editCourse') {
                      setSelectedItem({...selectedItem, registrationNumber: e.target.value});
                    } else {
                      setNewCourse({...newCourse, registrationNumber: e.target.value});
                    }
                  }}
                  className="mt-1"
                  placeholder={t('admin.courses.regNumberPlaceholder')}
                />
              </div>
              <div>
                <Label>{t('admin.courses.institutionNumber')}</Label>
                <Input 
                  value={dialogType === 'editCourse' ? selectedItem?.institutionNumber || '' : newCourse.institutionNumber || ''}
                  onChange={(e) => {
                    if (dialogType === 'editCourse') {
                      setSelectedItem({...selectedItem, institutionNumber: e.target.value});
                    } else {
                      setNewCourse({...newCourse, institutionNumber: e.target.value});
                    }
                  }}
                  className="mt-1"
                  placeholder={t('admin.courses.instNumberPlaceholder')}
                />
              </div>
              <div>
                <Label>{t('admin.courses.description')} ({COURSE_LANGS.find(l => l.code === courseLangTab)?.label})</Label>
                <Textarea 
                  data-testid={`course-desc-input-${courseLangTab}`}
                  value={dialogType === 'editCourse' ? (selectedItem?.description?.[courseLangTab] || '') : (newCourse.description[courseLangTab] || '')}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (dialogType === 'editCourse') {
                      setSelectedItem({...selectedItem, description: {...(selectedItem.description || {}), [courseLangTab]: val}});
                    } else {
                      setNewCourse({...newCourse, description: {...newCourse.description, [courseLangTab]: val}});
                    }
                  }}
                  rows={2}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{t('admin.courses.detailedContent')} ({COURSE_LANGS.find(l => l.code === courseLangTab)?.label})</Label>
                <div className="mt-1">
                  <RichTextEditor
                    key={`dc-${courseLangTab}-${dialogType === 'editCourse' ? selectedItem?.id : 'new'}`}
                    value={dialogType === 'editCourse' ? (selectedItem?.detailedContent?.[courseLangTab] || '') : (newCourse.detailedContent?.[courseLangTab] || '')}
                    onChange={(val) => {
                      if (dialogType === 'editCourse') {
                        setSelectedItem({...selectedItem, detailedContent: {...(selectedItem.detailedContent || {}), [courseLangTab]: val}});
                      } else {
                        setNewCourse({...newCourse, detailedContent: {...(newCourse.detailedContent || {}), [courseLangTab]: val}});
                      }
                    }}
                    getAuthHeaders={getAuthHeaders}
                    placeholder={t('admin.courses.detailedContentPlaceholder')}
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label>{t('admin.courses.price')}</Label>
                  <Input 
                    type="number"
                    value={dialogType === 'editCourse' ? selectedItem?.price || 0 : newCourse.price}
                    onChange={(e) => {
                      if (dialogType === 'editCourse') {
                        setSelectedItem({...selectedItem, price: parseInt(e.target.value)});
                      } else {
                        setNewCourse({...newCourse, price: parseInt(e.target.value)});
                      }
                    }}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{t('admin.courses.duration')}</Label>
                  <Input 
                    type="number"
                    value={dialogType === 'editCourse' ? selectedItem?.days || 1 : newCourse.days}
                    onChange={(e) => {
                      if (dialogType === 'editCourse') {
                        setSelectedItem({...selectedItem, days: parseInt(e.target.value)});
                      } else {
                        setNewCourse({...newCourse, days: parseInt(e.target.value)});
                      }
                    }}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{t('admin.courses.theoryHours')}</Label>
                  <Input 
                    type="number"
                    step="0.5"
                    value={dialogType === 'editCourse' ? selectedItem?.theoryHours || 0 : newCourse.theoryHours}
                    onChange={(e) => {
                      if (dialogType === 'editCourse') {
                        setSelectedItem({...selectedItem, theoryHours: parseFloat(e.target.value)});
                      } else {
                        setNewCourse({...newCourse, theoryHours: parseFloat(e.target.value)});
                      }
                    }}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{t('admin.courses.practicalHours')}</Label>
                  <Input 
                    type="number"
                    step="0.5"
                    value={dialogType === 'editCourse' ? selectedItem?.practicalHours || 0 : newCourse.practicalHours}
                    onChange={(e) => {
                      if (dialogType === 'editCourse') {
                        setSelectedItem({...selectedItem, practicalHours: parseFloat(e.target.value)});
                      } else {
                        setNewCourse({...newCourse, practicalHours: parseFloat(e.target.value)});
                      }
                    }}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="border rounded-lg p-3 space-y-2 bg-gray-50">
                <Label className="font-medium">コース設定（A/B/C・任意）</Label>
                <p className="text-xs text-gray-500">有効にしたコースは申込時に受講者が選択できます。日数・料金は共通で、受講時間（学科・実技）のみコースごとに設定します。</p>
                <div className="grid grid-cols-[96px_1fr_1fr_64px] gap-2 items-center text-xs text-gray-500 px-1 pt-1">
                  <span>コース</span><span>学科（時間）</span><span>実技（時間）</span><span className="text-right">合計</span>
                </div>
                {['A','B','C'].map((code) => {
                  const src = dialogType === 'editCourse' ? selectedItem : newCourse;
                  const opts = Array.isArray(src?.courseOptions) ? src.courseOptions : [];
                  const opt = opts.find(o => o.code === code) || { code, enabled: false, theoryHours: 0, practicalHours: 0 };
                  const setOpt = (patch) => {
                    const next = ['A','B','C'].map((c) => {
                      const ex = opts.find(o => o.code === c) || { code: c, enabled: false, theoryHours: 0, practicalHours: 0 };
                      const m = c === code ? { ...ex, ...patch } : ex;
                      const th = parseFloat(m.theoryHours) || 0;
                      const pr = parseFloat(m.practicalHours) || 0;
                      return { code: c, enabled: !!m.enabled, theoryHours: th, practicalHours: pr, totalHours: th + pr };
                    });
                    if (dialogType === 'editCourse') setSelectedItem({ ...selectedItem, courseOptions: next });
                    else setNewCourse({ ...newCourse, courseOptions: next });
                  };
                  const total = (parseFloat(opt.theoryHours) || 0) + (parseFloat(opt.practicalHours) || 0);
                  return (
                    <div key={code} className={`grid grid-cols-[96px_1fr_1fr_64px] gap-2 items-center rounded-md border px-1 py-1.5 bg-white ${opt.enabled ? 'border-[#00A651]/40' : 'border-gray-200 opacity-70'}`} data-testid={`course-option-row-${code}`}>
                      <label className="flex items-center gap-1.5 pl-1 cursor-pointer">
                        <input type="checkbox" checked={!!opt.enabled} onChange={(e) => setOpt({ enabled: e.target.checked })} data-testid={`course-option-enable-${code}`} />
                        <span className="font-medium text-sm">{code}コース</span>
                      </label>
                      <Input type="number" step="0.5" min="0" className="h-8" value={opt.theoryHours || 0} disabled={!opt.enabled} onChange={(e) => setOpt({ theoryHours: e.target.value })} data-testid={`course-option-theory-${code}`} />
                      <Input type="number" step="0.5" min="0" className="h-8" value={opt.practicalHours || 0} disabled={!opt.enabled} onChange={(e) => setOpt({ practicalHours: e.target.value })} data-testid={`course-option-practical-${code}`} />
                      <span className={`text-sm text-right pr-1 ${opt.enabled ? 'font-semibold text-gray-800' : 'text-gray-400'}`} data-testid={`course-option-total-${code}`}>{total}h</span>
                    </div>
                  );
                })}
              </div>
              <div>
                <Label>{t('admin.courses.imageUrl')}</Label>
                <Input 
                  value={dialogType === 'editCourse' ? selectedItem?.image || '' : newCourse.image}
                  onChange={(e) => {
                    if (dialogType === 'editCourse') {
                      setSelectedItem({...selectedItem, image: e.target.value});
                    } else {
                      setNewCourse({...newCourse, image: e.target.value});
                    }
                  }}
                  className="mt-1"
                  placeholder="https://..."
                />
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox"
                  id="featured"
                  checked={dialogType === 'editCourse' ? selectedItem?.featured || false : newCourse.featured}
                  onChange={(e) => {
                    if (dialogType === 'editCourse') {
                      setSelectedItem({...selectedItem, featured: e.target.checked});
                    } else {
                      setNewCourse({...newCourse, featured: e.target.checked});
                    }
                  }}
                  className="h-4 w-4"
                />
                <Label htmlFor="featured">{t('admin.courses.featured')}</Label>
              </div>

              {/* 講習名の表示色（スケジュールカレンダー・スケジュール一覧） */}
              <div>
                <Label>講習名の表示色</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="course-color-picker">
                  {(() => {
                    const current = dialogType === 'editCourse' ? (selectedItem?.color || '') : (newCourse.color || '');
                    const setColor = (cid) => {
                      if (dialogType === 'editCourse') setSelectedItem({ ...selectedItem, color: cid });
                      else setNewCourse({ ...newCourse, color: cid });
                    };
                    return (
                      <>
                        <button type="button" onClick={() => setColor('')}
                          data-testid="course-color-none"
                          className={`px-3 h-8 rounded-full border text-xs ${current === '' ? 'border-gray-900 font-semibold' : 'border-gray-300 text-gray-500'}`}>
                          自動
                        </button>
                        {COURSE_COLOR_OPTIONS.map(c => (
                          <button key={c.id} type="button" onClick={() => setColor(c.id)}
                            title={c.label}
                            data-testid={`course-color-${c.id}`}
                            className={`w-8 h-8 rounded-full ${c.bg} transition-transform ${current === c.id ? 'ring-2 ring-offset-2 ring-gray-900 scale-110' : 'hover:scale-105'}`}
                          />
                        ))}
                      </>
                    );
                  })()}
                </div>
                <p className="text-xs text-gray-400 mt-1">トップページのスケジュールカレンダーと管理画面のスケジュール一覧に反映されます</p>
              </div>

              {/* On-demand Course Settings */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <PlayCircle className="h-4 w-4" />
                  {t('admin.courses.courseTypeSettings')}
                </h4>
                <div className="space-y-3">
                  <div>
                    <Label>{t('admin.courses.courseType')}</Label>
                    <Select
                      value={dialogType === 'editCourse' ? selectedItem?.courseType || 'in-person' : newCourse.courseType}
                      onValueChange={(value) => {
                        if (dialogType === 'editCourse') {
                          setSelectedItem({...selectedItem, courseType: value});
                        } else {
                          setNewCourse({...newCourse, courseType: value});
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in-person">{t('admin.courses.inPerson')}</SelectItem>
                        <SelectItem value="on-demand">{t('admin.courses.onDemand')}</SelectItem>
                        <SelectItem value="hybrid">{t('admin.courses.hybrid')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* On-demand specific settings */}
                  {((dialogType === 'editCourse' && (selectedItem?.courseType === 'on-demand' || selectedItem?.courseType === 'hybrid')) ||
                    (dialogType !== 'editCourse' && (newCourse.courseType === 'on-demand' || newCourse.courseType === 'hybrid'))) && (
                    <div className="bg-orange-50 p-3 rounded-lg space-y-3">
                      <p className="text-sm text-orange-800 font-medium">{t('admin.courses.onDemandSettings')}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">{t('admin.courses.viewingPeriod')}</Label>
                          <Input
                            type="number"
                            min="1"
                            value={dialogType === 'editCourse' 
                              ? selectedItem?.onDemandSettings?.viewingPeriodDays || 30 
                              : newCourse.onDemandSettings.viewingPeriodDays}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 30;
                              if (dialogType === 'editCourse') {
                                setSelectedItem({
                                  ...selectedItem, 
                                  onDemandSettings: {...(selectedItem.onDemandSettings || {}), viewingPeriodDays: value}
                                });
                              } else {
                                setNewCourse({
                                  ...newCourse, 
                                  onDemandSettings: {...newCourse.onDemandSettings, viewingPeriodDays: value}
                                });
                              }
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{t('admin.courses.passingScore')}</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={dialogType === 'editCourse' 
                              ? selectedItem?.onDemandSettings?.passingScore || 80 
                              : newCourse.onDemandSettings.passingScore}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 80;
                              if (dialogType === 'editCourse') {
                                setSelectedItem({
                                  ...selectedItem, 
                                  onDemandSettings: {...(selectedItem.onDemandSettings || {}), passingScore: value}
                                });
                              } else {
                                setNewCourse({
                                  ...newCourse, 
                                  onDemandSettings: {...newCourse.onDemandSettings, passingScore: value}
                                });
                              }
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{t('admin.courses.maxRetake')}</Label>
                          <Input
                            type="number"
                            min="0"
                            value={dialogType === 'editCourse' 
                              ? selectedItem?.onDemandSettings?.maxRetakeCount || 3 
                              : newCourse.onDemandSettings.maxRetakeCount}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 3;
                              if (dialogType === 'editCourse') {
                                setSelectedItem({
                                  ...selectedItem, 
                                  onDemandSettings: {...(selectedItem.onDemandSettings || {}), maxRetakeCount: value}
                                });
                              } else {
                                setNewCourse({
                                  ...newCourse, 
                                  onDemandSettings: {...newCourse.onDemandSettings, maxRetakeCount: value}
                                });
                              }
                            }}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="certificateAutoIssue"
                            checked={dialogType === 'editCourse' 
                              ? selectedItem?.onDemandSettings?.certificateAutoIssue !== false 
                              : newCourse.onDemandSettings.certificateAutoIssue}
                            onChange={(e) => {
                              if (dialogType === 'editCourse') {
                                setSelectedItem({
                                  ...selectedItem, 
                                  onDemandSettings: {...(selectedItem.onDemandSettings || {}), certificateAutoIssue: e.target.checked}
                                });
                              } else {
                                setNewCourse({
                                  ...newCourse, 
                                  onDemandSettings: {...newCourse.onDemandSettings, certificateAutoIssue: e.target.checked}
                                });
                              }
                            }}
                            className="h-4 w-4"
                          />
                          <Label htmlFor="certificateAutoIssue" className="text-xs">{t('admin.courses.certAutoIssue')}</Label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('admin.common.cancel')}</Button>
                <Button 
                  className="bg-[#00A651] hover:bg-[#008a43]" 
                  onClick={() => {
                    if (dialogType === 'editCourse') {
                      updateCourse(selectedItem.id, selectedItem);
                    } else {
                      createCourse();
                    }
                  }}
                >
                  {dialogType === 'editCourse' ? t('admin.common.save') : t('admin.common.add')}
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* User Detail/Edit Dialog */}
          {(dialogType === 'viewUser' || dialogType === 'editUser') && selectedItem && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Profile Header */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-4">
                  {selectedItem.profileImage ? (
                    <img 
                      src={getImageUrl(selectedItem.profileImage)} 
                      alt="" 
                      onClick={() => setLightboxImg(getImageUrl(selectedItem.profileImage))}
                      className="w-16 h-16 rounded-full object-cover border-2 border-[#00A651] shadow cursor-pointer hover:opacity-80 transition-opacity"
                      data-testid="user-profile-photo"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center border-2 border-[#00A651]">
                      <User className="h-8 w-8 text-gray-500" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-bold">{selectedItem.name}</h3>
                    <p className="text-sm text-gray-400">ID: {selectedItem.id}</p>
                    <p className="text-xs text-gray-400">{t('admin.users.registrationDate')}: {formatDateShort(selectedItem.createdAt)}</p>
                  </div>
                </div>
                {dialogType === 'viewUser' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditUserData({...selectedItem});
                      setDialogType('editUser');
                    }}
                  >
                    <Edit className="h-4 w-4 mr-1" /> {t('admin.common.edit')}
                  </Button>
                )}
              </div>

              {dialogType === 'viewUser' ? (
                <>
                  {/* Basic Info - View Mode */}
                  <div className="p-4 bg-white border rounded-lg">
                    <h4 className="font-medium mb-3 text-sm text-gray-700">{t('admin.users.basicInfo')}</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-gray-500">{t('admin.form.nameLabel')}</span> {selectedItem.name}</div>
                      <div><span className="text-gray-500">{t('admin.form.nameKanaLabel')}</span> {selectedItem.nameKana || '-'}</div>
                      <div><span className="text-gray-500">{t('admin.form.emailLabel')}</span> {selectedItem.email}</div>
                      <div><span className="text-gray-500">{t('admin.form.phoneLabel')}</span> {selectedItem.phone || '-'}</div>
                      <div><span className="text-gray-500">{t('admin.form.birthDateLabel')}</span> {selectedItem.birthDate || '-'}</div>
                      <div><span className="text-gray-500">{t('admin.form.genderLabel')}</span> {selectedItem.gender === 'male' ? t('admin.form.male') : selectedItem.gender === 'female' ? t('admin.form.female') : (selectedItem.gender || '-')}</div>
                      <div><span className="text-gray-500">{t('admin.form.nationalityLabel')}</span> {selectedItem.nationality || '-'}</div>
                      <div><span className="text-gray-500">{t('admin.form.postalCodeLabel')}</span> {selectedItem.postalCode || '-'}</div>
                      <div className="col-span-2"><span className="text-gray-500">{t('admin.form.addressLabel')}</span> {selectedItem.address || '-'}</div>
                    </div>
                  </div>

                  {/* Company Info - View Mode */}
                  <div className="p-4 bg-white border rounded-lg">
                    <h4 className="font-medium mb-3 text-sm text-gray-700">{t('admin.users.companyInfo')}</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-gray-500">{t('admin.form.companyLabel')}</span> {selectedItem.company || '-'}</div>
                      <div><span className="text-gray-500">{t('admin.form.departmentLabel')}</span> {selectedItem.companyDepartment || '-'}</div>
                      <div><span className="text-gray-500">{t('admin.form.positionLabel')}</span> {selectedItem.companyPosition || '-'}</div>
                      <div><span className="text-gray-500">{t('admin.form.companyPhoneLabel')}</span> {selectedItem.companyPhone || '-'}</div>
                      <div><span className="text-gray-500">{t('admin.form.companyPostalCodeLabel')}</span> {selectedItem.companyPostalCode || '-'}</div>
                      <div className="col-span-2"><span className="text-gray-500">{t('admin.form.companyAddressLabel')}</span> {selectedItem.companyAddress || '-'}</div>
                    </div>
                  </div>

                  {/* Documents - View Mode */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium flex items-center gap-2 mb-3 text-sm">
                        <IdCard className="h-4 w-4" /> {t('admin.users.idDocument')}
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-gray-500">{t('admin.users.frontCurrent')}</Label>
                          {selectedItem.idDocumentFront ? (
                            <>
                              <a href={getImageUrl(selectedItem.idDocumentFront)} target="_blank" rel="noopener noreferrer">
                                <img src={getImageUrl(selectedItem.idDocumentFront)} alt="ID Front" className="w-full h-20 object-cover rounded border hover:opacity-80 transition mt-1" />
                              </a>
                              <div className="flex items-center justify-between mt-1">
                                {selectedItem.idDocumentFrontUploadedAt && (
                                  <p className="text-[10px] text-gray-400">{t('admin.common.registeredAt')}: {formatDateShort(selectedItem.idDocumentFrontUploadedAt)}</p>
                                )}
                                <button
                                  data-testid="delete-id-doc-front"
                                  onClick={() => handleAdminDeleteDocument(selectedItem.id, 'idDocumentFront')}
                                  className="text-red-400 hover:text-red-600 transition p-0.5"
                                  title={t('admin.common.delete')}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </>
                          ) : <div className="mt-1 h-20 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">{t('admin.users.notRegistered')}</div>}
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">{t('admin.users.backCurrent')}</Label>
                          {selectedItem.idDocumentBack ? (
                            <>
                              <a href={getImageUrl(selectedItem.idDocumentBack)} target="_blank" rel="noopener noreferrer">
                                <img src={getImageUrl(selectedItem.idDocumentBack)} alt="ID Back" className="w-full h-20 object-cover rounded border hover:opacity-80 transition mt-1" />
                              </a>
                              <div className="flex items-center justify-between mt-1">
                                {selectedItem.idDocumentBackUploadedAt && (
                                  <p className="text-[10px] text-gray-400">{t('admin.common.registeredAt')}: {formatDateShort(selectedItem.idDocumentBackUploadedAt)}</p>
                                )}
                                <button
                                  data-testid="delete-id-doc-back"
                                  onClick={() => handleAdminDeleteDocument(selectedItem.id, 'idDocumentBack')}
                                  className="text-red-400 hover:text-red-600 transition p-0.5"
                                  title={t('admin.common.delete')}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </>
                          ) : <div className="mt-1 h-20 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">{t('admin.users.notRegistered')}</div>}
                        </div>
                      </div>
                      {/* ID Document History */}
                      {(selectedItem.idDocumentFrontHistory?.length > 0 || selectedItem.idDocumentBackHistory?.length > 0) && (
                        <div className="mt-3 border-t pt-3">
                          <button
                            type="button"
                            onClick={() => setShowHistory(prev => ({...prev, idDoc: !prev.idDoc}))}
                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                          >
                            <History className="h-3 w-3" />
                            {t('admin.users.pastDocuments')} ({(selectedItem.idDocumentFrontHistory?.length || 0) + (selectedItem.idDocumentBackHistory?.length || 0)}{t('admin.common.items')})
                            {showHistory.idDoc ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          </button>
                          {showHistory.idDoc && (
                            <div className="mt-2 space-y-2">
                              {selectedItem.idDocumentFrontHistory?.map((hist, idx) => (
                                <div key={`idFront-${idx}`} className="flex items-center gap-2 p-2 bg-white rounded border">
                                  <a href={getImageUrl(hist.path)} target="_blank" rel="noopener noreferrer">
                                    <img src={getImageUrl(hist.path)} alt="" className="w-12 h-12 object-cover rounded" />
                                  </a>
                                  <div className="text-[10px] text-gray-500 flex-1">
                                    <p>{t('admin.users.front')}</p>
                                    <p>{t('admin.common.registeredAt')}: {formatDateShort(hist.uploadedAt)}</p>
                                    <p>{t('admin.common.updatedAt')}: {formatDateShort(hist.archivedAt)}</p>
                                  </div>
                                  <button
                                    data-testid={`delete-id-front-history-${idx}`}
                                    onClick={() => handleAdminDeleteDocumentHistory(selectedItem.id, 'idDocumentFront', idx)}
                                    className="text-red-400 hover:text-red-600 transition p-1 shrink-0"
                                    title={t('admin.common.delete')}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))}
                              {selectedItem.idDocumentBackHistory?.map((hist, idx) => (
                                <div key={`idBack-${idx}`} className="flex items-center gap-2 p-2 bg-white rounded border">
                                  <a href={getImageUrl(hist.path)} target="_blank" rel="noopener noreferrer">
                                    <img src={getImageUrl(hist.path)} alt="" className="w-12 h-12 object-cover rounded" />
                                  </a>
                                  <div className="text-[10px] text-gray-500 flex-1">
                                    <p>{t('admin.users.back')}</p>
                                    <p>{t('admin.common.registeredAt')}: {formatDateShort(hist.uploadedAt)}</p>
                                    <p>{t('admin.common.updatedAt')}: {formatDateShort(hist.archivedAt)}</p>
                                  </div>
                                  <button
                                    data-testid={`delete-id-back-history-${idx}`}
                                    onClick={() => handleAdminDeleteDocumentHistory(selectedItem.id, 'idDocumentBack', idx)}
                                    className="text-red-400 hover:text-red-600 transition p-1 shrink-0"
                                    title={t('admin.common.delete')}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium flex items-center gap-2 mb-3 text-sm">
                        <Award className="h-4 w-4" /> {t('admin.users.certDocuments')}
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-gray-500">{t('admin.users.frontCurrent')}</Label>
                          {selectedItem.certDocumentFront ? (
                            <>
                              <a href={getImageUrl(selectedItem.certDocumentFront)} target="_blank" rel="noopener noreferrer">
                                <img src={getImageUrl(selectedItem.certDocumentFront)} alt="Cert Front" className="w-full h-20 object-cover rounded border hover:opacity-80 transition mt-1" />
                              </a>
                              <div className="flex items-center justify-between mt-1">
                                {selectedItem.certDocumentFrontUploadedAt && (
                                  <p className="text-[10px] text-gray-400">{t('admin.common.registeredAt')}: {formatDateShort(selectedItem.certDocumentFrontUploadedAt)}</p>
                                )}
                                <button
                                  data-testid="delete-cert-doc-front"
                                  onClick={() => handleAdminDeleteDocument(selectedItem.id, 'certDocumentFront')}
                                  className="text-red-400 hover:text-red-600 transition p-0.5"
                                  title={t('admin.common.delete')}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </>
                          ) : <div className="mt-1 h-20 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">{t('admin.users.notRegistered')}</div>}
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">{t('admin.users.backCurrent')}</Label>
                          {selectedItem.certDocumentBack ? (
                            <>
                              <a href={getImageUrl(selectedItem.certDocumentBack)} target="_blank" rel="noopener noreferrer">
                                <img src={getImageUrl(selectedItem.certDocumentBack)} alt="Cert Back" className="w-full h-20 object-cover rounded border hover:opacity-80 transition mt-1" />
                              </a>
                              <div className="flex items-center justify-between mt-1">
                                {selectedItem.certDocumentBackUploadedAt && (
                                  <p className="text-[10px] text-gray-400">{t('admin.common.registeredAt')}: {formatDateShort(selectedItem.certDocumentBackUploadedAt)}</p>
                                )}
                                <button
                                  data-testid="delete-cert-doc-back"
                                  onClick={() => handleAdminDeleteDocument(selectedItem.id, 'certDocumentBack')}
                                  className="text-red-400 hover:text-red-600 transition p-0.5"
                                  title={t('admin.common.delete')}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </>
                          ) : <div className="mt-1 h-20 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">{t('admin.users.notRegistered')}</div>}
                        </div>
                      </div>
                      {/* Cert Document History */}
                      {(selectedItem.certDocumentFrontHistory?.length > 0 || selectedItem.certDocumentBackHistory?.length > 0) && (
                        <div className="mt-3 border-t pt-3">
                          <button
                            type="button"
                            onClick={() => setShowHistory(prev => ({...prev, certDoc: !prev.certDoc}))}
                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                          >
                            <History className="h-3 w-3" />
                            {t('admin.users.pastDocuments')} ({(selectedItem.certDocumentFrontHistory?.length || 0) + (selectedItem.certDocumentBackHistory?.length || 0)}{t('admin.common.items')})
                            {showHistory.certDoc ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          </button>
                          {showHistory.certDoc && (
                            <div className="mt-2 space-y-2">
                              {selectedItem.certDocumentFrontHistory?.map((hist, idx) => (
                                <div key={`certFront-${idx}`} className="flex items-center gap-2 p-2 bg-white rounded border">
                                  <a href={getImageUrl(hist.path)} target="_blank" rel="noopener noreferrer">
                                    <img src={getImageUrl(hist.path)} alt="" className="w-12 h-12 object-cover rounded" />
                                  </a>
                                  <div className="text-[10px] text-gray-500 flex-1">
                                    <p>{t('admin.users.front')}</p>
                                    <p>{t('admin.common.registeredAt')}: {formatDateShort(hist.uploadedAt)}</p>
                                    <p>{t('admin.common.updatedAt')}: {formatDateShort(hist.archivedAt)}</p>
                                  </div>
                                  <button
                                    data-testid={`delete-cert-front-history-${idx}`}
                                    onClick={() => handleAdminDeleteDocumentHistory(selectedItem.id, 'certDocumentFront', idx)}
                                    className="text-red-400 hover:text-red-600 transition p-1 shrink-0"
                                    title={t('admin.common.delete')}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))}
                              {selectedItem.certDocumentBackHistory?.map((hist, idx) => (
                                <div key={`certBack-${idx}`} className="flex items-center gap-2 p-2 bg-white rounded border">
                                  <a href={getImageUrl(hist.path)} target="_blank" rel="noopener noreferrer">
                                    <img src={getImageUrl(hist.path)} alt="" className="w-12 h-12 object-cover rounded" />
                                  </a>
                                  <div className="text-[10px] text-gray-500 flex-1">
                                    <p>{t('admin.users.back')}</p>
                                    <p>{t('admin.common.registeredAt')}: {formatDateShort(hist.uploadedAt)}</p>
                                    <p>{t('admin.common.updatedAt')}: {formatDateShort(hist.archivedAt)}</p>
                                  </div>
                                  <button
                                    data-testid={`delete-cert-back-history-${idx}`}
                                    onClick={() => handleAdminDeleteDocumentHistory(selectedItem.id, 'certDocumentBack', idx)}
                                    className="text-red-400 hover:text-red-600 transition p-1 shrink-0"
                                    title={t('admin.common.delete')}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('admin.common.close')}</Button>
                  </DialogFooter>
                </>
              ) : (
                <>
                  {/* Edit Mode */}
                  <div className="p-4 bg-white border rounded-lg">
                    <h4 className="font-medium mb-3 text-sm text-gray-700">{t('admin.users.basicInfo')}</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">{t('admin.form.nameRequired')}</Label>
                        <Input value={editUserData.name || ''} onChange={(e) => setEditUserData({...editUserData, name: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">{t('admin.form.nameKana')}</Label>
                        <Input value={editUserData.nameKana || ''} onChange={(e) => setEditUserData({...editUserData, nameKana: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">{t('admin.form.emailRequired')}</Label>
                        <Input type="email" value={editUserData.email || ''} onChange={(e) => setEditUserData({...editUserData, email: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">{t('admin.form.phone')}</Label>
                        <Input value={editUserData.phone || ''} onChange={(e) => setEditUserData({...editUserData, phone: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">{t('admin.form.birthDate')}</Label>
                        <Input type="date" value={editUserData.birthDate || ''} onChange={(e) => setEditUserData({...editUserData, birthDate: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">{t('admin.form.gender')}</Label>
                        <select value={editUserData.gender || ''} onChange={(e) => setEditUserData({...editUserData, gender: e.target.value})} className="mt-1 w-full border rounded-md px-3 py-2 text-sm">
                          <option value="">{t('admin.form.selectPlease')}</option>
                          <option value="male">{t('admin.form.male')}</option>
                          <option value="female">{t('admin.form.female')}</option>
                          <option value="other">{t('admin.form.other')}</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">{t('admin.form.nationality')}</Label>
                        <div className="mt-1">
                          <NationalitySelect
                            value={editUserData.nationality || ''}
                            onChange={(val) => setEditUserData({...editUserData, nationality: val})}
                            language={language}
                            dataTestId="admin-nationality-select"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">{t('admin.form.postalCode')}</Label>
                        <Input value={editUserData.postalCode || ''} onChange={(e) => setEditUserData({...editUserData, postalCode: e.target.value})} className="mt-1" placeholder="123-4567" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">{t('admin.form.address')}</Label>
                        <Input value={editUserData.address || ''} onChange={(e) => setEditUserData({...editUserData, address: e.target.value})} className="mt-1" />
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-white border rounded-lg">
                    <h4 className="font-medium mb-3 text-sm text-gray-700">{t('admin.users.companyInfo')}</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">{t('admin.form.company')}</Label>
                        <Input value={editUserData.company || ''} onChange={(e) => setEditUserData({...editUserData, company: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">{t('admin.form.department')}</Label>
                        <Input value={editUserData.companyDepartment || ''} onChange={(e) => setEditUserData({...editUserData, companyDepartment: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">{t('admin.form.position')}</Label>
                        <Input value={editUserData.companyPosition || ''} onChange={(e) => setEditUserData({...editUserData, companyPosition: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">{t('admin.form.companyPhone')}</Label>
                        <Input value={editUserData.companyPhone || ''} onChange={(e) => setEditUserData({...editUserData, companyPhone: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">{t('admin.form.companyPostalCode')}</Label>
                        <Input value={editUserData.companyPostalCode || ''} onChange={(e) => setEditUserData({...editUserData, companyPostalCode: e.target.value})} className="mt-1" placeholder="123-4567" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">{t('admin.form.companyAddress')}</Label>
                        <Input value={editUserData.companyAddress || ''} onChange={(e) => setEditUserData({...editUserData, companyAddress: e.target.value})} className="mt-1" />
                      </div>
                    </div>
                  </div>

                  {/* User Role Section */}
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-medium mb-3 text-sm text-yellow-800">{t('admin.users.roleSettings')}</h4>
                    <div>
                      <Label className="text-xs">{t('admin.users.userRole')}</Label>
                      <select 
                        value={editUserData.role || 'user'} 
                        onChange={(e) => setEditUserData({...editUserData, role: e.target.value})} 
                        className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                      >
                        <option value="user">{t('admin.users.roleUser')}</option>
                        <option value="admin">{t('admin.users.roleAdmin')}</option>
                      </select>
                      <p className="text-xs text-yellow-600 mt-1">{t('admin.users.adminRoleHint')}</p>
                    </div>
                  </div>

                  {/* Password Change Section */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-sm text-blue-800">{t('admin.users.changePassword')}</h4>
                      {!showPasswordChange && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-blue-600 border-blue-300 hover:bg-blue-100"
                          onClick={() => setShowPasswordChange(true)}
                        >
                          <Lock className="h-3 w-3 mr-1" />
                          {t('admin.users.changePasswordBtn')}
                        </Button>
                      )}
                    </div>
                    {showPasswordChange ? (
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs">{t('admin.users.newPassword')}</Label>
                          <Input 
                            type="password" 
                            value={editUserData.newPassword || ''} 
                            onChange={(e) => setEditUserData({...editUserData, newPassword: e.target.value})} 
                            className="mt-1" 
                            placeholder={t('admin.users.passwordPlaceholder')}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{t('admin.users.confirmPassword')}</Label>
                          <Input 
                            type="password" 
                            value={editUserData.confirmPassword || ''} 
                            onChange={(e) => setEditUserData({...editUserData, confirmPassword: e.target.value})} 
                            className="mt-1" 
                            placeholder={t('admin.users.confirmPasswordPlaceholder')}
                          />
                        </div>
                        {editUserData.newPassword && editUserData.confirmPassword && editUserData.newPassword !== editUserData.confirmPassword && (
                          <p className="text-xs text-red-600">{t('admin.users.passwordMismatch')}</p>
                        )}
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-gray-500"
                            onClick={() => {
                              setShowPasswordChange(false);
                              setEditUserData(prev => ({...prev, newPassword: '', confirmPassword: ''}));
                            }}
                          >
                            {t('admin.common.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">{t('admin.users.changePasswordHint')}</p>
                    )}
                  </div>

                  {/* Document Upload Section - Edit Mode */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* ID Document Upload */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium flex items-center gap-2 mb-3 text-sm">
                        <IdCard className="h-4 w-4" /> {t('admin.users.idDocument')}
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-gray-500">{t('admin.users.front')}</Label>
                          {(selectedItem.idDocumentFront || editUserData.idDocumentFront) ? (
                            <div className="relative group">
                              <img 
                                src={getImageUrl(editUserData.idDocumentFront || selectedItem.idDocumentFront)} 
                                alt="ID Front" 
                                className="w-full h-20 object-cover rounded border mt-1" 
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2 rounded mt-1">
                                <button
                                  type="button"
                                  onClick={() => idFrontRef.current?.click()}
                                  className="p-2 bg-white/20 hover:bg-white/40 rounded text-white"
                                  disabled={uploadingDoc === 'idDocumentFront'}
                                  title={t('admin.common.selectFile')}
                                >
                                  {uploadingDoc === 'idDocumentFront' ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => idFrontCameraRef.current?.click()}
                                  className="p-2 bg-white/20 hover:bg-white/40 rounded text-white"
                                  disabled={uploadingDoc === 'idDocumentFront'}
                                  title={t('admin.common.takePhoto')}
                                >
                                  <Camera className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  data-testid="edit-delete-id-doc-front"
                                  onClick={() => handleAdminDeleteDocument(selectedItem.id, 'idDocumentFront')}
                                  className="p-2 bg-red-500/70 hover:bg-red-600/90 rounded text-white"
                                  title={t('admin.common.delete')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-1 flex gap-1">
                              <button
                                type="button"
                                onClick={() => idFrontRef.current?.click()}
                                className="flex-1 h-20 bg-green-50 hover:bg-green-100 border-2 border-dashed border-green-300 rounded flex flex-col items-center justify-center text-green-600 text-xs transition"
                                disabled={uploadingDoc === 'idDocumentFront'}
                              >
                                {uploadingDoc === 'idDocumentFront' ? <RefreshCw className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mb-1" /> {t('admin.common.file')}</>}
                              </button>
                              <button
                                type="button"
                                onClick={() => idFrontCameraRef.current?.click()}
                                className="flex-1 h-20 bg-blue-50 hover:bg-blue-100 border-2 border-dashed border-blue-300 rounded flex flex-col items-center justify-center text-blue-600 text-xs transition"
                                disabled={uploadingDoc === 'idDocumentFront'}
                              >
                                <Camera className="h-4 w-4 mb-1" /> {t('admin.common.camera')}
                              </button>
                            </div>
                          )}
                          {(selectedItem.idDocumentFrontUploadedAt || editUserData.idDocumentFrontUploadedAt) && (
                            <p className="text-[10px] text-gray-400 mt-1">{t('admin.common.registeredAt')}: {formatDateShort(editUserData.idDocumentFrontUploadedAt || selectedItem.idDocumentFrontUploadedAt)}</p>
                          )}
                          <input
                            type="file"
                            ref={idFrontRef}
                            accept="image/*,.pdf"
                            className="hidden"
                            onChange={(e) => e.target.files[0] && handleAdminUploadDocument(selectedItem.id, e.target.files[0], 'idDocumentFront')}
                          />
                          <input
                            type="file"
                            ref={idFrontCameraRef}
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => e.target.files[0] && handleAdminUploadDocument(selectedItem.id, e.target.files[0], 'idDocumentFront')}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">{t('admin.users.back')}</Label>
                          {(selectedItem.idDocumentBack || editUserData.idDocumentBack) ? (
                            <div className="relative group">
                              <img 
                                src={getImageUrl(editUserData.idDocumentBack || selectedItem.idDocumentBack)} 
                                alt="ID Back" 
                                className="w-full h-20 object-cover rounded border mt-1" 
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2 rounded mt-1">
                                <button
                                  type="button"
                                  onClick={() => idBackRef.current?.click()}
                                  className="p-2 bg-white/20 hover:bg-white/40 rounded text-white"
                                  disabled={uploadingDoc === 'idDocumentBack'}
                                  title={t('admin.common.selectFile')}
                                >
                                  {uploadingDoc === 'idDocumentBack' ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => idBackCameraRef.current?.click()}
                                  className="p-2 bg-white/20 hover:bg-white/40 rounded text-white"
                                  disabled={uploadingDoc === 'idDocumentBack'}
                                  title={t('admin.common.takePhoto')}
                                >
                                  <Camera className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  data-testid="edit-delete-id-doc-back"
                                  onClick={() => handleAdminDeleteDocument(selectedItem.id, 'idDocumentBack')}
                                  className="p-2 bg-red-500/70 hover:bg-red-600/90 rounded text-white"
                                  title={t('admin.common.delete')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-1 flex gap-1">
                              <button
                                type="button"
                                onClick={() => idBackRef.current?.click()}
                                className="flex-1 h-20 bg-green-50 hover:bg-green-100 border-2 border-dashed border-green-300 rounded flex flex-col items-center justify-center text-green-600 text-xs transition"
                                disabled={uploadingDoc === 'idDocumentBack'}
                              >
                                {uploadingDoc === 'idDocumentBack' ? <RefreshCw className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mb-1" /> {t('admin.common.file')}</>}
                              </button>
                              <button
                                type="button"
                                onClick={() => idBackCameraRef.current?.click()}
                                className="flex-1 h-20 bg-blue-50 hover:bg-blue-100 border-2 border-dashed border-blue-300 rounded flex flex-col items-center justify-center text-blue-600 text-xs transition"
                                disabled={uploadingDoc === 'idDocumentBack'}
                              >
                                <Camera className="h-4 w-4 mb-1" /> {t('admin.common.camera')}
                              </button>
                            </div>
                          )}
                          {(selectedItem.idDocumentBackUploadedAt || editUserData.idDocumentBackUploadedAt) && (
                            <p className="text-[10px] text-gray-400 mt-1">{t('admin.common.registeredAt')}: {formatDateShort(editUserData.idDocumentBackUploadedAt || selectedItem.idDocumentBackUploadedAt)}</p>
                          )}
                          <input
                            type="file"
                            ref={idBackRef}
                            accept="image/*,.pdf"
                            className="hidden"
                            onChange={(e) => e.target.files[0] && handleAdminUploadDocument(selectedItem.id, e.target.files[0], 'idDocumentBack')}
                          />
                          <input
                            type="file"
                            ref={idBackCameraRef}
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => e.target.files[0] && handleAdminUploadDocument(selectedItem.id, e.target.files[0], 'idDocumentBack')}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Qualification Certificate Upload */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium flex items-center gap-2 mb-3 text-sm">
                        <Award className="h-4 w-4" /> {t('admin.users.certDocuments')}
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-gray-500">{t('admin.users.front')}</Label>
                          <div className="relative group">
                            {(selectedItem.certDocumentFront || editUserData.certDocumentFront) ? (
                              <img 
                                src={getImageUrl(editUserData.certDocumentFront || selectedItem.certDocumentFront)} 
                                alt="Cert Front" 
                                className="w-full h-20 object-cover rounded border mt-1" 
                              />
                            ) : (
                              <div className="mt-1 h-20 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">{t('admin.users.notRegistered')}</div>
                            )}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2 rounded mt-1">
                              <button
                                type="button"
                                onClick={() => certFrontRef.current?.click()}
                                className="p-2 bg-white/20 hover:bg-white/40 rounded text-white"
                                disabled={uploadingDoc === 'certDocumentFront'}
                                title={t('admin.common.selectFile')}
                              >
                                {uploadingDoc === 'certDocumentFront' ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                              </button>
                              <button
                                type="button"
                                onClick={() => certFrontCameraRef.current?.click()}
                                className="p-2 bg-white/20 hover:bg-white/40 rounded text-white"
                                disabled={uploadingDoc === 'certDocumentFront'}
                                title={t('admin.common.takePhoto')}
                              >
                                <Camera className="h-4 w-4" />
                              </button>
                              {(selectedItem.certDocumentFront || editUserData.certDocumentFront) && (
                                <button
                                  type="button"
                                  data-testid="edit-delete-cert-doc-front"
                                  onClick={() => handleAdminDeleteDocument(selectedItem.id, 'certDocumentFront')}
                                  className="p-2 bg-red-500/70 hover:bg-red-600/90 rounded text-white"
                                  title={t('admin.common.delete')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                          {(selectedItem.certDocumentFrontUploadedAt || editUserData.certDocumentFrontUploadedAt) && (
                            <p className="text-[10px] text-gray-400 mt-1">{t('admin.common.registeredAt')}: {formatDateShort(editUserData.certDocumentFrontUploadedAt || selectedItem.certDocumentFrontUploadedAt)}</p>
                          )}
                          <input
                            type="file"
                            ref={certFrontRef}
                            accept="image/*,.pdf"
                            className="hidden"
                            onChange={(e) => e.target.files[0] && handleAdminUploadDocument(selectedItem.id, e.target.files[0], 'certDocumentFront')}
                          />
                          <input
                            type="file"
                            ref={certFrontCameraRef}
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => e.target.files[0] && handleAdminUploadDocument(selectedItem.id, e.target.files[0], 'certDocumentFront')}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">{t('admin.users.back')}</Label>
                          <div className="relative group">
                            {(selectedItem.certDocumentBack || editUserData.certDocumentBack) ? (
                              <img 
                                src={getImageUrl(editUserData.certDocumentBack || selectedItem.certDocumentBack)} 
                                alt="Cert Back" 
                                className="w-full h-20 object-cover rounded border mt-1" 
                              />
                            ) : (
                              <div className="mt-1 h-20 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">{t('admin.users.notRegistered')}</div>
                            )}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2 rounded mt-1">
                              <button
                                type="button"
                                onClick={() => certBackRef.current?.click()}
                                className="p-2 bg-white/20 hover:bg-white/40 rounded text-white"
                                disabled={uploadingDoc === 'certDocumentBack'}
                                title={t('admin.common.selectFile')}
                              >
                                {uploadingDoc === 'certDocumentBack' ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                              </button>
                              <button
                                type="button"
                                onClick={() => certBackCameraRef.current?.click()}
                                className="p-2 bg-white/20 hover:bg-white/40 rounded text-white"
                                disabled={uploadingDoc === 'certDocumentBack'}
                                title={t('admin.common.takePhoto')}
                              >
                                <Camera className="h-4 w-4" />
                              </button>
                              {(selectedItem.certDocumentBack || editUserData.certDocumentBack) && (
                                <button
                                  type="button"
                                  data-testid="edit-delete-cert-doc-back"
                                  onClick={() => handleAdminDeleteDocument(selectedItem.id, 'certDocumentBack')}
                                  className="p-2 bg-red-500/70 hover:bg-red-600/90 rounded text-white"
                                  title={t('admin.common.delete')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                          {(selectedItem.certDocumentBackUploadedAt || editUserData.certDocumentBackUploadedAt) && (
                            <p className="text-[10px] text-gray-400 mt-1">{t('admin.common.registeredAt')}: {formatDateShort(editUserData.certDocumentBackUploadedAt || selectedItem.certDocumentBackUploadedAt)}</p>
                          )}
                          <input
                            type="file"
                            ref={certBackRef}
                            accept="image/*,.pdf"
                            className="hidden"
                            onChange={(e) => e.target.files[0] && handleAdminUploadDocument(selectedItem.id, e.target.files[0], 'certDocumentBack')}
                          />
                          <input
                            type="file"
                            ref={certBackCameraRef}
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => e.target.files[0] && handleAdminUploadDocument(selectedItem.id, e.target.files[0], 'certDocumentBack')}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => setDialogType('viewUser')}>{t('admin.common.cancel')}</Button>
                    <Button 
                      className="bg-[#00A651] hover:bg-[#008a43]"
                      disabled={editUserData.newPassword && editUserData.newPassword !== editUserData.confirmPassword}
                      onClick={async () => {
                        try {
                          // Validate password if provided
                          if (editUserData.newPassword) {
                            if (editUserData.newPassword.length < 6) {
                              toast.error(t('admin.users.passwordMinLength'));
                              return;
                            }
                            if (editUserData.newPassword !== editUserData.confirmPassword) {
                              toast.error(t('admin.users.passwordMismatch'));
                              return;
                            }
                          }
                          // Remove confirmPassword from data sent to API
                          const { confirmPassword, ...dataToSend } = editUserData;
                          await axios.put(`${API}/admin/users/${selectedItem.id}`, dataToSend, { headers: getAuthHeaders() });
                          toast.success(editUserData.newPassword ? t('admin.users.updateWithPasswordSuccess') : t('admin.users.updateSuccess'));
                          fetchData();
                          setSelectedItem({...selectedItem, ...editUserData});
                          setDialogType('viewUser');
                          // Clear password fields and reset password change mode
                          setEditUserData(prev => ({...prev, newPassword: '', confirmPassword: ''}));
                          setShowPasswordChange(false);
                        } catch (error) {
                          toast.error(error.response?.data?.detail || t('admin.common.updateFailed'));
                        }
                      }}
                    >
                      <Save className="h-4 w-4 mr-1" /> {t('admin.common.save')}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add User Modal is now in UsersTab component */}

      {/* Video Management Dialog */}
      <Dialog open={dialogOpen && dialogType === 'manageVideos'} onOpenChange={(open) => {
        if (!open) {
          setDialogOpen(false);
          setCourseVideos([]);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5" />
              {t('admin.videos.management')}: {selectedItem?.name?.ja}
            </DialogTitle>
            <DialogDescription>
              {t('admin.videos.manageDescription')}
            </DialogDescription>
          </DialogHeader>

          {/* Language switcher tab (top of form) */}
          <div className="flex flex-wrap items-center gap-1 border-b pb-3" data-testid="video-lang-tabs">
            {COURSE_LANGS.map((lng) => (
              <button
                key={lng.code}
                type="button"
                onClick={() => setNewVideoLang(lng.code)}
                data-testid={`video-lang-tab-${lng.code}`}
                className={`px-3 py-1 rounded-md text-sm transition-colors flex items-center gap-1.5 ${newVideoLang === lng.code ? 'bg-[#00A651] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                <LangFlag cc={lng.cc} /> {lng.label}
              </button>
            ))}
            <button
              type="button"
              onClick={handleTranslateVideos}
              disabled={translatingVideos}
              data-testid="video-translate-all-btn"
              title={t('admin.videos.translateAllHint')}
              className="ml-auto px-3 py-1 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 disabled:opacity-60"
            >
              {translatingVideos
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('admin.videos.translating')}</>
                : <><Languages className="h-3.5 w-3.5" /> {t('admin.videos.translateAll')}</>}
            </button>
          </div>

          {/* Upload New Video */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              {t('admin.videos.uploadNew')}
              <span className="inline-flex items-center gap-1.5 text-sm font-normal text-[#00A651]">
                <LangFlag cc={COURSE_LANGS.find(l => l.code === newVideoLang)?.cc} />
                {COURSE_LANGS.find(l => l.code === newVideoLang)?.label}
              </span>
            </h4>
            <div className="space-y-3">
              <div>
                <Label>{t('admin.videos.title')} <span className="text-red-500">*</span></Label>
                <Input
                  value={newVideo.title}
                  onChange={(e) => setNewVideo({...newVideo, title: e.target.value})}
                  placeholder={t('admin.videos.titlePlaceholder')}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{t('admin.videos.descriptionOptional')}</Label>
                <Textarea
                  value={newVideo.description}
                  onChange={(e) => setNewVideo({...newVideo, description: e.target.value})}
                  placeholder={t('admin.videos.descPlaceholder')}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div>
                <Label>{t('admin.videos.requiredWatchPercent')}</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={newVideo.requiredWatchPercent}
                  onChange={(e) => setNewVideo({...newVideo, requiredWatchPercent: parseInt(e.target.value) || 90})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{t('admin.videos.file')} <span className="text-red-500">*</span></Label>
                <Input
                  ref={videoFileRef}
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">{t('admin.videos.supportedFormats')}</p>
              </div>
              
              {uploadingVideo && (
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-[#00A651] h-2.5 rounded-full transition-all" 
                    style={{ width: `${videoUploadProgress}%` }}
                  ></div>
                  <p className="text-xs text-center mt-1">{videoUploadProgress}% {t('admin.videos.uploading')}</p>
                </div>
              )}

              <Button 
                onClick={uploadVideo} 
                disabled={uploadingVideo}
                className="bg-[#00A651] hover:bg-[#008a43]"
              >
                {uploadingVideo ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('admin.videos.uploading')}</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" /> {t('admin.common.upload')}</>
                )}
              </Button>
            </div>
          </div>

          {/* Video List */}
          <div className="border rounded-lg p-4">
            <h4 className="font-semibold mb-3">{t('admin.videos.registeredVideos')} ({courseVideos.length})</h4>
            {courseVideos.length === 0 ? (
              <p className="text-gray-500 text-center py-4">{t('admin.videos.noVideos')}</p>
            ) : (
              <div className="space-y-2">
                {courseVideos.map((video, index) => (
                  <div key={video.id} className="p-3 bg-gray-50 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />
                        <div className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-medium">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          {editingVideoId === video.id ? (
                            <div className="space-y-2 pr-2">
                              <Input
                                value={videoEdit.title}
                                onChange={(e) => setVideoEdit({...videoEdit, title: e.target.value})}
                                placeholder={t('admin.videos.title')}
                                className="h-8 text-sm"
                                data-testid={`edit-video-title-${video.id}`}
                              />
                              <Input
                                value={videoEdit.description}
                                onChange={(e) => setVideoEdit({...videoEdit, description: e.target.value})}
                                placeholder={t('admin.videos.descriptionOptional')}
                                className="h-8 text-sm"
                                data-testid={`edit-video-desc-${video.id}`}
                              />
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">{t('admin.videos.requiredWatchPercent')}</span>
                                <Input
                                  type="number" min="0" max="100"
                                  value={videoEdit.requiredWatchPercent}
                                  onChange={(e) => setVideoEdit({...videoEdit, requiredWatchPercent: parseInt(e.target.value) || 0})}
                                  className="h-8 text-sm w-20"
                                  data-testid={`edit-video-percent-${video.id}`}
                                />
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="font-medium">{locVal(video.title, newVideoLang) || locVal(video.title, 'ja')}</p>
                              <p className="text-xs text-gray-500">
                                {locVal(video.description, newVideoLang) || t('admin.videos.noDescription')} | {t('admin.videos.requiredWatchPercent')}: {video.requiredWatchPercent}%
                              </p>
                              {video.quiz && (
                                <Badge 
                                  className="mt-1 bg-orange-100 text-orange-700 text-xs cursor-pointer hover:bg-orange-200 transition-colors"
                                  onClick={() => openQuizEditor(video)}
                                >
                                  <ClipboardCheck className="h-3 w-3 mr-1" />
                                  {t('admin.quiz.hasQuiz')} ({video.quiz.questions?.length || 0}{t('admin.common.items')})
                                </Badge>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {editingVideoId === video.id ? (
                          <>
                            <Button size="sm" className="bg-[#00A651] hover:bg-[#008a43]" onClick={() => updateVideoMeta(video.id)} data-testid={`save-video-${video.id}`}>
                              {t('admin.common.save')}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingVideoId(null)} data-testid={`cancel-video-${video.id}`}>
                              {t('admin.common.cancel')}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-blue-600 border-blue-200"
                              onClick={() => startEditVideo(video)}
                              title={t('admin.common.edit')}
                              data-testid={`edit-video-${video.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className={video.quiz ? "text-orange-600 border-orange-300 bg-orange-50" : "text-gray-600"}
                              onClick={() => openQuizEditor(video)}
                              title={t('admin.quiz.manageTitle')}
                            >
                              <ClipboardCheck className="h-4 w-4 mr-1" />
                              <span className="text-xs">{t('admin.quiz.quiz')}</span>
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-600"
                              onClick={() => deleteVideo(video.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Selected-language video file */}
                    <div className="border-t pt-3">
                      {(() => {
                        const lng = COURSE_LANGS.find(l => l.code === newVideoLang);
                        const lf = (video.languageFiles || {})[newVideoLang];
                        const isUploading = uploadingVideoLang === `${video.id}:${newVideoLang}`;
                        return (
                          <div
                            className={`flex items-center justify-between gap-2 px-3 py-2 rounded border text-sm ${lf ? 'bg-green-50 border-green-200' : 'bg-white border-dashed border-gray-300'}`}
                            data-testid={`video-lang-row-${video.id}-${newVideoLang}`}
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <LangFlag cc={lng?.cc} />
                              <span className="truncate">{lng?.label}{t('admin.videos.versionSuffix')}</span>
                              {lf ? (
                                <Badge variant="outline" className="text-[10px]">{(lf.fileSize / 1024 / 1024).toFixed(1)}MB</Badge>
                              ) : (
                                <span className="text-xs text-gray-400">{t('admin.videos.notUploaded')}</span>
                              )}
                            </span>
                            <span className="flex items-center gap-1 shrink-0">
                              {lf ? (
                                <>
                                  <Button variant="outline" size="sm" className="h-7 text-blue-600" disabled={isUploading} onClick={() => triggerVideoLangUpload(video.id, newVideoLang)} data-testid={`video-lang-replace-${video.id}-${newVideoLang}`}>
                                    {isUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Upload className="h-3 w-3 mr-1" />{t('admin.videos.replace')}</>}
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-600" disabled={isUploading} onClick={() => deleteVideoLang(video.id, newVideoLang)} data-testid={`video-lang-delete-${video.id}-${newVideoLang}`}>
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </>
                              ) : (
                                <Button variant="outline" size="sm" className="h-7 text-[#00A651]" disabled={isUploading} onClick={() => triggerVideoLangUpload(video.id, newVideoLang)} data-testid={`video-lang-upload-${video.id}-${newVideoLang}`}>
                                  {isUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Plus className="h-3 w-3 mr-1" />{t('admin.common.upload')}</>}
                                </Button>
                              )}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <input
            ref={videoLangFileRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
            className="hidden"
            onChange={handleVideoLangFileSelected}
            data-testid="video-lang-file-input"
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('admin.common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quiz/Test Management Dialog */}
      <Dialog open={quizDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setQuizDialogOpen(false);
          setSelectedVideoForQuiz(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-orange-500" />
              {t('admin.quiz.manageQuiz')}: {locVal(selectedVideoForQuiz?.title, quizLangTab) || locVal(selectedVideoForQuiz?.title, 'ja')}
            </DialogTitle>
            <DialogDescription>
              {t('admin.quiz.manageDescription')}
            </DialogDescription>
          </DialogHeader>

          {/* Language switcher tab (top of form) */}
          <div className="flex flex-wrap items-center gap-1 border-b pb-3" data-testid="quiz-lang-tabs">
            {COURSE_LANGS.map((lng) => (
              <button
                key={lng.code}
                type="button"
                onClick={() => setQuizLangTab(lng.code)}
                data-testid={`quiz-lang-tab-${lng.code}`}
                className={`px-3 py-1 rounded-md text-sm transition-colors flex items-center gap-1.5 ${quizLangTab === lng.code ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                <LangFlag cc={lng.cc} /> {lng.label}
              </button>
            ))}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleTranslateQuiz}
              disabled={translatingQuiz}
              className="ml-auto text-orange-600 border-orange-400 hover:bg-orange-500 hover:text-white h-7 text-xs"
              data-testid="quiz-translate-all-btn"
            >
              {translatingQuiz
                ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />{t('admin.quiz.translating')}</>
                : <><Languages className="h-3 w-3 mr-1" />{t('admin.quiz.translateAll')}</>}
            </Button>
          </div>

          {quizLoading ? (
            <div className="py-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-orange-500" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Quiz Settings */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label>{t('admin.quiz.title')} ({COURSE_LANGS.find(l => l.code === quizLangTab)?.label})</Label>
                  <Input
                    value={locVal(quizData.title, quizLangTab)}
                    onChange={(e) => setQuizData({...quizData, title: setLocVal(quizData.title, quizLangTab, e.target.value)})}
                    placeholder={t('admin.quiz.defaultTitle')}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{t('admin.courses.passingScore')}</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={quizData.passingScore}
                    onChange={(e) => setQuizData({...quizData, passingScore: parseInt(e.target.value) || 80})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{t('admin.quiz.timeLimit')}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={quizData.timeLimit || ''}
                    onChange={(e) => setQuizData({...quizData, timeLimit: e.target.value ? parseInt(e.target.value) : null})}
                    placeholder={t('admin.quiz.unlimited')}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Questions */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold">{t('admin.quiz.questionList')} ({quizData.questions.length})</h4>
                  <Button onClick={addQuestion} size="sm" className="bg-[#00A651] hover:bg-[#008a43]">
                    <HelpCircle className="h-4 w-4 mr-2" />
                    {t('admin.quiz.addQuestion')}
                  </Button>
                </div>

                {quizData.questions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                    <ClipboardCheck className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>{t('admin.quiz.noQuestions')}</p>
                    <p className="text-sm">{t('admin.quiz.clickToAddQuestion')}</p>
                  </div>
                ) : (
                  quizData.questions.map((question, qIndex) => (
                    <div key={question.id || qIndex} className="border rounded-lg p-4 bg-white">
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex items-center gap-2">
                          <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-sm font-medium">
                            {t('admin.quiz.questionNum')}{qIndex + 1}
                          </span>
                          <select
                            value={question.questionType}
                            onChange={(e) => updateQuestion(qIndex, 'questionType', e.target.value)}
                            className="text-sm border rounded px-2 py-1"
                          >
                            <option value="single">{t('admin.quiz.singleChoice')}</option>
                            <option value="multiple">{t('admin.quiz.multipleChoice')}</option>
                          </select>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600"
                          onClick={() => removeQuestion(qIndex)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="mb-4">
                        <Label>{t('admin.quiz.questionText')} ({COURSE_LANGS.find(l => l.code === quizLangTab)?.label})</Label>
                        <Textarea
                          value={locVal(question.questionText, quizLangTab)}
                          onChange={(e) => updateQuestion(qIndex, 'questionText', setLocVal(question.questionText, quizLangTab, e.target.value))}
                          placeholder={t('admin.quiz.enterQuestion')}
                          className="mt-1"
                          rows={2}
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm text-gray-600">
                            {t('admin.quiz.options')} ({question.questionType === 'single' ? t('admin.quiz.singleAnswer') : t('admin.quiz.multipleAnswers')})
                          </Label>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => addOption(qIndex)}
                            className="text-xs"
                          >
                            + {t('admin.quiz.addOption')}
                          </Button>
                        </div>
                        {question.options.map((option, oIndex) => (
                          <div key={oIndex} className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => toggleCorrectOption(qIndex, oIndex)}
                              className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                                option.isCorrect 
                                  ? 'border-green-500 bg-green-500 text-white' 
                                  : 'border-gray-300 hover:border-green-400'
                              }`}
                            >
                              {option.isCorrect && <CheckCircle2 className="h-4 w-4" />}
                            </button>
                            <Input
                              value={locVal(option.text, quizLangTab)}
                              onChange={(e) => updateQuestionOption(qIndex, oIndex, 'text', setLocVal(option.text, quizLangTab, e.target.value))}
                              placeholder={`${t('admin.quiz.option')} ${oIndex + 1}`}
                              className="flex-1"
                            />
                            {question.options.length > 2 && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-500"
                                onClick={() => removeOption(qIndex, oIndex)}
                              >
                                <CircleX className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <p className="text-xs text-gray-500 mt-1">
                          {t('admin.quiz.correctAnswerHint')}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex justify-between">
            <div>
              {selectedVideoForQuiz?.quiz && (
                <Button 
                  variant="outline" 
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={deleteQuiz}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('admin.quiz.deleteQuiz')}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setQuizDialogOpen(false)}>
                {t('admin.common.cancel')}
              </Button>
              <Button 
                onClick={saveQuiz}
                disabled={savingQuiz}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {savingQuiz ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('admin.common.saving')}</>
                ) : (
                  t('admin.common.save')
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Certificate Issue Dialog with Certificate Number */}
      <Dialog open={certIssueDialogOpen} onOpenChange={setCertIssueDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {certIssueRequest?.status === 'completed' ? t('admin.certificates.editInfo') : t('admin.certificates.issueComplete')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4 max-h-[65vh] overflow-y-auto">
            {/* User Assignment Section */}
            <div className="border rounded-lg p-3 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-gray-700">{t('admin.certificates.userLink')}</Label>
                {certIssueRequest?.userId && users.find(u => u.id === certIssueRequest.userId) ? (
                  <Badge className="bg-green-100 text-green-700 text-xs">{t('admin.certificates.linked')}</Badge>
                ) : (
                  <Badge className="bg-gray-100 text-gray-500 text-xs">{t('admin.certificates.unlinked')}</Badge>
                )}
              </div>
              {certIssueRequest?.userId && users.find(u => u.id === certIssueRequest.userId) ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#00A651] flex items-center justify-center text-white text-xs font-bold">
                      {(users.find(u => u.id === certIssueRequest.userId)?.name || '?')[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{users.find(u => u.id === certIssueRequest.userId)?.name}</p>
                      <p className="text-xs text-gray-500">{users.find(u => u.id === certIssueRequest.userId)?.email}</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50 text-xs"
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/admin/certificate-requests/${certIssueRequest.id}/unassign-user`, {}, { headers: getAuthHeaders() });
                        setCertIssueRequest(r => ({...r, userId: ''}));
                        toast.success(t('admin.certificates.unlinkSuccess'));
                      } catch { toast.error('解除に失敗しました'); }
                    }}>
                    {t('admin.certificates.unlink')}
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="flex gap-2">
                    <Input
                      placeholder={t("admin.certificates.searchUser")}
                      value={certUserSearchQuery || ''}
                      onChange={(e) => setCertUserSearchQuery(e.target.value)}
                      className="text-xs"
                    />
                    <Button variant="outline" size="sm" className="text-xs shrink-0"
                      onClick={() => setCertUserSearchResults(
                        users.filter(u => u.role !== 'admin' && (
                          (u.name || '').toLowerCase().includes((certUserSearchQuery || '').toLowerCase()) ||
                          (u.email || '').toLowerCase().includes((certUserSearchQuery || '').toLowerCase()) ||
                          (u.phone || '').includes(certUserSearchQuery || '')
                        )).slice(0, 5)
                      )}>
                      検索
                    </Button>
                  </div>
                  {certUserSearchResults && certUserSearchResults.length > 0 && (
                    <div className="mt-2 border rounded max-h-32 overflow-y-auto">
                      {certUserSearchResults.map(u => (
                        <button key={u.id} type="button"
                          className="w-full text-left px-3 py-2 hover:bg-green-50 border-b last:border-b-0 flex items-center justify-between"
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/admin/certificate-requests/${certIssueRequest.id}/assign-user`, { userId: u.id }, { headers: getAuthHeaders() });
                              setCertIssueRequest(r => ({...r, userId: u.id}));
                              setCertUserSearchResults(null);
                              setCertUserSearchQuery('');
                              toast.success(`${u.name} ${t('admin.certificates.linkSuccess')}`);
                            } catch { toast.error(t('admin.certificates.linkFailed')); }
                          }}>
                          <div>
                            <p className="text-sm font-medium">{u.name}</p>
                            <p className="text-xs text-gray-500">{u.email}</p>
                          </div>
                          <span className="text-xs text-[#00A651]">{t('admin.certificates.select')}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {certUserSearchResults && certUserSearchResults.length === 0 && (
                    <p className="text-xs text-gray-400 mt-2">該当するユーザーが見つかりません</p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">{t('admin.form.user')} <span className="text-red-500">*</span></Label>
                <Input value={certIssueRequest?.userName || ''} onChange={e => setCertIssueRequest(r => ({...r, userName: e.target.value}))} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">{t('admin.common.email')}</Label>
                <Input value={certIssueRequest?.userEmail || ''} onChange={e => setCertIssueRequest(r => ({...r, userEmail: e.target.value}))} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">生年月日</Label>
                <Input type="date" value={certIssueRequest?.userBirthDate || ''} onChange={e => setCertIssueRequest(r => ({...r, userBirthDate: e.target.value}))} className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">住所</Label>
                <Input value={certIssueRequest?.userAddress || ''} onChange={e => setCertIssueRequest(r => ({...r, userAddress: e.target.value}))} className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">顔写真</Label>
                <div className="flex items-center gap-3 mt-1">
                  {(() => {
                    const photo = certIssueRequest?.userPhoto || 
                      (certIssueRequest?.userId && users.find(u => u.id === certIssueRequest.userId)?.profileImage) ||
                      (certIssueRequest?.userId && users.find(u => u.id === certIssueRequest.userId)?.photoFront) ||
                      (!certIssueRequest?.userId && certIssueRequest?.userName && users.find(u => u.name === certIssueRequest.userName)?.profileImage) || '';
                    return photo ? (
                      (() => { const purl = photo.startsWith('http') ? photo : photo.startsWith('/api/') ? `${BACKEND_URL}${photo}` : `${BACKEND_URL}/api/uploads/${photo}`; return (
                      <img src={purl} alt="顔写真" onClick={() => setLightboxImg(purl)} className="w-14 h-14 rounded-full object-cover border-2 border-[#00A651] cursor-pointer hover:opacity-80 transition-opacity" data-testid="cert-user-photo" />
                      ); })()
                    ) : null;
                  })()}
                  <Input type="file" accept="image/*" className="text-xs" data-testid="cert-face-photo-input" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const formData = new FormData();
                    formData.append('file', file);
                    try {
                      const res = await axios.post(`${API}/upload`, formData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                      setCertIssueRequest(r => ({...r, userPhoto: res.data?.url || res.data?.path || ''}));
                      toast.success(t('admin.certificates.uploadSuccess'));
                    } catch { toast.error(t('admin.common.uploadFailed')); }
                  }} />
                </div>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">カード証明書写真</Label>
                <div className="flex items-center gap-3 mt-1">
                  {certIssueRequest?.userCardPhoto ? (
                    (() => { const cp = certIssueRequest.userCardPhoto; const curl = cp.startsWith('http') ? cp : cp.startsWith('/api/') ? `${BACKEND_URL}${cp}` : `${BACKEND_URL}/api/uploads/${cp}`; return (
                    <img src={curl} alt="カード証明書写真" onClick={() => setLightboxImg(curl)} className="w-24 h-14 rounded object-cover border-2 border-purple-400 cursor-pointer hover:opacity-80 transition-opacity" data-testid="cert-card-photo" />
                    ); })()
                  ) : null}
                  <Input type="file" accept="image/*" className="text-xs" data-testid="cert-card-photo-input" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const formData = new FormData();
                    formData.append('file', file);
                    try {
                      const res = await axios.post(`${API}/upload`, formData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                      setCertIssueRequest(r => ({...r, userCardPhoto: res.data?.url || res.data?.path || ''}));
                      toast.success(t('admin.certificates.uploadSuccess'));
                    } catch { toast.error(t('admin.common.uploadFailed')); }
                  }} />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">{t('admin.certificates.courseName')}</Label>
              <Input value={certIssueRequest?.courseName || ''} onChange={e => setCertIssueRequest(r => ({...r, courseName: e.target.value}))} className="mt-1" />
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-2">{t('admin.certificates.certType')}</p>
              <div className="flex flex-wrap gap-1">
                {(certIssueRequest?.types || (certIssueRequest?.type ? [certIssueRequest.type] : [])).map((ct, idx) => (
                  <Badge key={idx} className={ct === 'card' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                    {ct === 'card' ? t('admin.certificates.card') : t('admin.certificates.digital')}
                  </Badge>
                ))}
              </div>
            </div>
            {certIssueRequest?.existingCertRef && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3">
                <p className="text-sm text-green-700">
                  <span className="font-medium">{t('admin.certificates.duplicateWarning')}</span><br />
                  {t('admin.certificates.autoFilled')}
                </p>
              </div>
            )}
            {/* Status */}
            <div>
              <Label className="text-xs">{t('admin.common.status')}</Label>
              <Select value={certIssueRequest?.status || 'pending'} onValueChange={(v) => setCertIssueRequest(r => ({...r, status: v}))}>
                <SelectTrigger className={`mt-1 font-semibold text-sm ${
                  certIssueRequest?.status === 'passed' ? 'bg-green-100 border-green-400 text-green-800' :
                  certIssueRequest?.status === 'failed' ? 'bg-red-100 border-red-400 text-red-800' :
                  certIssueRequest?.status === 'cancelled' ? 'bg-gray-100 border-gray-400 text-gray-800' :
                  certIssueRequest?.status === 'processing' ? 'bg-blue-100 border-blue-400 text-blue-800' :
                  'bg-yellow-100 border-yellow-400 text-yellow-800'
                }`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">{t('admin.certificates.pending')}</SelectItem>
                  <SelectItem value="processing">{t('admin.certificates.processing')}</SelectItem>
                  <SelectItem value="passed">{t('admin.certificates.passed')}</SelectItem>
                  <SelectItem value="failed">{t('admin.certificates.failed')}</SelectItem>
                  <SelectItem value="cancelled">{t('admin.common.cancelled')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-gray-500 mb-2 block">{t('admin.certificates.certNumber')} <span className="text-red-500">*</span></label>
              <div className={`flex gap-1 ${certIssueRequest?.status !== 'passed' ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex-shrink-0 bg-gray-100 border border-r-0 rounded-l-md px-3 py-2 text-sm text-gray-600">
                  {courses.find(c => c.id === certIssueRequest?.courseId)?.registrationNumber || courses.find(c => c.name?.ja === certIssueRequest?.courseName)?.registrationNumber || t('admin.certificates.noRegNumber')}
                </div>
                <Input
                  value={certNumberSuffix}
                  onChange={(e) => {
                    const suffix = e.target.value;
                    setCertNumberSuffix(suffix);
                    const prefix = courses.find(c => c.id === certIssueRequest?.courseId)?.registrationNumber || courses.find(c => c.name?.ja === certIssueRequest?.courseName)?.registrationNumber || '';
                    setCertNumber(prefix ? `${prefix}-${suffix}` : suffix);
                  }}
                  className="rounded-l-none"
                  placeholder={t('admin.certificates.enterSequence')}
                  data-testid="cert-number-input"
                  disabled={certIssueRequest?.status !== 'passed'}
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-500 mb-2 block">{t('admin.certificates.completionDate')} <span className="text-red-500">*</span></label>
              <Input
                type="date"
                value={certCompletionDate}
                onChange={(e) => setCertCompletionDate(e.target.value)}
                data-testid="cert-completion-date-input"
                disabled={certIssueRequest?.status !== 'passed'}
                className={certIssueRequest?.status !== 'passed' ? 'opacity-50' : ''}
              />
            </div>

            {/* Linked Training Guide Section */}
            {certLinkedGuide && (
              <div className="border rounded-lg p-3 bg-blue-50 border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-blue-800">{t('admin.certificates.trainingGuide')}</p>
                  <Badge className={certLinkedGuide.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                    {certLinkedGuide.status === 'sent' ? t('admin.certificates.sent') : t('admin.certificates.unsent')}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-gray-500">会場:</span> <span className="font-medium">{certLinkedGuide.venue || '-'}</span></div>
                  <div><span className="text-gray-500">集合日時:</span> <span className="font-medium">{certLinkedGuide.meetingTime || '-'}</span></div>
                  <div className="col-span-2"><span className="text-gray-500">持ち物:</span> <span className="font-medium">{certLinkedGuide.itemsToBring?.substring(0, 50) || '-'}{certLinkedGuide.itemsToBring?.length > 50 ? '...' : ''}</span></div>
                </div>
                <div className="flex gap-1 mt-2">
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => {
                    setGuideCreateForm({
                      id: certLinkedGuide.id,
                      applicationId: certLinkedGuide.applicationId || '',
                      templateId: '',
                      courseName: certLinkedGuide.courseName || '',
                      schedule: certLinkedGuide.schedule || '',
                      userName: certLinkedGuide.userName || '',
                      userEmail: certLinkedGuide.userEmail || '',
                      venue: certLinkedGuide.venue || '',
                      venueAddress: certLinkedGuide.venueAddress || '',
                      meetingTime: certLinkedGuide.meetingTime || '',
                      itemsToBring: certLinkedGuide.itemsToBring || '',
                      notes: certLinkedGuide.notes || '',
                      mapUrl: certLinkedGuide.mapUrl || '',
                      additionalInfo: certLinkedGuide.additionalInfo || ''
                    });
                    setCertIssueDialogOpen(false);
                    setGuideCreateDialogOpen(true);
                  }}>
                    <Edit className="h-3 w-3 mr-1" />{t('admin.certificates.edit')}
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => window.open(`${API}/admin/training-guides/${certLinkedGuide.id}/pdf`, '_blank')}>
                    <FileText className="h-3 w-3 mr-1" />PDF
                  </Button>
                </div>
              </div>
            )}
            {!certLinkedGuide && certIssueRequest?.applicationId && (
              <div className="border rounded-lg p-3 bg-gray-50 text-center">
                <p className="text-xs text-gray-400 mb-2">{t('admin.certificates.guideNotCreated')}</p>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => {
                  const courseName = certIssueRequest?.courseName || '';
                  const courseNameOnly = courseName.replace(/【[^】]*】/, '').replace(/\s*\(.*\)$/, '').trim();
                  const matchTemplate = findGuideTemplate(courseNameOnly, getAppCourseOption(certIssueRequest?.applicationId));
                  const dateMatch = courseName.match(/\((\d{4}-\d{2}-\d{2})/);
                  const startDate = dateMatch?.[1] || '';
                  const templateTime = (matchTemplate?.meetingTime || '').replace(/^\d{4}-\d{2}-\d{2}\s*/, '').trim() || '08:20';
                  const meetingTime = computeGuideMeetingTime(matchTemplate?.timetable, startDate, templateTime);
                  const localCourse = courses.find(c => c.name?.ja === courseNameOnly);
                  const localCourseName = localCourse?.name?.[language] || courseNameOnly;
                  setGuideCreateForm({
                    applicationId: certIssueRequest?.applicationId || '',
                    templateId: matchTemplate?.id || '',
                    courseName: localCourseName + (courseName.match(/\(([^)]+)\)/) ? ` (${courseName.match(/\(([^)]+)\)/)[1]})` : ''),
                    schedule: courseName.match(/\(([^)]+)\)/)?.[1] || '',
                    userName: getGuideUserName(certIssueRequest?.applicationId, certIssueRequest?.userName),
                    courseOption: getAppCourseOption(certIssueRequest?.applicationId),
                    userEmail: certIssueRequest?.userEmail || '',
                    venue: matchTemplate?.venue || '',
                    venueAddress: matchTemplate?.venueAddress || '',
                    meetingTime: meetingTime,
                    itemsToBring: matchTemplate?.itemsToBring || '',
                    notes: matchTemplate?.notes || '',
                    mapUrl: matchTemplate?.mapUrl || '',
                    additionalInfo: matchTemplate?.additionalInfo || '',
                    sendEmail: true
                  });
                  setCertIssueDialogOpen(false);
                  setGuideCreateDialogOpen(true);
                }}>
                  <Plus className="h-3 w-3 mr-1" />{t('admin.certificates.createGuide')}
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCertIssueDialogOpen(false)}>
              {t('admin.common.cancel')}
            </Button>
            <Button
              className="bg-[#00A651] hover:bg-[#008a43]"
              disabled={certIssueRequest?.status === 'passed' && (!certNumberSuffix.trim() || !certCompletionDate)}
              onClick={async () => {
                try {
                  const prefix = courses.find(c => c.id === certIssueRequest?.courseId)?.registrationNumber || courses.find(c => c.name?.ja === certIssueRequest?.courseName)?.registrationNumber || '';
                  const finalCertNumber = certIssueRequest?.status === 'passed' ? (prefix ? `${prefix}-${certNumberSuffix.trim()}` : certNumberSuffix.trim()) : (certIssueRequest?.certificateNumber || '');
                  if (finalCertNumber && certificateRequests.some(r => r.id !== certIssueRequest?.id && r.certificateNumber === finalCertNumber)) {
                    toast.error(`この修了証番号（${finalCertNumber}）は既に登録されています`);
                    return;
                  }
                  await axios.put(`${API}/admin/certificate-requests/${certIssueRequest?.id}`, 
                    { 
                      status: certIssueRequest?.status === 'passed' ? 'completed' : certIssueRequest?.status, 
                      certificateNumber: finalCertNumber,
                      completionDate: certIssueRequest?.status === 'passed' ? certCompletionDate : (certIssueRequest?.completionDate || ''),
                      userName: certIssueRequest?.userName || '',
                      userEmail: certIssueRequest?.userEmail || '',
                      userBirthDate: certIssueRequest?.userBirthDate || '',
                      userAddress: certIssueRequest?.userAddress || '',
                      userPhoto: certIssueRequest?.userPhoto || '',
                      userCardPhoto: certIssueRequest?.userCardPhoto || '',
                      courseName: certIssueRequest?.courseName || ''
                    },
                    { headers: getAuthHeaders() }
                  );
                  // 申込ステータスも連動更新
                  const appStatusMap = { passed: 'completed_passed', failed: 'completed_failed', cancelled: 'cancelled' };
                  const newAppStatus = appStatusMap[certIssueRequest.status];
                  if (newAppStatus) {
                    // 同じユーザー名の申込を全て検索して更新
                    const matchApps = applications.filter(a => a.name === certIssueRequest?.userName);
                    for (const app of matchApps) {
                      try {
                        await axios.put(`${API}/applications/${app.id}`, 
                          { status: newAppStatus }, { headers: getAuthHeaders() });
                      } catch (e) { /* skip */ }
                    }
                  }
                  toast.success(certIssueRequest?.status === 'completed' ? t('admin.common.updated') : t('admin.certificates.issueCompleted'));
                  setCertIssueDialogOpen(false);
                  setCertIssueRequest(null);
                  setCertNumber('');
                  setCertNumberSuffix('');
                  setCertCompletionDate('');
                  fetchData();
                } catch (error) {
                  toast.error(error.response?.data?.detail || t('admin.common.updateFailed'));
                }
              }}
            >
              {certIssueRequest?.status === 'completed' || certIssueRequest?.status === 'passed' ? t('admin.common.save') : t('admin.common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Certificate Dialog */}
      <Dialog open={showNewCertDialog} onOpenChange={setShowNewCertDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin.certificates.addNewIssued')}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Label className="text-sm">{t('admin.form.user')} <span className="text-red-500">*</span></Label>
                <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                  <input type="checkbox" checked={newCertData.manualEntry} onChange={e => setNewCertData({...newCertData, manualEntry: e.target.checked, userId: ''})} className="rounded" />
                  未登録ユーザー
                </label>
              </div>
              {!newCertData.manualEntry ? (
                <select
                  value={newCertData.userId}
                  onChange={(e) => setNewCertData({...newCertData, userId: e.target.value})}
                  className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">{t('admin.form.selectUser')}</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                  ))}
                </select>
              ) : (
                <div className="space-y-2">
                  <Input value={newCertData.manualName} onChange={e => setNewCertData({...newCertData, manualName: e.target.value})} placeholder={t('admin.common.name')} />
                  <Input value={newCertData.manualEmail} onChange={e => setNewCertData({...newCertData, manualEmail: e.target.value})} placeholder={t('admin.common.email')} />
                  <Input type="date" value={newCertData.manualBirthDate || ''} onChange={e => setNewCertData({...newCertData, manualBirthDate: e.target.value})} placeholder="生年月日" />
                  <p className="text-[10px] text-gray-400 -mt-1">生年月日</p>
                  <Input value={newCertData.manualAddress || ''} onChange={e => setNewCertData({...newCertData, manualAddress: e.target.value})} placeholder="住所" />
                  <div>
                    <p className="text-xs text-gray-500 mb-1">顔写真</p>
                    <Input type="file" accept="image/*" data-testid="newcert-face-photo-input" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const res = await axios.post(`${API}/upload`, formData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                        setNewCertData(d => ({ ...d, manualPhoto: res.data?.url || res.data?.path || '' }));
                        toast.success(t('admin.certificates.uploadSuccess'));
                      } catch { toast.error(t('admin.common.uploadFailed')); }
                    }} className="text-xs" />
                    {newCertData.manualPhoto && <img src={newCertData.manualPhoto.startsWith('http') ? newCertData.manualPhoto : newCertData.manualPhoto.startsWith('/api/') ? `${BACKEND_URL}${newCertData.manualPhoto}` : `${BACKEND_URL}/api/uploads/${newCertData.manualPhoto}`} alt="顔写真" className="mt-1 w-16 h-20 object-cover rounded border" />}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">カード証明書写真</p>
                    <Input type="file" accept="image/*" data-testid="newcert-card-photo-input" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const res = await axios.post(`${API}/upload`, formData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                        setNewCertData(d => ({ ...d, manualCardPhoto: res.data?.url || res.data?.path || '' }));
                        toast.success(t('admin.certificates.uploadSuccess'));
                      } catch { toast.error(t('admin.common.uploadFailed')); }
                    }} className="text-xs" />
                    {newCertData.manualCardPhoto && <img src={newCertData.manualCardPhoto.startsWith('http') ? newCertData.manualCardPhoto : newCertData.manualCardPhoto.startsWith('/api/') ? `${BACKEND_URL}${newCertData.manualCardPhoto}` : `${BACKEND_URL}/api/uploads/${newCertData.manualCardPhoto}`} alt="カード証明書写真" className="mt-1 w-24 h-14 object-cover rounded border" />}
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label className="text-sm">{t('admin.form.courseName')} <span className="text-red-500">*</span></Label>
              <select
                value={newCertData.courseId}
                onChange={(e) => setNewCertData({...newCertData, courseId: e.target.value})}
                className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
              >
                <option value="">{t('admin.form.selectCourse')}</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>{course.name?.[language] || course.name?.ja || course.id}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-sm">{t('admin.certificates.certType')} <span className="text-red-500">*</span></Label>
              <select
                value={newCertData.type}
                onChange={(e) => setNewCertData({...newCertData, type: e.target.value})}
                className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
              >
                <option value="digital">{t('admin.certificates.digital')}</option>
                <option value="card">{t('admin.certificates.card')}</option>
              </select>
            </div>
            <div>
              <Label className="text-sm">{t('admin.certificates.certNumber')} <span className="text-red-500">*</span></Label>
              <div className="flex gap-1 mt-1">
                <div className="flex-shrink-0 bg-gray-100 border border-r-0 rounded-l-md px-3 py-2 text-sm text-gray-600">
                  {courses.find(c => c.id === newCertData.courseId)?.registrationNumber || t('admin.form.selectCourse')}
                </div>
                <Input
                  value={newCertData.certificateNumberSuffix || ''}
                  onChange={(e) => {
                    const prefix = courses.find(c => c.id === newCertData.courseId)?.registrationNumber || '';
                    const suffix = e.target.value;
                    setNewCertData({
                      ...newCertData,
                      certificateNumberSuffix: suffix,
                      certificateNumber: prefix ? `${prefix}-${suffix}` : suffix
                    });
                  }}
                  className="rounded-l-none"
                  placeholder={t('admin.certificates.enterSequence')}
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">{t('admin.certificates.completionDate')} <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={newCertData.completionDate}
                onChange={(e) => setNewCertData({...newCertData, completionDate: e.target.value})}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCertDialog(false)}>
              {t('admin.common.cancel')}
            </Button>
            <Button
              className="bg-[#00A651] hover:bg-[#008a43]"
              disabled={(!newCertData.userId && !newCertData.manualName) || !newCertData.courseId || !newCertData.certificateNumber || !newCertData.completionDate}
              onClick={async () => {
                try {
                  const selectedUser = newCertData.manualEntry ? null : users.find(u => u.id === newCertData.userId);
                  const selectedCourse = courses.find(c => c.id === newCertData.courseId);
                  if (newCertData.certificateNumber && certificateRequests.some(r => r.certificateNumber === newCertData.certificateNumber)) {
                    toast.error(`この修了証番号（${newCertData.certificateNumber}）は既に登録されています`);
                    return;
                  }
                  
                  await axios.post(`${API}/admin/certificate-requests/create`, {
                    userId: newCertData.manualEntry ? '' : newCertData.userId,
                    userName: newCertData.manualEntry ? newCertData.manualName : selectedUser?.name,
                    userEmail: newCertData.manualEntry ? newCertData.manualEmail : selectedUser?.email,
                    userBirthDate: newCertData.manualEntry ? newCertData.manualBirthDate || '' : selectedUser?.birthDate || '',
                    userAddress: newCertData.manualEntry ? newCertData.manualAddress || '' : selectedUser?.address || '',
                    userPhoto: newCertData.manualEntry ? newCertData.manualPhoto || '' : selectedUser?.photoFront || '',
                    userCardPhoto: newCertData.manualEntry ? newCertData.manualCardPhoto || '' : '',
                    courseId: newCertData.courseId,
                    courseName: selectedCourse?.name?.[language] || selectedCourse?.name?.ja || selectedCourse?.id,
                    type: newCertData.type,
                    status: 'completed',
                    certificateNumber: newCertData.certificateNumber,
                    completionDate: newCertData.completionDate
                  }, { headers: getAuthHeaders() });
                  
                  toast.success(t('admin.certificates.addIssuedSuccess'));
                  setShowNewCertDialog(false);
                  fetchData();
                } catch (error) {
                  toast.error(error.response?.data?.detail || t('admin.common.addFailed'));
                }
              }}
            >
              {t('admin.common.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 未紐付け修了証の一括リンク */}
      <UnlinkedCertsDialog
        open={unlinkedCertsOpen}
        onOpenChange={setUnlinkedCertsOpen}
        authHeaders={getAuthHeaders}
        onChanged={fetchData}
      />

      {/* 重複アカウント整理 */}
      <DuplicateUsersDialog
        open={duplicateUsersOpen}
        onOpenChange={setDuplicateUsersOpen}
        authHeaders={getAuthHeaders}
        onChanged={fetchData}
      />


      {/* Invoice Create Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingInvoice ? t('admin.invoices.editTitle') : t('admin.invoices.createTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <div className="grid grid-cols-2 gap-1">
              <div>
                <Label className="text-xs">{t('admin.common.name')}</Label>
                <Input value={invoiceFormData.userName} onChange={(e) => setInvoiceFormData({...invoiceFormData, userName: e.target.value})} className="mt-0.5" />
              </div>
              <div>
                <Label className="text-xs">{t('admin.form.email')}</Label>
                <Input value={invoiceFormData.userEmail} onChange={(e) => setInvoiceFormData({...invoiceFormData, userEmail: e.target.value})} className="mt-0.5" />
              </div>
              <div>
                <Label className="text-xs">{t('admin.form.phone')}</Label>
                <Input value={invoiceFormData.userPhone || ''} onChange={(e) => setInvoiceFormData({...invoiceFormData, userPhone: e.target.value})} className="mt-0.5" />
              </div>
              <div>
                <Label className="text-xs">{t('admin.form.company')}</Label>
                <Input value={invoiceFormData.companyName} onChange={(e) => setInvoiceFormData({...invoiceFormData, companyName: e.target.value})} className="mt-0.5" />
              </div>
              <div className="col-span-2 rounded-md border border-gray-200 bg-gray-50 p-2 space-y-2" data-testid="invoice-pdf-display-options">
                <div>
                  <Label className="text-xs">請求書の宛先（PDF）</Label>
                  <select
                    value={invoiceFormData.recipientDisplay || 'both'}
                    onChange={(e) => setInvoiceFormData({ ...invoiceFormData, recipientDisplay: e.target.value })}
                    className="mt-0.5 w-full border rounded-md px-2 py-1.5 text-sm bg-white"
                    data-testid="invoice-recipient-display"
                  >
                    <option value="both">会社名＋氏名</option>
                    <option value="company" disabled={!invoiceFormData.companyName}>会社名のみ</option>
                    <option value="name">氏名のみ</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-0.5" data-testid="invoice-recipient-preview">
                    表示：{(() => { const co = invoiceFormData.companyName || ''; const rn = invoiceFormData.userName || ''; const rd = invoiceFormData.recipientDisplay || 'both'; return (rd === 'company' && co) ? co : (rd === 'name' && rn) ? rn : (co ? `${co}  ${rn}` : rn); })()}　様
                  </p>
                </div>
                <label className="flex items-center justify-between gap-2 text-sm cursor-pointer">
                  <span>
                    PDFに受講者名を表示する
                    {(() => { const names = (applications.find(a => a.id === invoiceFormData.applicationId)?.participants || []).map(p => p?.name).filter(Boolean); return names.length ? <span className="block text-xs text-gray-500">受講者: {names.join('、')}</span> : null; })()}
                  </span>
                  <Switch checked={!invoiceFormData.hideParticipants} onCheckedChange={(v) => setInvoiceFormData({ ...invoiceFormData, hideParticipants: !v })} data-testid="invoice-show-participants-switch" />
                </label>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">{t('admin.form.address')}</Label>
                <Input value={invoiceFormData.userAddress || ''} onChange={(e) => setInvoiceFormData({...invoiceFormData, userAddress: e.target.value})} className="mt-0.5" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">{t('admin.certificates.courseName')}</Label>
                <select 
                  value={invoiceFormData.courseName} 
                  onChange={(e) => setInvoiceFormData({...invoiceFormData, courseName: e.target.value})} 
                  className="mt-0.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="invoice-course-select"
                >
                  <option value="">{t("admin.form.selectPlease")}</option>
                  {[...schedules].sort((a, b) => (b.startDate || '').localeCompare(a.startDate || '')).map(s => {
                    const statusLabel = s.status === 'full' ? `【${t('admin.schedules.full')}】` : s.status === 'closed' ? `【${t('admin.schedules.closed')}】` : s.status === 'in_progress' ? `【${t('admin.schedules.inProgress')}】` : '';
                    const localName = (() => { const c = courses.find(c => c.id === s.courseId) || courses.find(c => c.name?.ja === s.courseName); return c?.name?.[language] || c?.name?.ja || s.courseName; })();
                    const label = `${statusLabel}${localName} (${s.startDate}${s.endDate ? ` 〜 ${s.endDate}` : ''})`;
                    return (
                      <option key={s.id} value={label}>{label}</option>
                    );
                  })}
                </select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-bold text-red-600">{t('admin.invoices.dueDate')} <span className="text-red-500">*</span></Label>
                <Input type="date" value={invoiceFormData.dueDate || ''} onChange={(e) => setInvoiceFormData({...invoiceFormData, dueDate: e.target.value})} 
                  className={`mt-0.5 border-2 ${invoiceFormData.dueDate ? 'border-green-400' : 'border-red-400 bg-red-50'}`}
                  data-testid="invoice-due-date" />
                {!invoiceFormData.dueDate && <p className="text-[11px] text-red-500 mt-1">※ {t('admin.invoices.dueDate')}</p>}
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium">{t('admin.invoices.items')}</Label>
              <div className="space-y-1 mt-1">
                {invoiceFormData.items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      placeholder={t('admin.invoices.itemName')}
                      value={item.name}
                      onChange={(e) => {
                        const newItems = [...invoiceFormData.items];
                        newItems[idx].name = e.target.value;
                        setInvoiceFormData({...invoiceFormData, items: newItems});
                      }}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder={t('admin.invoices.amount')}
                      value={item.amount || ''}
                      onChange={(e) => {
                        const newItems = [...invoiceFormData.items];
                        newItems[idx].amount = parseInt(e.target.value) || 0;
                        const total = newItems.reduce((s, i) => s + (i.amount || 0), 0);
                        setInvoiceFormData({...invoiceFormData, items: newItems, totalAmount: total});
                      }}
                      className="w-32"
                    />
                    <span className="text-sm text-gray-500">{t('admin.invoices.yen')}</span>
                    {invoiceFormData.items.length > 1 && (
                      <Button size="sm" variant="ghost" className="text-red-500 px-2" onClick={() => {
                        const newItems = invoiceFormData.items.filter((_, i) => i !== idx);
                        const total = newItems.reduce((s, i) => s + (i.amount || 0), 0);
                        setInvoiceFormData({...invoiceFormData, items: newItems, totalAmount: total});
                      }}>
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => {
                  setInvoiceFormData({...invoiceFormData, items: [...invoiceFormData.items, { name: '', amount: 0 }]});
                }}>
                  <Plus className="h-3 w-3 mr-1" /> {t('admin.invoices.addItem')}
                </Button>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
              <span className="font-medium">{t('admin.invoices.totalAmount')}</span>
              <span className="text-xl font-bold text-[#00A651]">¥{(invoiceFormData.totalAmount || 0).toLocaleString()}</span>
            </div>

            <div>
              <Label className="text-xs">{t('admin.form.remarks')}</Label>
              <Textarea value={invoiceFormData.note} onChange={(e) => setInvoiceFormData({...invoiceFormData, note: e.target.value})} rows={2} className="mt-1" placeholder={t('admin.form.enterRemarks')} />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sendEmail"
                checked={invoiceFormData.sendEmail}
                onChange={(e) => setInvoiceFormData({...invoiceFormData, sendEmail: e.target.checked})}
                className="rounded"
              />
              <label htmlFor="sendEmail" className="text-sm text-gray-700">{t('admin.invoices.sendEmailNotification')}</label>
            </div>
            {!editingInvoice && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sendWhatsApp"
                  checked={invoiceFormData.sendWhatsApp !== false}
                  onChange={(e) => setInvoiceFormData({...invoiceFormData, sendWhatsApp: e.target.checked})}
                  className="rounded"
                  data-testid="invoice-send-whatsapp-check"
                />
                <label htmlFor="sendWhatsApp" className="text-sm text-gray-700">WhatsAppで請求案内・決済リンクを送信する（電話番号の登録がある場合）</label>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sendGuide"
                checked={!!invoiceFormData.sendGuide}
                onChange={(e) => setInvoiceFormData({...invoiceFormData, sendGuide: e.target.checked})}
                className="rounded"
                data-testid="invoice-send-guide-check"
              />
              <label htmlFor="sendGuide" className="text-sm text-gray-700">{t('admin.invoices.alsoSendGuide')}</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setInvoiceDialogOpen(false); setEditingInvoice(null); }}>{t('admin.common.cancel')}</Button>
            <Button
              className="bg-[#00A651] hover:bg-[#008a43]"
              data-testid="submit-invoice"
              disabled={!invoiceFormData.totalAmount}
              onClick={async () => {
                try {
                  if (editingInvoice) {
                    await axios.put(`${API}/admin/invoices/${editingInvoice.id}`, invoiceFormData, { headers: getAuthHeaders() });
                    toast.success(t('admin.invoices.updateSuccess'));
                  } else {
                    await axios.post(`${API}/admin/invoices`, invoiceFormData, { headers: getAuthHeaders() });
                    toast.success(t('admin.invoices.createSuccess'));
                  }
                  setInvoiceDialogOpen(false);
                  setEditingInvoice(null);
                  fetchData();
                  setActiveTab('invoices');
                  // 「時間割（講習案内）も送る」がONなら、受講者情報を引き継いで講習案内作成画面を開く
                  if (invoiceFormData.sendGuide) {
                    const courseName = invoiceFormData.courseName || '';
                    // コース名部分を抽出（【】と日程部分を除く）
                    const courseNameOnly = courseName.replace(/【[^】]*】/, '').replace(/\s*\(.*\)$/, '').trim();
                    const matchTemplate = findGuideTemplate(courseNameOnly, getAppCourseOption(invoiceFormData.applicationId));
                    // 講習名から開始日(YYYY-MM-DD)を抽出し、テンプレの古い日付を除いた時刻と結合
                    const dateMatch = courseName.match(/\((\d{4}-\d{2}-\d{2})/);
                    const startDate = dateMatch?.[1] || '';
                    const templateTime = (matchTemplate?.meetingTime || '').replace(/^\d{4}-\d{2}-\d{2}\s*/, '').trim() || '08:20';
                    const meetingTime = computeGuideMeetingTime(matchTemplate?.timetable, startDate, templateTime);
                    const schedulePart = courseName.match(/\(([^)]+)\)/)?.[1] || '';
                    const localCourse = courses.find(c => c.name?.ja === courseNameOnly);
                    const localCourseName = localCourse?.name?.[language] || courseNameOnly;
                    setGuideCreateForm({
                      applicationId: invoiceFormData.applicationId || '',
                      templateId: matchTemplate?.id || '',
                      courseName: localCourseName + (schedulePart ? ` (${schedulePart})` : ''),
                      schedule: schedulePart,
                      userName: getGuideUserName(invoiceFormData.applicationId, invoiceFormData.userName),
                      courseOption: getAppCourseOption(invoiceFormData.applicationId),
                      userEmail: invoiceFormData.userEmail || '',
                      venue: matchTemplate?.venue || '',
                      venueAddress: matchTemplate?.venueAddress || '',
                      meetingTime: meetingTime,
                      itemsToBring: matchTemplate?.itemsToBring || '',
                      notes: matchTemplate?.notes || '',
                      mapUrl: matchTemplate?.mapUrl || '',
                      additionalInfo: matchTemplate?.additionalInfo || '',
                      timetable: matchTemplate?.timetable || [],
                      sendEmail: true,
                    });
                    setGuideCreateDialogOpen(true);
                    toast.info(t('admin.invoices.guidePrefilled'));
                  }
                } catch (e) {
                  toast.error(e.response?.data?.detail || t('admin.invoices.createFailed'));
                }
              }}
            >
              {editingInvoice ? t('admin.common.update') : t('admin.invoices.issueInvoice')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {quoteApp && <QuoteDialog app={quoteApp} onClose={() => setQuoteApp(null)} getAuthHeaders={getAuthHeaders} />}

      {/* Payment Confirm Dialog */}
      <Dialog open={paymentConfirmOpen} onOpenChange={setPaymentConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('admin.invoices.confirmPayment')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedInvoice && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <div className="font-mono text-xs text-gray-500">{selectedInvoice.invoiceNumber}</div>
                <div className="font-medium mt-1">{selectedInvoice.userName}</div>
                <div className="text-[#00A651] font-bold mt-1">¥{(selectedInvoice.totalAmount || 0).toLocaleString()}</div>
              </div>
            )}
            <PaymentConfirmFields
              data={paymentConfirmData}
              setData={setPaymentConfirmData}
              confirmedDate={selectedInvoice?.status === 'paid' ? (jstDate(selectedInvoice.confirmedAt) || selectedInvoice.paidAt) : ''}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentConfirmOpen(false)}>{t('admin.common.cancel')}</Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="confirm-payment-btn"
              disabled={!paymentConfirmData.paymentMethod || !(paymentConfirmData.confirmedBy || '').trim()}
              onClick={async () => {
                try {
                  await axios.put(`${API}/admin/invoices/${selectedInvoice.id}`, {
                    status: 'paid',
                    paymentMethod: paymentConfirmData.paymentMethod,
                    confirmedBy: paymentConfirmData.confirmedBy,
                    paymentNote: paymentConfirmData.paymentNote
                  }, { headers: getAuthHeaders() });
                  toast.success(selectedInvoice.status === 'paid' ? '入金情報を更新しました' : t('admin.invoices.paymentConfirmed'));
                  setPaymentConfirmOpen(false);
                  fetchData();
                } catch(e) { toast.error(t('admin.common.updateFailed')); }
              }}
            >
              {selectedInvoice?.status === 'paid' ? '更新する' : t('admin.invoices.confirmPayment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Dialog */}
      <Dialog open={invoiceDetailOpen} onOpenChange={setInvoiceDetailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin.invoices.detail')}</DialogTitle>
            <DialogDescription className="sr-only">請求書の詳細情報</DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm text-gray-500">{selectedInvoice.invoiceNumber}</span>
                <Badge className={
                  selectedInvoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                  selectedInvoice.status === 'cancelled' ? 'bg-gray-100 text-gray-500' :
                  'bg-orange-100 text-orange-700'
                }>
                  {selectedInvoice.status === 'paid' ? t('admin.invoices.paid') : selectedInvoice.status === 'cancelled' ? t('admin.common.cancelled') : t('admin.invoices.unpaid')}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">{t('admin.form.nameLabel')}</span> {selectedInvoice.userName}</div>
                <div><span className="text-gray-500">{t('admin.form.emailLabel')}</span> {selectedInvoice.userEmail}</div>
                {selectedInvoice.userPhone && <div><span className="text-gray-500">{t('admin.form.phoneLabel')}</span> {selectedInvoice.userPhone}</div>}
                {selectedInvoice.companyName && <div><span className="text-gray-500">{t('admin.form.companyLabel')}</span> {selectedInvoice.companyName}</div>}
                {selectedInvoice.userAddress && <div className="col-span-2"><span className="text-gray-500">{t('admin.form.addressLabel')}</span> {selectedInvoice.userAddress}</div>}
                <div className="col-span-2"><span className="text-gray-500">{t('admin.form.courseNameLabel')}</span> {selectedInvoice.courseName || '-'}</div>
              </div>
              <div className="border rounded-lg p-3 space-y-1">
                {selectedInvoice.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.name}</span>
                    <span>¥{(item.amount || 0).toLocaleString()}</span>
                  </div>
                ))}
                <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                  <span>{t('admin.invoices.total')}</span>
                  <span className="text-[#00A651]">¥{(selectedInvoice.totalAmount || 0).toLocaleString()}</span>
                </div>
              </div>
              {selectedInvoice.note && <div className="text-sm"><span className="text-gray-500">{t('admin.form.remarksLabel')}</span> {selectedInvoice.note}</div>}
              {selectedInvoice.confirmedBy && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm space-y-1">
                  <div className="font-medium text-blue-800">変更履歴</div>
                  <div><span className="font-medium text-blue-700">担当者名: </span>{selectedInvoice.confirmedBy}</div>
                </div>
              )}
              {selectedInvoice.status === 'paid' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm space-y-1">
                  <div className="font-medium text-green-800">{t('admin.invoices.paymentInfo')}</div>
                  {selectedInvoice.paidAt && <div><span className="text-green-700">{t('admin.invoices.paidDate')}</span> {formatDateShort(selectedInvoice.paidAt)}</div>}
                  <div data-testid="invoice-detail-payment-method"><span className="text-green-700">入金方法：</span>{PAYMENT_METHOD_LABELS[selectedInvoice.paymentMethod] || (selectedInvoice.paidVia === 'stripe' ? 'クレジットカード' : <span className="text-red-600">未記録</span>)}</div>
                  {selectedInvoice.confirmedAt && <div data-testid="invoice-detail-confirmed-date"><span className="text-green-700">確認年月日：</span>{jstDate(selectedInvoice.confirmedAt).replace(/-/g, '/')}</div>}
                  {selectedInvoice.paymentNote && <div><span className="text-green-700">{t('admin.form.remarksLabel')}</span> {selectedInvoice.paymentNote}</div>}
                </div>
              )}
              <div className="text-xs text-gray-400">
                <div>{t('admin.invoices.issueDate')}: {formatDateShort(selectedInvoice.createdAt)}</div>
              </div>
            </div>
          )}
          <DialogFooter>
            {selectedInvoice?.status === 'paid' && (
              <Button
                variant="outline"
                className="text-[#00A651] border-[#00A651]"
                data-testid="invoice-create-guide-btn"
                onClick={() => {
                  const inv = selectedInvoice;
                  const course = courses.find(c => c.id === inv.applicationId ? false : c.name?.ja === inv.courseName);
                  // 講習名からパターンを自動マッチング
                  const courseName = inv.courseName || '';
                  // コース名部分を抽出（日程部分を除く）
                  const courseNameOnly = courseName.replace(/【[^】]*】/, '').replace(/\s*\(.*\)$/, '').trim();
                  const matchTemplate = findGuideTemplate(courseNameOnly, getAppCourseOption(inv.applicationId));
                  
                  // 講習開始日を抽出してパターンの時刻と組み合わせ
                  const dateMatch = courseName.match(/\((\d{4}-\d{2}-\d{2})/);
                  const startDate = dateMatch?.[1] || '';
                  const templateTime = (matchTemplate?.meetingTime || '').replace(/^\d{4}-\d{2}-\d{2}\s*/, '').trim() || '08:20';
                  const meetingTime = computeGuideMeetingTime(matchTemplate?.timetable, startDate, templateTime);
                  const localCourse2 = courses.find(c => c.name?.ja === courseNameOnly);
                  const localCourseName2 = localCourse2?.name?.[language] || courseNameOnly;
                  setGuideCreateForm({
                    applicationId: inv.applicationId || '',
                    templateId: matchTemplate?.id || '',
                    courseName: localCourseName2 + (courseName.match(/\(([^)]+)\)/) ? ` (${courseName.match(/\(([^)]+)\)/)[1]})` : ''),
                    schedule: courseName.match(/\(([^)]+)\)/)?.[1] || '',
                    userName: getGuideUserName(inv.applicationId, inv.userName),
                    courseOption: getAppCourseOption(inv.applicationId),
                    userEmail: inv.userEmail || '',
                    venue: matchTemplate?.venue || '',
                    venueAddress: matchTemplate?.venueAddress || '',
                    meetingTime: meetingTime,
                    itemsToBring: matchTemplate?.itemsToBring || '',
                    notes: matchTemplate?.notes || '',
                    mapUrl: matchTemplate?.mapUrl || '',
                    additionalInfo: matchTemplate?.additionalInfo || ''
                  });
                  setInvoiceDetailOpen(false);
                  setGuideCreateDialogOpen(true);
                }}
              >
                <FileCheck className="h-4 w-4 mr-1" />
                {t('admin.guides.createGuide')}
              </Button>
            )}
            <Button
              variant="outline"
              className="text-blue-600"
              onClick={() => {
                setEditingInvoice(selectedInvoice);
                setInvoiceFormData({
                  applicationId: selectedInvoice.applicationId || '',
                  userId: selectedInvoice.userId || '',
                  userName: selectedInvoice.userName || '',
                  userEmail: selectedInvoice.userEmail || '',
                  userPhone: selectedInvoice.userPhone || '',
                  userAddress: selectedInvoice.userAddress || '',
                  companyName: selectedInvoice.companyName || '',
                  companyAddress: selectedInvoice.companyAddress || '',
                  companyPhone: selectedInvoice.companyPhone || '',
                  courseName: selectedInvoice.courseName || '',
                  items: selectedInvoice.items || [{ name: '', amount: 0 }],
                  totalAmount: selectedInvoice.totalAmount || 0,
                  note: selectedInvoice.note || '',
                  sendEmail: false,
                  dueDate: selectedInvoice.dueDate || '',
                  hideParticipants: !!selectedInvoice.hideParticipants,
                  recipientDisplay: selectedInvoice.recipientDisplay || 'both'
                });
                setInvoiceDetailOpen(false);
                setInvoiceDialogOpen(true);
              }}
            >
              {t('admin.common.edit')}
            </Button>
            {selectedInvoice?.status === 'unpaid' && (
              <Button
                variant="outline"
                className="text-green-600 border-green-500 hover:bg-green-50"
                data-testid="invoice-detail-confirm-payment-btn"
                onClick={() => {
                  setPaymentConfirmData({ paidAt: new Date().toISOString().split('T')[0], paymentNote: '', confirmedBy: '', paymentMethod: selectedInvoice.paymentMethod || '' });
                  setPaymentConfirmOpen(true);
                  setInvoiceDetailOpen(false);
                }}
              >
                入金確認
              </Button>
            )}
            {selectedInvoice?.status === 'paid' && (
              <Button
                variant="outline"
                className="text-green-700 border-green-500 hover:bg-green-50"
                data-testid="invoice-detail-edit-payment-btn"
                onClick={() => {
                  setPaymentConfirmData({
                    paidAt: (selectedInvoice.paidAt || '').slice(0, 10) || new Date().toISOString().split('T')[0],
                    paymentNote: selectedInvoice.paymentNote || '',
                    confirmedBy: selectedInvoice.confirmedBy || '',
                    paymentMethod: selectedInvoice.paymentMethod || (selectedInvoice.paidVia === 'stripe' ? 'credit_card' : '')
                  });
                  setPaymentConfirmOpen(true);
                  setInvoiceDetailOpen(false);
                }}
              >
                入金情報を修正
              </Button>
            )}
            <Button variant="outline" onClick={() => setInvoiceDetailOpen(false)}>{t('admin.common.close')}</Button>
            <Button 
              variant="outline"
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
              data-testid="invoice-detail-pdf-btn"
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/invoices/${selectedInvoice.id}/pdf`, {
                    headers: getAuthHeaders(),
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                  window.open(url, '_blank');
                } catch(e) { toast.error('PDFの表示に失敗しました'); }
              }}
            >
              PDF表示
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guide Template Dialog */}
      <Dialog open={guideTemplateDialogOpen} onOpenChange={setGuideTemplateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? t('admin.guides.editPattern') : t('admin.guides.addPattern')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('admin.guides.patternName')} <span className="text-red-500">*</span></Label>
              <Select value={guideTemplateForm.title} onValueChange={v => setGuideTemplateForm(f => ({ ...f, title: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder={t('admin.form.selectCourse')} /></SelectTrigger>
                <SelectContent>
                  {courses.map(c => (
                    <SelectItem key={c.id} value={c.name?.ja || c.title || c.id}>{c.name?.ja || c.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>対象コース</Label>
              <Select value={guideTemplateForm.courseOption || 'none'} onValueChange={v => setGuideTemplateForm(f => ({ ...f, courseOption: v === 'none' ? '' : v }))}>
                <SelectTrigger className="mt-1" data-testid="guide-template-course-option"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">指定なし（全コース共通）</SelectItem>
                  {['A','B','C'].map(code => <SelectItem key={code} value={code}>{code}コース</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">コースごとに時間割が異なる場合、コース別のパターンを作成してください。講習案内PDFは講習名とコースが一致するパターンの時間割を使用します。</p>
            </div>
            <div>
              <Label>{t('admin.guides.venue')}</Label>
              <Input value={guideTemplateForm.venue} onChange={e => setGuideTemplateForm(f => ({ ...f, venue: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>{t('admin.guides.venueAddress')}</Label>
              <Input value={guideTemplateForm.venueAddress} onChange={e => setGuideTemplateForm(f => ({ ...f, venueAddress: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>{t('admin.guides.meetingTime')}</Label>
              <div className="flex gap-2 mt-1">
                <Input type="date" value={(guideTemplateForm.meetingTime || '').split(' ')[0] || ''} onChange={e => {
                  const time = (guideTemplateForm.meetingTime || '').split(' ')[1] || '';
                  setGuideTemplateForm(f => ({ ...f, meetingTime: `${e.target.value} ${time}`.trim() }));
                }} className="flex-1" />
                <Input type="time" value={(guideTemplateForm.meetingTime || '').split(' ')[1] || ''} onChange={e => {
                  const date = (guideTemplateForm.meetingTime || '').split(' ')[0] || '';
                  setGuideTemplateForm(f => ({ ...f, meetingTime: `${date} ${e.target.value}`.trim() }));
                }} className="w-32" />
              </div>
            </div>
            <div>
              <Label>{t('admin.guides.itemsToBring')}</Label>
              <Textarea value={guideTemplateForm.itemsToBring} onChange={e => setGuideTemplateForm(f => ({ ...f, itemsToBring: e.target.value }))} className="mt-1" rows={3} placeholder={t('admin.guides.itemsPlaceholder')} />
            </div>
            <div>
              <Label>{t('admin.guides.notes')}</Label>
              <Textarea value={guideTemplateForm.notes} onChange={e => setGuideTemplateForm(f => ({ ...f, notes: e.target.value }))} className="mt-1" rows={3} />
            </div>
            <div>
              <Label>{t('admin.guides.mapUrl')}</Label>
              <Input value={guideTemplateForm.mapUrl} onChange={e => setGuideTemplateForm(f => ({ ...f, mapUrl: e.target.value }))} className="mt-1" placeholder="https://..." />
            </div>
            <div>
              <Label>{t('admin.guides.additionalInfo')}</Label>
              <Textarea value={guideTemplateForm.additionalInfo} onChange={e => setGuideTemplateForm(f => ({ ...f, additionalInfo: e.target.value }))} className="mt-1" rows={2} />
            </div>

            {/* Timetable Section */}
            <div className="border-t pt-3 mt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">講習時間割</Label>
                <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => {
                  setGuideTemplateForm(f => ({
                    ...f,
                    timetable: [...(f.timetable || []), {
                      dayTitle: `第${(f.timetable || []).length + 1}日目`,
                      slots: [{ period: '1', time: '', duration: '', subject: '', textbook: '', instructor: '' }]
                    }]
                  }));
                }}>
                  <Plus className="h-3 w-3 mr-1" />{t('admin.common.add')}
                </Button>
              </div>
              {(guideTemplateForm.timetable || []).length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-3">時間割が設定されていません</p>
              ) : (
                <div className="space-y-3">
                  {(guideTemplateForm.timetable || []).map((day, dayIdx) => (
                    <div key={dayIdx} className="border rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <Input
                          value={day.dayTitle}
                          onChange={e => {
                            const tt = [...guideTemplateForm.timetable];
                            tt[dayIdx] = { ...tt[dayIdx], dayTitle: e.target.value };
                            setGuideTemplateForm(f => ({ ...f, timetable: tt }));
                          }}
                          className="h-7 text-xs font-semibold w-48"
                          placeholder="第1日目―学科"
                        />
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => {
                            const tt = [...guideTemplateForm.timetable];
                            tt[dayIdx] = { ...tt[dayIdx], slots: [...tt[dayIdx].slots, { period: String(tt[dayIdx].slots.length + 1), time: '', duration: '', subject: '', textbook: '', instructor: '' }] };
                            setGuideTemplateForm(f => ({ ...f, timetable: tt }));
                          }}>+行</Button>
                          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-red-500" onClick={() => {
                            if (!window.confirm(`${day.dayTitle}?`)) return;
                            const tt = guideTemplateForm.timetable.filter((_, i) => i !== dayIdx);
                            setGuideTemplateForm(f => ({ ...f, timetable: tt }));
                          }}>{t('admin.common.delete')}</Button>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-[10px]">
                          <thead>
                            <tr className="text-gray-500">
                              <th className="px-1 py-0.5 text-left w-8">時限</th>
                              <th className="px-1 py-0.5 text-left w-28">時間</th>
                              <th className="px-1 py-0.5 text-left w-12">分</th>
                              <th className="px-1 py-0.5 text-left">科目</th>
                              <th className="px-1 py-0.5 text-left">教本</th>
                              <th className="px-1 py-0.5 text-left">講師</th>
                              <th className="w-6"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {day.slots.map((slot, slotIdx) => (
                              <tr key={slotIdx} className="border-t border-gray-200">
                                <td className="px-1 py-0.5"><Input value={slot.period} onChange={e => {
                                  const tt = [...guideTemplateForm.timetable]; tt[dayIdx].slots[slotIdx] = { ...slot, period: e.target.value };
                                  setGuideTemplateForm(f => ({ ...f, timetable: tt }));
                                }} className="h-6 text-[10px] px-1" /></td>
                                <td className="px-1 py-0.5"><Input value={slot.time} onChange={e => {
                                  const tt = [...guideTemplateForm.timetable]; tt[dayIdx].slots[slotIdx] = { ...slot, time: e.target.value };
                                  setGuideTemplateForm(f => ({ ...f, timetable: tt }));
                                }} className="h-6 text-[10px] px-1" placeholder="9:00～10:00" /></td>
                                <td className="px-1 py-0.5"><Input value={slot.duration} onChange={e => {
                                  const tt = [...guideTemplateForm.timetable]; tt[dayIdx].slots[slotIdx] = { ...slot, duration: e.target.value };
                                  setGuideTemplateForm(f => ({ ...f, timetable: tt }));
                                }} className="h-6 text-[10px] px-1 w-12" /></td>
                                <td className="px-1 py-0.5"><Input value={slot.subject} onChange={e => {
                                  const tt = [...guideTemplateForm.timetable]; tt[dayIdx].slots[slotIdx] = { ...slot, subject: e.target.value };
                                  setGuideTemplateForm(f => ({ ...f, timetable: tt }));
                                }} className="h-6 text-[10px] px-1" /></td>
                                <td className="px-1 py-0.5"><Input value={slot.textbook} onChange={e => {
                                  const tt = [...guideTemplateForm.timetable]; tt[dayIdx].slots[slotIdx] = { ...slot, textbook: e.target.value };
                                  setGuideTemplateForm(f => ({ ...f, timetable: tt }));
                                }} className="h-6 text-[10px] px-1" /></td>
                                <td className="px-1 py-0.5"><Input value={slot.instructor} onChange={e => {
                                  const tt = [...guideTemplateForm.timetable]; tt[dayIdx].slots[slotIdx] = { ...slot, instructor: e.target.value };
                                  setGuideTemplateForm(f => ({ ...f, timetable: tt }));
                                }} className="h-6 text-[10px] px-1" /></td>
                                <td className="px-1 py-0.5">
                                  <button type="button" className="text-red-400 hover:text-red-600" onClick={() => {
                                    const tt = [...guideTemplateForm.timetable];
                                    tt[dayIdx] = { ...tt[dayIdx], slots: tt[dayIdx].slots.filter((_, i) => i !== slotIdx) };
                                    setGuideTemplateForm(f => ({ ...f, timetable: tt }));
                                  }}><X className="h-3 w-3" /></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGuideTemplateDialogOpen(false)}>{t('admin.common.cancel')}</Button>
            <Button className="bg-[#00A651] hover:bg-[#008a43]" onClick={async () => {
              if (!guideTemplateForm.title) { toast.error(t('admin.guides.templateNameRequired')); return; }
              try {
                if (editingTemplate) {
                  await axios.put(`${API}/admin/guide-templates/${editingTemplate.id}`, guideTemplateForm, { headers: getAuthHeaders() });
                  toast.success(t('admin.guides.templateUpdateSuccess'));
                } else {
                  await axios.post(`${API}/admin/guide-templates`, guideTemplateForm, { headers: getAuthHeaders() });
                  toast.success(t('admin.guides.templateAddSuccess'));
                }
                setGuideTemplateDialogOpen(false);
                fetchData();
              } catch { toast.error(t('admin.common.saveFailed')); }
            }}>
              {editingTemplate ? t('admin.common.update') : t('admin.common.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Application Create Dialog */}
      <Dialog open={adminAppDialogOpen} onOpenChange={setAdminAppDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('admin.common.addNew')}</DialogTitle>
            <DialogDescription className="sr-only">{t("admin.common.addNew")}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 bg-gray-50 border rounded-md p-3" data-testid="admin-app-user-link">
              <Label className="text-xs font-semibold">登録ユーザーと紐付け</Label>
              <select
                className="w-full mt-1 border rounded-md p-2 text-sm bg-white"
                value={adminAppForm.userId || ''}
                data-testid="admin-app-user-select"
                onChange={(e) => {
                  const u = users.find(x => x.id === e.target.value);
                  if (!u) {
                    setAdminAppForm(f => ({ ...f, userId: '' }));
                    return;
                  }
                  setAdminAppForm(f => ({
                    ...f,
                    userId: u.id,
                    name: u.name || f.name,
                    email: u.email || f.email,
                    phone: u.phone || f.phone,
                    company: u.company || f.company,
                    nationality: u.nationality || f.nationality || '',
                    address: u.address || f.address || '',
                  }));
                  toast.success(`${u.name} を紐付けました`);
                }}
              >
                <option value="">紐付けなし（手入力）</option>
                {[...users].filter(u => u.role !== 'admin')
                  .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ja'))
                  .map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name}{u.email ? `（${u.email}）` : ''}
                    </option>
                  ))}
              </select>
              <p className="text-[11px] text-gray-400 mt-1">紐付けると、その受講者のマイページに申込が表示されます（任意）</p>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">{t('admin.form.nameRequired')} <span className="text-red-500">*</span></Label>
              <Input value={adminAppForm.name} onChange={e => setAdminAppForm(f => ({ ...f, name: e.target.value }))} className="mt-1" placeholder={t('admin.common.name')} />
            </div>
            <div>
              <Label className="text-xs">{t('admin.common.email')}</Label>
              <Input value={adminAppForm.email} onChange={e => setAdminAppForm(f => ({ ...f, email: e.target.value }))} className="mt-1" placeholder={t('admin.common.email')} />
            </div>
            <div>
              <Label className="text-xs">{t('admin.form.phone')}</Label>
              <Input value={adminAppForm.phone} onChange={e => setAdminAppForm(f => ({ ...f, phone: e.target.value }))} className="mt-1" placeholder={t('admin.form.phone')} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">{t('admin.form.company')}</Label>
              <Input value={adminAppForm.company} onChange={e => setAdminAppForm(f => ({ ...f, company: e.target.value }))} className="mt-1" placeholder={t('admin.form.company')} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">{t('admin.form.courseName')} <span className="text-red-500">*</span></Label>
              <select className="w-full mt-1 border rounded-md p-2 text-sm" value={adminAppForm.courseId} onChange={e => {
                const course = courses.find(c => c.id === e.target.value);
                setAdminAppForm(f => ({ ...f, courseId: e.target.value, courseName: course?.name?.ja || '' }));
              }}>
                <option value="">{t("admin.form.selectPlease")}</option>
                {courses.map(c => <option key={c.id} value={c.id}>{c.name?.ja || c.id}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">{t('admin.stats.schedules')}</Label>
              <select className="w-full mt-1 border rounded-md p-2 text-sm" value={adminAppForm.scheduleName} onChange={e => {
                const sch = schedules.find(s => {
                  const label = `${s.courseName} (${s.startDate}${s.endDate ? ` 〜 ${s.endDate}` : ''})`;
                  return label === e.target.value;
                });
                setAdminAppForm(f => ({ ...f, scheduleName: e.target.value, preferredDate: sch?.startDate || '' }));
              }}>
                <option value="">{t("admin.form.selectPlease")}</option>
                {[...schedules].filter(s => !adminAppForm.courseId || s.courseId === adminAppForm.courseId).sort((a, b) => (b.startDate || '').localeCompare(a.startDate || '')).map(s => {
                  const label = `${s.courseName} (${s.startDate}${s.endDate ? ` 〜 ${s.endDate}` : ''})`;
                  return <option key={s.id} value={label}>{label}</option>;
                })}
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">{t('admin.form.notes')}</Label>
              <Textarea value={adminAppForm.message} onChange={e => setAdminAppForm(f => ({ ...f, message: e.target.value }))} className="mt-1" rows={2} placeholder={t('admin.form.notes')} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdminAppDialogOpen(false)}>{t('admin.common.cancel')}</Button>
            <Button className="bg-[#00A651] hover:bg-[#008a43]" data-testid="admin-app-submit" onClick={async () => {
              if (!adminAppForm.name.trim()) { toast.error(t('admin.users.requiredFields')); return; }
              if (!adminAppForm.courseId) { toast.error(t('admin.form.selectPlease')); return; }
              // 重複申込チェック（同じ会員／同じメール × 同じ講習）
              const dupes = applications.filter(a => {
                if (a.courseId !== adminAppForm.courseId) return false;
                if (a.status === 'cancelled') return false;
                if (adminAppForm.userId) return a.userId === adminAppForm.userId;
                const email = (adminAppForm.email || '').trim().toLowerCase();
                if (email && (a.email || '').trim().toLowerCase() === email) return true;
                return (a.name || '').trim() === adminAppForm.name.trim();
              });
              if (dupes.length > 0) {
                const list = dupes.slice(0, 3).map(a => `・${formatDateShort(a.createdAt)} ${a.name}（${a.preferredDate || '日程未定'}）`).join('\n');
                const ok = window.confirm(
                  `⚠ 重複申込の可能性があります\n\n同じ受講者・同じ講習の申込が既に ${dupes.length} 件あります：\n${list}\n\nこのまま追加しますか？`
                );
                if (!ok) return;
              }
              try {
                await axios.post(`${API}/admin/applications`, adminAppForm, { headers: getAuthHeaders() });
                toast.success(t('admin.applications.saveSuccess'));
                setAdminAppDialogOpen(false);
                fetchData();
              } catch { toast.error('追加に失敗しました'); }
            }}>{t('admin.common.add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guide Create Dialog */}
      <Dialog open={guideCreateDialogOpen} onOpenChange={setGuideCreateDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('admin.guides.createGuide')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1">
            <div>
              <Label>{t('admin.guides.selectPattern')}</Label>
              <Select value={guideCreateForm.templateId || ''} onValueChange={(v) => {
                const tpl = guideTemplates.find(t => t.id === v);
                if (tpl) {
                  setGuideCreateForm(f => ({
                    ...f,
                    templateId: v,
                    venue: tpl.venue || f.venue,
                    venueAddress: tpl.venueAddress || f.venueAddress,
                    meetingTime: (() => {
                      const datePart = (f.meetingTime || '').split(' ')[0] || '';
                      const fallback = (tpl.meetingTime || '').replace(/^\d{4}-\d{2}-\d{2}\s*/, '').trim() || '08:20';
                      return computeGuideMeetingTime(tpl.timetable, datePart, fallback);
                    })(),
                    timetable: tpl.timetable || f.timetable,
                    itemsToBring: tpl.itemsToBring || f.itemsToBring,
                    notes: tpl.notes || f.notes,
                    mapUrl: tpl.mapUrl || f.mapUrl,
                    additionalInfo: tpl.additionalInfo || f.additionalInfo,
                  }));
                }
              }}>
                <SelectTrigger className="mt-0.5"><SelectValue placeholder={t('admin.guides.selectPattern')} /></SelectTrigger>
                <SelectContent>
                  {guideTemplates.map(tpl => (
                    <SelectItem key={tpl.id} value={tpl.id}>{(() => { const c = courses.find(c => c.name?.ja === tpl.title); return c?.name?.[language] || c?.name?.ja || tpl.title; })()}{tpl.courseOption ? `（${tpl.courseOption}コース）` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-1">
              <div>
                <Label>{t('admin.guides.participantName')}</Label>
                <Input value={guideCreateForm.userName || ''} onChange={e => setGuideCreateForm(f => ({ ...f, userName: e.target.value }))} className="mt-0.5" />
              </div>
              <div>
                <Label>{t('admin.tabs.webmail')}</Label>
                <Input value={guideCreateForm.userEmail || ''} onChange={e => setGuideCreateForm(f => ({ ...f, userEmail: e.target.value }))} className="mt-0.5" />
              </div>
            </div>
            <div>
              <Label>{t('admin.certificates.courseName')}</Label>
              <Input value={guideCreateForm.courseName || ''} onChange={e => setGuideCreateForm(f => ({ ...f, courseName: e.target.value }))} className="mt-0.5" />
            </div>
            <div>
              <Label>コース</Label>
              <Select value={guideCreateForm.courseOption || 'none'} onValueChange={v => setGuideCreateForm(f => ({ ...f, courseOption: v === 'none' ? '' : v }))}>
                <SelectTrigger className="mt-0.5" data-testid="guide-create-course-option"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">指定なし</SelectItem>
                  {['A','B','C'].map(code => <SelectItem key={code} value={code}>{code}コース</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {['B','C'].includes(guideCreateForm.courseOption) && (() => {
              const rng = (guideCreateForm.courseName || '').match(/\d{4}-\d{2}-\d{2}/g) || (guideCreateForm.schedule || '').match(/\d{4}-\d{2}-\d{2}/g) || [];
              const rangeMax = rng.length ? [...rng].sort().slice(-1)[0] : '';
              const courseEnd = (start) => {
                const core = (guideCreateForm.courseName || '').replace(/\s*[(（].*$/, '').trim();
                const n = (findGuideTemplate(core, guideCreateForm.courseOption)?.timetable || []).length || 1;
                const d = new Date(`${start}T00:00:00`);
                d.setDate(d.getDate() + n - 1);
                const end = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                return rangeMax && end > rangeMax ? rangeMax : end;
              };
              return (
                <div>
                  <Label>コース開始日 <span className="text-red-500">*</span></Label>
                  <Input type="date" required className={`mt-0.5 ${!guideCreateForm.courseStartDate ? 'border-red-400' : ''}`} min={rng.length ? [...rng].sort()[0] : undefined} max={rng.length ? [...rng].sort().slice(-1)[0] : undefined}
                    value={guideCreateForm.courseStartDate || ''}
                    onChange={e => { const d = e.target.value; setGuideCreateForm(f => ({ ...f, courseStartDate: d, schedule: d ? `${d} 〜 ${courseEnd(d)}` : f.schedule, meetingTime: d ? `${d} ${(f.meetingTime || '').replace(/^\d{4}-\d{2}-\d{2}\s*/, '').trim() || '08:20'}` : f.meetingTime })); }}
                    data-testid="guide-create-course-start-date" />
                  <p className="text-xs text-gray-500 mt-0.5">{guideCreateForm.courseOption}コースの受講開始日。日程・集合日時はこの日に合わせて自動で変わります。PDFの時間割の日付もこの日から数えます（未入力なら日程の初日）。</p>
                </div>
              );
            })()}
            <div>
              <Label>{t('admin.guides.schedule')}</Label>
              <Input value={guideCreateForm.schedule || ''} onChange={e => setGuideCreateForm(f => ({ ...f, schedule: e.target.value }))} className="mt-0.5" />
            </div>
            <div>
              <Label>{t('admin.guides.venue')}</Label>
              <Input value={guideCreateForm.venue || ''} onChange={e => setGuideCreateForm(f => ({ ...f, venue: e.target.value }))} className="mt-0.5" />
            </div>
            <div>
              <Label>{t('admin.guides.venueAddress')}</Label>
              <Input value={guideCreateForm.venueAddress || ''} onChange={e => setGuideCreateForm(f => ({ ...f, venueAddress: e.target.value }))} className="mt-0.5" />
            </div>
            <div>
              <Label>{t('admin.guides.meetingTime')}</Label>
              <div className="flex gap-2 mt-0.5">
                <Input type="date" value={(guideCreateForm.meetingTime || '').split(' ')[0] || ''} onChange={e => {
                  const time = (guideCreateForm.meetingTime || '').split(' ')[1] || '';
                  setGuideCreateForm(f => ({ ...f, meetingTime: `${e.target.value} ${time}`.trim() }));
                }} className="flex-1" />
                <Input type="time" value={(guideCreateForm.meetingTime || '').split(' ')[1] || ''} onChange={e => {
                  const date = (guideCreateForm.meetingTime || '').split(' ')[0] || '';
                  setGuideCreateForm(f => ({ ...f, meetingTime: `${date} ${e.target.value}`.trim() }));
                }} className="w-32" />
              </div>
            </div>
            <div>
              <Label>{t('admin.guides.itemsToBring')}</Label>
              <Textarea value={guideCreateForm.itemsToBring || ''} onChange={e => setGuideCreateForm(f => ({ ...f, itemsToBring: e.target.value }))} className="mt-0.5" rows={2} />
            </div>
            <div>
              <Label>{t('admin.guides.notes')}</Label>
              <Textarea value={guideCreateForm.notes || ''} onChange={e => setGuideCreateForm(f => ({ ...f, notes: e.target.value }))} className="mt-0.5" rows={2} />
            </div>
            <div>
              <Label>{t('admin.guides.mapUrl')}</Label>
              <Input value={guideCreateForm.mapUrl || ''} onChange={e => setGuideCreateForm(f => ({ ...f, mapUrl: e.target.value }))} className="mt-0.5" />
            </div>
            <div>
              <Label>{t('admin.form.other')}</Label>
              <Textarea value={guideCreateForm.additionalInfo || ''} onChange={e => setGuideCreateForm(f => ({ ...f, additionalInfo: e.target.value }))} className="mt-0.5" rows={2} />
            </div>
          </div>
          <div className="flex items-center gap-4 pt-2 border-t">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="guideSendEmail" checked={guideCreateForm.sendEmail !== false} onChange={e => setGuideCreateForm(f => ({ ...f, sendEmail: e.target.checked }))} className="rounded" />
              <label htmlFor="guideSendEmail" className="text-sm text-gray-700">{t('admin.invoices.sendEmailNotification')}</label>
            </div>
          </div>
          {(() => {
            const linkedApp = applications.find(a => a.id === guideCreateForm.applicationId);
            const bulkNames = (linkedApp?.participants || []).map(p => (p?.name || '').trim()).filter(Boolean);
            if (guideCreateForm.id || bulkNames.length <= 1) return null;
            return (
              <div className="mt-2 p-3 rounded-md bg-green-50 border border-green-200" data-testid="guide-bulk-section">
                <p className="text-xs text-gray-600 mb-2">{t('admin.guides.bulkHint')}</p>
                <Button
                  variant="outline"
                  className="w-full border-[#00A651] text-[#00A651] hover:bg-green-100"
                  data-testid="guide-bulk-create-btn"
                  onClick={async () => {
                    if (guideStartDateMissing(guideCreateForm)) return;
                    try {
                      const res = await axios.post(`${API}/admin/training-guides/bulk`, { ...guideCreateForm }, { headers: getAuthHeaders() });
                      const { created = 0, skipped = 0, sent = false } = res.data || {};
                      toast.success(`講習案内を${created}件作成しました${skipped ? `（既存${skipped}件はスキップ）` : ''}${sent ? '・代表者へメール送信済み' : ''}`);
                      setGuideCreateDialogOpen(false);
                      fetchData();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || t('admin.common.createFailed'));
                    }
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />{t('admin.guides.createAllParticipants')}（{bulkNames.length}{t('admin.common.people')}）
                </Button>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGuideCreateDialogOpen(false)}>{t('admin.common.cancel')}</Button>
            <Button className="bg-[#00A651] hover:bg-[#008a43]" onClick={async () => {
              if (guideStartDateMissing(guideCreateForm)) return;
              try {
                if (guideCreateForm.id) {
                  await axios.put(`${API}/admin/training-guides/${guideCreateForm.id}`, guideCreateForm, { headers: getAuthHeaders() });
                  toast.success(t('admin.guides.templateUpdateSuccess'));
                  setGuideCreateDialogOpen(false);
                  fetchData();
                } else {
                  const res = await axios.post(`${API}/admin/training-guides`, guideCreateForm, { headers: getAuthHeaders() });
                  const guideId = res.data?.id;
                  toast.success(t('admin.guides.createSuccess'));
                  setGuideCreateDialogOpen(false);
                  fetchData();
                  
                  // チェックされている場合のみメール送信
                  if (guideCreateForm.sendEmail !== false && guideId && guideCreateForm.userEmail) {
                    try {
                      await axios.post(`${API}/admin/training-guides/${guideId}/send`, {}, { headers: getAuthHeaders() });
                      toast.success(`${guideCreateForm.userEmail} にメール送信しました`);
                    } catch { toast.error('メール送信に失敗しました'); }
                  }
                  
                  // PDF印刷画面を新しいタブで開く
                  if (guideId) {
                    window.open(`${API}/admin/training-guides/${guideId}/pdf`, '_blank');
                  }
                }
              } catch { toast.error(t('admin.common.createFailed')); }
            }}>
              {guideCreateForm.id ? t('admin.common.save') : t('admin.guides.createGuide')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
